import $ from 'jquery';

import { SLASH_MODE_OBELUS } from '../getters/attemptGetter';
import { DragDropGetter } from '../getters/dragDropGetter';
import { FractionShadeGetter } from '../getters/fractionShadeGetter';
import { HotSpotGetter } from '../getters/hotSpotGetter';
import {
  InlineChoiceGetter,
  getInlineChoiceMenu,
} from '../getters/inlineChoiceGetter';
import { getSectionedHelpPanel } from '../help/SectionedHelp';
import { getVerticalSolvingStep } from '../help/VerticalSolvingStep';
import { makeProblemContext } from '../makeProblemContext';
import {
  hasText,
  getBox,
  buildMulti,
  buildText,
  buildQuantity,
  buildSubscript,
  buildRoot,
  buildBarred,
  buildFraction,
  buildFunction,
  buildAbsoluteValue,
  buildPower,
  buildSystem,
  Fraction,
} from '../mathObjects';
import { toLine } from '../mathObjects/utils/toLine';
import {
  axesModalState,
  setAxesModalGridGetter,
} from '../modals/axesModal/axesModalStore';
import {
  problemModalState,
  setFocusedNativeInput,
} from '../modals/problemModalStore';
import {
  buildTTSDTO,
  getReadAloudWrapper,
  hasReadAloudContent,
  initSpeech,
  injectReadAloudButton,
} from '../readAloud/readAloudUtil';
import { cancelReadAloud } from '../readAloud/Reader';
import {
  arrow,
  arrowDown,
  arrowLeft,
  arrowRight,
  arrowUp,
  circle,
  dot,
  dottedLine,
  line,
  lineA,
  numLineDrawing,
  roundRect2,
} from '../renderers';
import { lineFont, lineFontSize } from '../state/font';
import {
  buildAttemptGetterCheckbox,
  buildMultiChoiceCheckbox,
  buildMultiSelectCheckbox,
} from '../uiObjects/attemptGetterCheckbox';
import {
  GRID_GMM_NAME,
  NUMBER_LINE_GMM_NAME,
  getBasicObject,
  getFillerBasicObject,
} from '../uiObjects/basicObject';
import { getBulletedList } from '../uiObjects/bulletedList';
import { getBasicButton } from '../uiObjects/button';
import { floater } from '../uiObjects/floaterManager';
import { functionButton } from '../uiObjects/functionButton';
import { keyboardModeTab } from '../uiObjects/keyboardModeTab';
import { MathImage, getProbImage } from '../uiObjects/mathImage';
import { getMultiMenu, getWordMenu } from '../uiObjects/menu';
import { ScribbleObject } from '../uiObjects/scribbleObject';
import { getStringsAndValuesParagraph } from '../uiObjects/stringsAndValuesParagraph';
import { getSubmitButton } from '../uiObjects/submitButton';
import { applyPixelRatioToCanvas } from '../utils/applyPixelRatioToCanvas';
import { decodeText } from '../utils/decodeText';
import { drawImage } from '../utils/drawImage';
import { encodeText } from '../utils/encodeText';
import { getImage } from '../utils/getImage';
import { getIntersectingPolygon } from '../utils/getIntersectingPolygon';
import {
  getFullTopLeftLocation,
  getFullTopLeftLocation2,
} from '../utils/getLocation';
import { isTouchDeviceOnly } from '../utils/isTouchDevice';
import { processFloat } from '../utils/processFloat';
import { scaleFont } from '../utils/scaleFont';
import { baseBugPatch } from '../value';

import {
  GRID_ADD_MODE,
  addGridDragModeButtonToRow,
  applyGridPointPlacement,
  canUseGridAddControls,
  clearGridShade,
  configureGridAddMoveMode,
  createGridDragTransientState,
  createGridGetterHistory,
  createGridHistoryRestoredIncompleteHaloPainter,
  findGridDragTarget,
  getGridControlCursor,
  getGridDragBounds,
  getLockedGridModeSelector,
  getStandardGridModeSelectorSizerText,
  getGridRawPointFromLocal,
  getGridUndoRedoButton,
  hasDraggableGridObjects,
  isDraggableGridGetterMode,
  isGridLineStyleMode,
  isGridRawPointWithinDragBounds,
  isSupportedGridMode,
  paintGridDragModeGlow,
  paintGridLineStyleButton,
  removeDuplicateGridPointAtTarget,
  resolveDraggedPoint,
  resolveGridDraggedShape,
  rollbackInvalidShadedGridDrag,
  shouldShowGridModeSelectorRow,
  setGridShadePoint,
} from './grid';
import { createGridGuide } from './grid/gridGuide';
import { getNumberLineObjectsAfterAdd } from './numberLineObjectPlacement';
import {
  NARROW_PROBLEM_WIDTH,
  PROBLEM_WIDTH,
  MAX_MULTI_LINE_WIDTH,
  STUDENT_PROBLEM_BACKCOLOR,
} from './problemCanvasConstants';
import { initImages } from './problemCanvasImages';
import { callSubmit } from './submit';

const AXIS_COLOR = 'black';
const AXIS_WIDTH = 3;

// used for user-created ray/segment/line color
const DEFAULT_GRAPH_OBJECT_COLOR = 'rgb(50,135,244)';
// used for first point of user-created ray/segment/line
const DEFAULT_GRAPH_OBJECT_PRE_COLOR = 'rgb(255, 136, 0)';
const ACTIVE_GRID_CREATION_MODE_COLOR = '#2f80ed';
const ACTIVE_GRID_CREATION_MODE_ICON_COLOR = '#ffffff';
const GRID_GUIDE_PARABOLA_VERTEX_THRESHOLD = 3;
const UNFOCUSED_BOX_BORDER_COLOR = 'gray';
const SEPARATOR_COLOR = 'lightgray';
const SEPARATOR_THICKNESS = 1;

const inverse = '\u207B\u00B9';
const theta = '\u03B8';

const EXTRA_PIXELS_UNDER_PROBLEM = 50;
const DEFAULT_BUTTON_SIZE = 30;
const LARGER_BUTTON_SCALAR = 1.4;
const DEFAULT_BUTTON_GAP = 8;
const lineAfterH = 20;
const rowVertSpacer = 10;
const rowHorSpacer = 5;
const STATUS_DIAMETER = 30;
const LABEL_WARNING_RESERVED_SPACE = 26;
const NL_MODE_3_STATUS_DIAMETER = 18;
const NL_OPTIONS_BUTTON_W = 150;
const NL_OPTIONS_BUTTON_H = 32;
const NL_SWAP_BUTTON_W = 80;
const NL_SWAP_BUTTON_H = 40;
const NL_BUTTON_MARGIN = 6;

// When a student clicks on a BasicObject that has "animates" as true,
// we paint the object with the "animated" color, then after 400ms,
// we repaint with the original color. 400ms feels too long for some objects,
// in particular, ones that trigger menus to open as the menu will already be
// open before the object is repainted with the original color.
// In other words, it causes a "laggy" feel to the app.
const ANIMATE_TIMEOUT_200MS = 200;

const KEYBOARD_BOTTOM_MODE = Object.freeze({
  NUMBERS: 'numbers',
  ALPHABET: 'alphabet',
  FUNCTIONS: 'functions',
});

// define a couple of "modes" for the number line to handle status
// of "modeChanger" button or "locationIndicator"
const NL_DRAW_MODE = Object.freeze({
  DEFAULT: 'NL_OPTIONS',
  MID_SELECT: 'MID_SELECTION',
});

const otherChars = '0123456789 ><+=-\u03C0\u2212.*\u00F7,\u2264\u2265%$:\'"';

export function buildProblemCanvasOwner(partialProblemContext) {
  // The 'text' that will receive key input (may be undefined)
  let focused;
  let imageNum;
  let base64Imgs = {};

  let canvasW = PROBLEM_WIDTH;
  let screenMaxTextW = MAX_MULTI_LINE_WIDTH;
  let narrow;
  let builtCurrentFrom;
  var keyboard;
  var keyboardRow;
  var DEFAULT_KEYBOARD_MAX_W = NARROW_PROBLEM_WIDTH;
  var LARGER_KEYBOARD_MAX_W = 400;
  var submitLock = false;
  var lastSubmitTime = -1;
  var lettersButton;
  var clearAllB;
  var submitCount = 0;

  var $canvas;

  var problemPanel;
  var attemptGetters = new Map();
  var onlyNormalGetters = true;
  var currentAttemptGetter;
  var buttons = {};
  var buttonKeys = [];
  var currentProblemId;
  var imageFromRestoreTest;

  var imageLoadedListener;
  var selfCheckListener;

  var world;
  var gridGuide;
  var currentCanvasMoveEvent;
  var activeGridDrag;

  // DragDropGetter | undefined
  var dragController;

  // fills out inbound partialProblemContext with defaults for any optional inbound properties
  var problemContext = makeProblemContext(partialProblemContext);
  var isStudent = problemContext.isStudent();

  var getProblemContext = function () {
    return problemContext;
  };

  function getApiUrl() {
    return getProblemContext().config.JAVA_URL;
  }

  /**
   * We can remove this when retroencabulator stops using it.
   */
  function setBase64EncodedImgs(imgDict) {
    base64Imgs = imgDict;
  }

  const setToBlank = () => {
    if (!$canvas) return;

    if (attemptGetters) attemptGetters.clear();
    buttons = {};
    currentAttemptGetter = undefined;
    builtCurrentFrom = undefined;

    if (problemPanel) {
      problemPanel.children = [];
      problemPanel.viewportSlideX = 0;
      problemPanel.viewportSlideY = 0;
    }

    keyboardRow = undefined;

    if (keyboard) {
      keyboard.getter = undefined;
      keyboard.agId = undefined;
    }

    window.scrollTo(0, 0);
    repositionProblemRows();
  };

  var getThemeName = function () {
    return problemContext.getThemeName();
  };

  var getThemeColor = function () {
    return problemContext.getThemeColor();
  };

  var getContrastingThemeColor = function () {
    return problemContext.getContrastingThemeColor();
  };

  // Very similar for InlineChoiceGetter, MatchTableGridGetter, MultiSelectGetter,
  // and traditional multiple choice getter. For now, pass into those
  // classes as we make them, but...
  // Target for cleansing.
  var grabFocus = function (getter) {
    if (currentAttemptGetter && currentAttemptGetter.gmmName == 'gridGetter') {
      currentAttemptGetter.g.setSelected(false);
    }

    setFocused(undefined);

    removeKeyboard();
    currentAttemptGetter = getter;
  };

  var errorToServer = function (error) {
    getProblemContext().errorToServer(error);
  };

  var getProblemJSDependencies = function () {
    return {
      grabFocus,
      isTesting,
      getStaticPanel,
      getStaticPanelFromText,
      getText,
      getMulti,
      getFraction,
      getBarred,
      getQuantity,
      getAbsoluteValue,
      getPower,
      getSubscript,
      getFunction,
      getRoot,
      getProblemContext,
      maybeUndoSelectedLineEntryButton,
      getProbImage,
      submitAttempt,
      getThemeColor,
      getContrastingThemeColor,
      paintCanvas,
      resizeCanvas,
      $canvas,
      isNarrow,
      NARROW_PROBLEM_WIDTH,
      MAX_MULTI_LINE_WIDTH,
      EXTRA_PIXELS_UNDER_PROBLEM,
      getCurrentAttemptGetter,
      errorToServer,
      isReadAloud,
      screenMaxTextW,
      setFocused,
      getFocused,
      keyDown,
      changedMaybe,
      storeCurrentLineXML,
      moveCursor,
      ctx: $canvas[0].getContext('2d'),
      reportErrorIncludingAttemptSoFar,
      attemptGetters,
      isStudent,
      imageLoadedListener,
    };
  };

  var getCurrentAttemptGetter = function () {
    return currentAttemptGetter;
  };

  var setLoading = function (b) {
    getProblemContext().setLoading(b);
  };

  var paintCanvas = function () {
    if (!$canvas) return;

    try {
      // Some devices (ex: iPhone Pro on iOS 18) painted fuzzy
      // when we only setup the canvas pixel ratio on load.
      resizeCanvas($canvas[0], world.viewportW, world.viewportH);

      var ctx = $canvas[0].getContext('2d');

      ctx.save();

      // determine if we should fade the keyboard (helps the floater stand out)
      if (keyboard) {
        keyboard.setFade(floater.hasPanel && floater.hasPanel());
      }

      world.paint(ctx);

      floater.paint(ctx);

      ctx.restore();

      ctx.save();

      if (gridGuide && gridGuide.on) {
        gridGuide.paintMe(ctx);
      }

      ctx.restore();
    } catch (e) {
      console.error(e);
      errorToServer('paintCanvas error: ' + (e.stack || e.message));
    }
  };

  var preCircle = function (ctx, gridDot, r, color, open, lt) {
    if (open && !lt) lt = 2;
    r = r || 5;

    if (open && lt) {
      r -= lt / 2.0;
      r += 0.5;
    }

    var col =
      gridGuide && gridGuide.on && gridGuide.dot === gridDot
        ? DEFAULT_GRAPH_OBJECT_PRE_COLOR
        : gridDot.color || color;

    circle(ctx, gridDot.cx, gridDot.cy, r, col, open, false, lt);
  };

  var paintHistoryRestoredIncompleteHalo = createGridHistoryRestoredIncompleteHaloPainter(
    {
      color: DEFAULT_GRAPH_OBJECT_COLOR,
      paintCanvas: paintCanvas,
    }
  );

  var touchDeviceChanged = function () {
    getProblemContext().logHistory?.(
      'touchDeviceChanged, so rebuild current problem'
    );
    rebuildCurrentProblem();
  };

  var canvasMouseMove = function (e) {
    // Teacher sporadically threw errors about $canvas being undefined,
    // so we added this check to the three mouse events. We suspect the problem
    // is related to React timing between rendering the core canvas,
    // attaching the mouse event listeners, and a few ms later
    // calling problemCanvasOwner to setup the $canvas.
    if (!$canvas) {
      getProblemContext().logHistory?.('Skip mouseMove, no $canvas');

      return;
    }

    try {
      if (ignoreTeacherAfterTotallyCorrect()) return;

      var x = e.pageX - $(e.target).offset().left;
      var y = e.pageY - $(e.target).offset().top;

      canvasMouseMoveHelper(x, y, e);
    } catch (error) {
      console.error(error);
      errorToServer(
        'Error during canvasMouseMove: ' + (error.stack || error.message)
      );
    }
  };

  var canvasMouseMoveHelper = function (x, y, evt) {
    if (dragController) {
      dragController.moveDraggingImage(x, y);
    }

    if (activeGridDrag && activeGridDrag.updateFromCanvasPoint) {
      activeGridDrag.updateFromCanvasPoint(x, y);
      $canvas.css('cursor', 'grabbing');

      return true;
    }

    if (gridGuide && gridGuide.on) {
      $canvas.css(
        'cursor',
        gridGuide.getModalAction(x, y) ? 'pointer' : 'default'
      );

      return true;
    }

    let results = {
      kill: false,
      repaint: false,
      mousePointerChangeTo: undefined,
    };

    // If we are dragging, we don't want to change the cursor, otherwise,
    // set the cursor as the default so it doesn't "stick" as a pointer.
    // Example scenario: I clicked the logTrigButton (cursor was a pointer
    // because of being over that button) and the cursor remained a pointer
    // until I moused over the menu which reset it based on its location
    // between menu items.
    if (!isDragging()) $canvas.css('cursor', 'default');

    currentCanvasMoveEvent = evt;

    try {
      // If the floater has a panel, we want to let it handle the mouse move
      // so that it can change the cursor if needed. This also allows us to
      // ignore elements on the problem canvas that are not "clickable" when
      // the floater is open as a click outside of the floater just closes it.
      if (floater.hasPanel && floater.hasPanel()) {
        floater.mouseMove(x, y, results);
      } else {
        world.mouseMove(x, y, results);
      }
    } finally {
      currentCanvasMoveEvent = undefined;
    }

    if (results.repaint) {
      paintCanvas();
    }

    if (!isDragging() && results.mousePointerChangeTo) {
      $canvas.css('cursor', results.mousePointerChangeTo);
    }

    return results.kill;
  };

  var isDragging = function () {
    return (
      Boolean(activeGridDrag) || (dragController && dragController.isDragging())
    );
  };

  var canvasMouseUp = function (e) {
    if (!$canvas) {
      getProblemContext().logHistory?.('Skip mouseUp, no $canvas');

      return;
    }

    try {
      if (ignoreTeacherAfterTotallyCorrect()) return;

      var x = e.pageX - $(e.target).offset().left;
      var y = e.pageY - $(e.target).offset().top;

      canvasMouseUpHelper(x, y);
    } catch (error) {
      console.error(error);
      errorToServer(
        'Error during canvasMouseUp: ' + (error.stack || error.message)
      );
    }
  };

  var canvasMouseUpHelper = function (x, y) {
    if (gridGuide) {
      gridGuide.stopGridGuideArrowRepeat();
    }

    if (activeGridDrag && activeGridDrag.finish) {
      activeGridDrag.finish(x, y);
    }

    world.mouseUp(x, y);
    paintCanvas();
  };

  var canvasMouseDown = function (mouseEvent) {
    if (!$canvas) {
      getProblemContext().logHistory?.('Skip mouseDown, no $canvas');

      return;
    }

    try {
      if (ignoreTeacherAfterTotallyCorrect()) return;

      const canvasRect = $canvas[0].getBoundingClientRect();
      const x = mouseEvent.clientX - canvasRect.left;
      const y = mouseEvent.clientY - canvasRect.top;

      const causedSomethingToHappen = canvasMouseDownHelper(x, y, mouseEvent);

      if (causedSomethingToHappen) {
        mouseEvent.preventDefault();
        mouseEvent.stopPropagation();
      }

      return causedSomethingToHappen;
    } catch (error) {
      console.error(error);
      errorToServer(
        'Error during canvasMouseDown: ' + (error.stack || error.message)
      );

      return false;
    }
  };

  var canvasMouseDownHelper = function (x, y, evt) {
    if (!getProblemContext().activity()) return;

    if (floater.mouseDown(x, y, paintCanvas)) {
      paintCanvas();

      // If the floater is a menu for a number line getter,
      // check if we are in "mode 3" where we'd have a "location indicator".
      // If so, clear the location indicator and reset the draw mode
      // to DEFAULT so that a click off the menu will either:
      // a) move the locationIndicator if the click is on the number line
      // b) clear the graph if the click is off the number line
      if (
        currentAttemptGetter &&
        currentAttemptGetter.gmmName === 'numberLineGetter' &&
        currentAttemptGetter.nl &&
        currentAttemptGetter.nl.args &&
        currentAttemptGetter.nl.args.locationIndicator
      ) {
        currentAttemptGetter.nl.args.locationIndicator = undefined;
        currentAttemptGetter.setDrawMode(NL_DRAW_MODE.DEFAULT);
        currentAttemptGetter.clear(false);
      } else {
        return true;
      }
    }

    if (gridGuide && gridGuide.on && gridGuide.handlePointerDown(x, y, evt)) {
      return true;
    }

    if (dragController) {
      dragController.mostRecentMouseDown = { x: x, y: y };
      dragController.sourceCanvas = evt.target;
    }

    var b = world.mouseDown(x, y, paintCanvas, evt);

    if (b) {
      paintCanvas();
    }

    return !!b;
  };

  var pressedKey = function (char, skipRepaint) {
    if (char == '/') {
      const slashMode =
        currentAttemptGetter &&
        currentAttemptGetter.getJsonFromServer &&
        currentAttemptGetter.getJsonFromServer().slashMode;

      if (slashMode !== SLASH_MODE_OBELUS && focused && focused.parent) {
        if (
          currentAttemptGetter != null &&
          currentAttemptGetter.selectedButton != null
        ) {
          currentAttemptGetter.selectedButton = null;
        }

        var created = focused.parent.insertRequested('fraction', focused, {
          absorbLeftIntoNumerator: true,
        });

        scaleFraction(created);

        if (created) {
          if (created.autoCapturedNumerator) {
            created.denominator.grabFocus();
          } else {
            created.grabFocus();
          }

          setKeyboardBottom(KEYBOARD_BOTTOM_MODE.NUMBERS);

          return;
        }
      }

      char = '\u00F7';
    }

    // only allow alpha numeric
    var allow = otherChars.indexOf(char) > -1;

    if (!allow) {
      // taking out limitation of keystrokes to only relevant letters
      // cuz kids thought keyboards were broken
      allow = /^[a-z]+$/i.test(char);
    }

    if (allow) {
      // Remove any extra button selections
      if (
        currentAttemptGetter != null &&
        currentAttemptGetter.selectedButton != null
      )
        currentAttemptGetter.selectedButton = null;

      focused.insertString(char, skipRepaint);

      storeCurrentLineXML();
    }
  };

  var storeCurrentLineXML = function () {
    // if currentAttemptGetter is a 'normal getter' used by a number line getter,
    // then this storage logic should acquire the parent number line getter
    var found = attemptGetters.get(currentAttemptGetter.agId);

    if (!found) {
      console.error('ag #' + currentAttemptGetter.agId + ' not found');

      return;
    }

    if (focused.line) {
      var xml = focused.line.toXML();
      var cell;

      if (found.gmmName === 'tableGetter') {
        var col = found.table.args.cols[focused.line.tableCol];

        cell = col[focused.line.tableRow];
        cell.xml = xml;
      } else if (found.gmmName === 'valueTableGetter') {
        var row = found.valueTable.args.rows[focused.line.tableRow];

        cell = row[focused.line.tableCol];
        cell.xml = xml;
      } else if (found.gmmName === 'numberLineGetter') {
        var zone = focused.line.nlZone;

        found.nl.args[zone] = xml;
      }
      // Assume 'normalGetter'
      else {
        found.setXML(focused.line.lineNumber, xml);
      }

      focused.line.notifySizeChanged();
    } else {
      console.error('Not coded for non-line focused (such as Grid?)');
    }
  };

  var moveCursor = function (direction) {
    if (!focused) {
      return false;
    }

    var moved = false;

    if (direction === 'left') {
      moved = focused.slideCursor(-1);
    } else if (direction == 'right') {
      moved = focused.slideCursor(1);
    } else {
      moved = focused.parent.moveCursor(focused, direction);
    }

    if (!moved && currentAttemptGetter.gmmName === 'tableGetter') {
      currentAttemptGetter.tab(direction);

      return true;
    }

    if (!moved && currentAttemptGetter.gmmName === 'valueTableGetter') {
      currentAttemptGetter.tab(direction);

      return true;
    }

    return moved;
  };

  var getMulti = function (line, text) {
    return buildMulti(getProblemJSDependencies(), line, text);
  };

  var maybeUndoSelectedLineEntryButton = function () {
    if (!currentAttemptGetter.selectedButton) return false;

    for (var a = 0; a < buttonKeys.length; a++) {
      if (buttonKeys[a].text === currentAttemptGetter.selectedButton) {
        buttonKeys[a].sel = false;
        currentAttemptGetter.selectedButton = undefined;
        submitAttempt({
          blank: true,
          eventSource: 'undoSelectedLineEntryButton',
        });
      }
    }

    return true;
  };

  var getQuantity = function (line, contents, abVal, lChar, rChar) {
    return buildQuantity(
      getProblemJSDependencies(),
      line,
      contents,
      abVal,
      lChar,
      rChar
    );
  };

  var getAbsoluteValue = function (line, contents) {
    return buildAbsoluteValue(getProblemJSDependencies(), line, contents);
  };

  var getPower = function (line, base, exp) {
    return buildPower(getProblemJSDependencies(), line, base, exp);
  };

  var getSubscript = function (line, base, sub) {
    return buildSubscript(getProblemJSDependencies(), line, base, sub);
  };

  var getFunction = function (line, type, base, value) {
    return buildFunction(getProblemJSDependencies(), line, type, base, value);
  };

  var getRoot = function (line, radicand, root) {
    return buildRoot(getProblemJSDependencies(), line, radicand, root);
  };

  var getBarred = function (line, stuff) {
    return buildBarred(getProblemJSDependencies(), line, stuff);
  };

  var getFraction = function (line, num, denom) {
    return buildFraction(getProblemJSDependencies(), line, num, denom);
  };

  var getText = function (x, y, t, fontSize, multiline, maxW) {
    return buildText(
      getProblemJSDependencies(),
      x,
      y,
      t,
      fontSize,
      multiline,
      maxW
    );
  };

  // Getters call this so that a 'square' for an exam problem will be
  // updated to uncertainty regarding readiness for turning in a test
  // (whether to put a check mark on square)
  var changedMaybe = function (useAgId) {
    // building getter, probably, so no need to save changes to it
    if (!currentAttemptGetter) return;

    if (!isTesting()) return;
    var di = maybeUndoSelectedLineEntryButton();

    if (isTesting() && !di) {
      getProblemContext().setCurrentUncertain();
      attemptGetters.get(useAgId || currentAttemptGetter.agId).uncertain = true;
    }
  };

  var getDrawing = function (d) {
    var ret = getBasicObject();
    var xtraW = 50;
    var xtraH = 20;
    var mw = d.w + xtraW;
    var mh = d.h + xtraH;
    var scale =
      !narrow || mw <= NARROW_PROBLEM_WIDTH ? 1 : NARROW_PROBLEM_WIDTH / mw;

    ret.setAllDim(mw * scale, mh * scale);
    ret.d = d;

    ret.paintMe = function (ctx) {
      ctx.save();

      ctx.scale(scale, scale);

      ctx.save();

      var d = ret.d;

      ctx.translate(d.cx + 0.5 * xtraW, d.cy + 0.5 * xtraH);
      ctx.translate(0, -d.slideUp);
      ctx.rotate(d.rotate);

      // circle has to be first or it fills over other items
      for (var a = 0; a < d.geoMarks.length; a++) {
        var gm = d.geoMarks[a];

        if (gm.t === 'geoCircle') {
          circle(ctx, gm.cx, gm.cy, gm.r, 'black', true);
        }
      }

      for (var a = 0; a < d.geoMarks.length; a++) {
        var gm = d.geoMarks[a];

        if (gm.t === 'Segment') {
          if (!gm.dotted) lineA(ctx, gm.pts, gm.thickness, gm.color);
          else
            dottedLine(
              ctx,
              gm.pts[0],
              gm.pts[1],
              gm.pts[2],
              gm.pts[3],
              gm.color
            );

          if (gm.arrows) {
            for (var aa = 0; aa < gm.arrows.length; aa++) {
              var arrow = gm.arrows[aa];
              var arPts = [];

              arPts.push(arrow[0]);
              arPts.push(arrow[1]);
              arPts.push(arrow[2]);
              arPts.push(arrow[3]);
              lineA(ctx, arPts, null, gm.markupColor);
              arPts = [];
              arPts.push(arrow[4]);
              arPts.push(arrow[5]);
              arPts.push(arrow[2]);
              arPts.push(arrow[3]);
              lineA(ctx, arPts, null, gm.markupColor);
            }
          }

          if (gm.congruencyMarks) {
            for (var aa = 0; aa < gm.congruencyMarks.length; aa++) {
              var pts = gm.congruencyMarks[aa];

              lineA(ctx, pts, null, gm.markupColor);
            }
          }
        } else if (gm.t === 'Rectangle') {
          ctx.beginPath();
          ctx.moveTo(gm.pts[0], gm.pts[1]);

          for (var z = 2; z < 8; z += 2) {
            ctx.lineTo(gm.pts[z], gm.pts[z + 1]);
          }

          if (gm.shaded) {
            ctx.fillStyle = 'gray';
            ctx.globalAlpha = 0.4;
            ctx.fill();
            ctx.globalAlpha = 1;
          }

          ctx.stroke();
          ctx.beginPath();

          if (gm.pts.length > 7) {
            ctx.moveTo(gm.pts[8], gm.pts[9]);

            for (var z = 10; z < 16; z += 2) {
              ctx.lineTo(gm.pts[z], gm.pts[z + 1]);
            }
          }

          if (gm.shaded) {
            ctx.fillStyle = 'white';
            ctx.fill();
          }

          ctx.stroke();
        } else if (gm.t === 'geoPoint') {
          circle(ctx, gm.cx, gm.cy, 3, 'black', false);
        } else if (gm.t == 'geoAngle') {
          var pts = [];

          pts.push(gm.pts[0]);
          pts.push(gm.pts[1]);
          pts.push(gm.pts[2]);
          pts.push(gm.pts[3]);
          lineA(ctx, pts);
          pts = [];
          pts.push(gm.pts[4]);
          pts.push(gm.pts[5]);
          pts.push(gm.pts[2]);
          pts.push(gm.pts[3]);
          lineA(ctx, pts);
        } else if (gm.t === 'geoCongruencyArc') {
          for (var z = 0; z < gm.arcs.length; z++) {
            var arc = gm.arcs[z];

            for (var zz = 0; zz < arc.length; zz += 2) {
              var pts = [];

              pts.push(arc[zz]);
              pts.push(arc[zz + 1]);
              pts.push(arc[zz + 2]);
              pts.push(arc[zz + 3]);
              lineA(ctx, pts);
            }
          }
        } else if (gm.t === 'geoArc') {
          if (gm.arcType === 'open') {
            ctx.beginPath();
            ctx.arc(gm.cx, gm.cy, gm.r, gm.as, gm.ae, false);
            ctx.stroke();
          } else {
            ctx.stroke();
            ctx.beginPath();

            // need to fill arc here
            if (gm.arcType === 'pie') {
              ctx.moveTo(gm.cx, gm.cy);
            }

            ctx.arc(gm.cx, gm.cy, gm.r, gm.as, gm.ae, false);
            ctx.fillStyle = '#3370d4';
            ctx.fill();

            if (gm.arcType === 'chord') {
              lineA(ctx, gm.pts);
            } else if (gm.arcType === 'pie') {
              lineA(ctx, gm.pts1);
              lineA(ctx, gm.pts2);
            }
          }
        }
      }

      ctx.restore();
      ctx.save();
      ctx.translate(0, -d.slideUp);
      ctx.translate(0.5 * xtraW, 0.5 * xtraH);
      // ctx.scale(scale, scale);

      var adjX = 0;
      var adjY = 0;

      for (var a = 0; a < d.geoMarks.length; a++) {
        var gm = d.geoMarks[a];

        if (gm.t === 'geoLabel') {
          if (gm.line === undefined) {
            gm.line = toLine({
              problemJS: getProblemJSDependencies(),
              xml: gm.xml,
            });
            gm.line.viewportX = adjX + (gm.cx - gm.line.viewportW / 2);
            gm.line.viewportY = adjY + (gm.cy - gm.line.viewportH / 2);
          }

          gm.line.fillStyle = gm.color;
          gm.line.paint(ctx);
        }
      }

      ctx.restore();
      ctx.restore();
    };

    return ret;
  };

  var getStaticPanelFromText = function (text, font, tts, targetForReadAloud) {
    return getStaticPanel(
      {
        xml: '<t>' + text + '</t>',
        font: font,
        tts: tts,
      },
      0,
      0,
      undefined,
      targetForReadAloud
    );
  };

  // If there is a targetForReadAloud, it means we already have a BasicObject
  // that will show the ReadAloudButton and the static panel we are about to build
  // should NOT have a ReadAloudButton but instead should have its tts data
  // appended to the TTS that is already associated with targetForReadAloud
  var getStaticPanel = function (
    args,
    x,
    y,
    leftJ,
    targetForReadAloud,
    maxWidth
  ) {
    x = x || 0;
    y = y || 0;

    if (args.panels) {
      var obj = getBasicObject(x, 0);
      var maxH = 0;

      for (var a = 0; a < args.panels.length; a++) {
        var panel = getStaticPanel(
          args.panels[a],
          0,
          0,
          null,
          undefined,
          args.vertPSS ? maxWidth : undefined
        );

        obj.add(panel);
        maxH = Math.max(panel.viewportH, maxH);
      }

      if (args.vertPSS) {
        obj.layoutChildrenUD(5);
        if (!leftJ && args.horizontalAlignment !== 'LEFT')
          obj.centerHorizontally();
      } else {
        obj.layoutChildrenLR(10);

        for (var a = 0; a < obj.children.length; a++) {
          var child = obj.children[a];
          var surplus = (maxH - child.viewportH) / 2;

          child.viewportY += surplus;
        }
      }

      obj.sizeMeToFitChildren();

      return obj;
    } else if (args.drawing) {
      return getDrawing(args.drawing);
    } else if (args.cards) {
      return getCards(args);
    } else if (args.imageLibrary) {
      var obj = getBasicObject(x, 0);

      obj.image = getImage(args.imageLibrary, paintCanvas);
      obj.setAllDim(obj.image.width, obj.image.height);

      obj.paintMe = function (ctx) {
        drawImage(ctx, obj.image, 0, 0);
      };

      return obj;
    } else if (args.stemAndLeaf) {
      return getStemAndLeaf(args.stemAndLeaf);
    } else if (args.bulletedList) {
      return getBulletedList(
        getProblemJSDependencies(),
        args,
        maxWidth ?? screenMaxTextW
      );
    } else if (args.sectionedHelp) {
      return getSectionedHelpPanel(
        getProblemJSDependencies(),
        args.sectionedHelp
      );
    }
    // stand-alone two-column help section (no enclosing SectionedHelp)
    else if (args.twoColumnHelpSteps) {
      return getSectionedHelpPanel(getProblemJSDependencies(), {
        sections: [args],
      });
    } else if (args.verticalSolvingStep) {
      return getVerticalSolvingStep(
        getProblemJSDependencies(),
        args.verticalSolvingStep
      );
    }
    // java side: Value (such as Equation, Expression, Fraction, etc.)
    else if (args.xml) {
      var line = toLine({
        problemJS: getProblemJSDependencies(),
        xml: args.xml,
        x,
        font: args.font,
        multiline: true,
        editable: false,
      });

      if (isReadAloud() && args.tts) {
        injectReadAloudButton(
          targetForReadAloud || line,
          args.tts,
          { paintCanvas, errorToServer, $canvas },
          line
        );

        // Resize line to account for inject ReadAloudButton
        if (!targetForReadAloud) {
          line.lockViewportDim = false;
          line.buildSizeRecursive();
        }
      }

      return line;
    } else if (args.g) {
      var grid = getGrid(args.g);

      return grid;
    } else if (args.table || args.valueTable) {
      if (!isReadAloud())
        return args.table
          ? getTable(x, 0, args.table)
          : getValueTable(x, 0, args.valueTable);

      // ReadAloudButton and table are wrapped in a BasicObject.
      // Clicking on button reads all non-editable cells of table.
      var readAloudTableWrapper = getBasicObject();
      var table = args.table
        ? getTable(x, 0, args.table, readAloudTableWrapper)
        : getValueTable(x, 0, args.valueTable, readAloudTableWrapper);

      readAloudTableWrapper.add(table);
      readAloudTableWrapper.layoutChildrenLR();
      readAloudTableWrapper.sizeMeToFitChildren();

      // We found cases where tables barely fit in narrow view prior to read
      // aloud, so audio icon added to top left caused table's row to exceed
      // narrow max width. Here, we detect this issue and resolve it by
      // bumping the audio icon to above the table
      if (
        isNarrow() &&
        readAloudTableWrapper.viewportW > NARROW_PROBLEM_WIDTH
      ) {
        readAloudTableWrapper.layoutChildrenUD();
        readAloudTableWrapper.children.forEach(child => {
          child.viewportX = 0;
        });
        readAloudTableWrapper.sizeMeToFitChildren();
      }

      return readAloudTableWrapper;
    } else if (args.nl) {
      var ret = getNumberLine(0, 0, args.nl);

      return ret;
    }

    // java side: StringsAndValuesParagraph
    else if (args.xmlGraph) {
      return getStringsAndValuesParagraph(
        getProblemJSDependencies(),
        args,
        maxWidth ?? screenMaxTextW,
        x,
        targetForReadAloud
      );
    }
    // 3 possibilities from java side:
    // SystemOfEquations,
    // SystemOfInequalities,
    // SystemOfIneqs
    else if (args.linesXML) {
      return buildSystem(
        getProblemJSDependencies(),
        args,
        targetForReadAloud,
        x,
        y
      );
    } else if (args.image) {
      return getProbImage(
        getProblemJSDependencies(),
        args,
        maxWidth,
        x,
        y,
        targetForReadAloud
      );
    }
  };

  var getCards = function (data) {
    var ret = getBasicObject();

    if (data.dir) {
      var directions = getStaticPanelFromText(data.dir, undefined, data.tts);

      ret.add(directions);
    }

    var cards = getBasicObject();

    var s = lineFontSize + 10;
    var row;

    for (var a = 0; a < data.cards.length; a++) {
      if (row === undefined) {
        row = getBasicObject();
      }

      var letter = getBasicObject();

      letter.viewportMargin = 1;
      var t = getText(0, 0, data.cards[a]);

      letter.add(t);
      letter.setAllDim(s);
      letter.centerHorizontally();
      letter.centerVertically();
      row.add(letter);

      if (a == data.cards.length - 1 || (a > 0 && (a + 1) % 7 === 0)) {
        row.layoutChildrenLR(5);
        row.sizeMeToFitChildren();
        cards.add(row);
        row = undefined;
      }
    }

    cards.layoutChildrenUD(5);
    cards.sizeMeToFitChildren();

    ret.add(cards);
    ret.layoutChildrenUD(10);
    ret.sizeMeToFitChildren();
    ret.centerHorizontally();

    return ret;
  };

  var getStemAndLeaf = function (vals) {
    var vSpaceToTitle = 10;
    var vSpaceAfterTitle = 10;
    var vSpaceAfterRow = 8;
    var vSpaceToKey = 10;
    var vSpaceInKey = 5;
    var vSpaceAfterKey = 10;

    var horizSpace = 13;
    var horizOuterMargin = 10;

    var horizKeyMargin = 7;

    var ret = getBasicObject();

    var data = vals.data;

    var keyStem = 0;
    var keyLeaf = 0;
    var width = 0;
    var height = 0;
    var text = getText(0, 0, '8');

    var stemToLeaves = {};

    for (var x = 0; x < data.length; x++) {
      var i = data[x];
      var stem = 0;
      var leaf = 0;

      if (i < 10) {
        leaf = i;
      } else if (i < 100) {
        stem = parseInt(i / 10);
        leaf = i % 10;
      } else if (i < 1000) {
        stem = parseInt(i / 100);
        leaf = parseInt((i % 100) / 10);
      } else if (i < 10000) {
        stem = parseInt(i / 1000);
        leaf = parseInt((i % 1000) / 100);
      }

      if (stemToLeaves[stem + ''] === undefined) {
        stemToLeaves[stem + ''] = {};
      }

      if (stem === 0) {
        ret.keyStem = 0;
        ret.keyLeaf = leaf;
      } else if (keyStem === 0) {
        ret.keyStem = stem;
        ret.keyLeaf = leaf;
      }

      var leafer = stemToLeaves[stem + ''];

      if (leafer[leaf + ''] === undefined) {
        leafer[leaf + ''] = 0;
      }

      leafer[leaf + ''] += 1;
    }

    var r = Object.keys(stemToLeaves).length;

    if (keyStem === 0 && r > 1) {
      for (var i in stemToLeaves) {
        if (stemToLeaves.hasOwnProperty(i)) {
          if (parseInt(i) !== 0) {
            keyStem = i;
            var leaves = stemToLeaves[i];

            for (var l in leaves) {
              if (leaves.hasOwnProperty(l)) {
                keyLeaf = l;
                break;
              }
            }

            break;
          }
        }
      }
    }

    height += vSpaceToTitle + vSpaceAfterTitle + vSpaceToKey + vSpaceAfterKey;
    height += 4 * vSpaceInKey + text.viewportH * 2;

    height += (r - 1) * vSpaceAfterRow;

    height += (r + 1) * text.viewportH;

    width += horizOuterMargin;
    width += text.viewportW;
    // tween stem and first leaf
    width += horizSpace;
    var longest = 0;

    for (var prop in stemToLeaves) {
      if (stemToLeaves.hasOwnProperty(prop)) {
        var len = 0;
        var leavers = stemToLeaves[prop];

        for (var lprop in leavers) {
          if (leavers.hasOwnProperty(lprop)) {
            len += leavers[lprop];
          }
        }

        longest = Math.max(longest, len);
      }
    }

    width += (longest - 1) * horizSpace;
    width += longest * text.viewportW;
    width += horizOuterMargin;

    ret.setAllDim(width, height);

    ret.paintMe = function (gr) {
      gr.save();

      gr.translate(horizOuterMargin, vSpaceToTitle);
      var innerW = width - horizOuterMargin * 2;

      var text = getText(0, 0, 'Data');

      gr.save();
      gr.translate(innerW / 2 - text.viewportW / 2, 0);
      text.paintMe(gr);
      gr.restore();

      text.setText('8');
      var tw = text.viewportW;

      gr.translate(0, text.viewportH + vSpaceAfterTitle);

      var poke = 5;
      var lineLen = r * text.viewportH + (r - 1) * vSpaceAfterRow;
      var lineX = tw + horizSpace / 2;

      line(gr, lineX, -poke, lineX, lineLen + poke);

      for (var stem in stemToLeaves) {
        if (stemToLeaves.hasOwnProperty(stem)) {
          text.setText(stem);
          text.paintMe(gr);

          var leaf = stemToLeaves[stem];
          var horDelta = 0;

          for (var i in leaf) {
            if (leaf.hasOwnProperty(i)) {
              text.setText(i);

              for (var x = 0; x < leaf[i]; x++) {
                gr.translate(horizSpace + tw, 0);
                horDelta += horizSpace + tw;
                text.paintMe(gr);
              }
            }
          }

          gr.translate(-horDelta, 0);
          gr.translate(0, vSpaceAfterRow + text.viewportH);
        }
      }

      gr.translate(0, -vSpaceAfterRow);
      gr.translate(0, vSpaceToKey);

      var keyString =
        keyStem + ' | ' + keyLeaf + ' = ' + keyStem + '' + keyLeaf;

      text.setText(keyString);
      var keyW = horizKeyMargin * 2 + text.viewportW;
      var keyH = vSpaceInKey * 4 + text.viewportH * 2;
      var keyRectX = innerW / 2 - keyW / 2;

      gr.beginPath();
      gr.rect(keyRectX + 2, 0, keyW - 4, keyH);
      gr.lineWidth = 1;
      gr.strokeStyle = 'black';
      gr.stroke();

      gr.translate(0, vSpaceInKey);
      text.setText('Key');
      gr.save();
      gr.translate(innerW / 2 - text.viewportW / 2, 0);
      text.paintMe(gr);
      gr.restore();

      text.setText(keyString);
      gr.translate(
        innerW / 2 - text.viewportW / 2,
        text.viewportH + 2 * vSpaceInKey
      );
      text.paintMe(gr);

      gr.restore();
    };

    return ret;
  };

  var isNarrow = function () {
    return canvasW < PROBLEM_WIDTH;
  };

  var getMaxKeyboardW = function () {
    if (isNarrow()) return DEFAULT_KEYBOARD_MAX_W;
    else return LARGER_KEYBOARD_MAX_W;
  };

  var getKeyboardW = function () {
    if (useTouchKeyboard()) return getMaxKeyboardW();

    // the keyboardScalar is calculated based on the max width of the keyboard,
    // but the actual width of the non-touch keyboard is less than that max width
    // so we will calculate it here
    // (2 larger buttons (clear & submit) + 5 normal buttons + 6 spaces)
    // and 4px for the box shadow (and padding) to the right of the submit button
    return (
      5 * getButtonW() +
      2 * getButtonW() * LARGER_BUTTON_SCALAR +
      6 * getButtonGap() +
      4
    );
  };

  var getKeyboardScalar = function () {
    return getMaxKeyboardW() / DEFAULT_KEYBOARD_MAX_W;
  };

  var setProblem = function (p, id) {
    cancelReadAloud();

    if (gridGuide) {
      gridGuide.close();
      gridGuide.grid = undefined;
      gridGuide.dot = undefined;
    }

    activeGridDrag = undefined;

    // reset cursor in case it was 'grab'/'grabbing' from DragDrop
    // or 'pointer' from hovering over a button
    $canvas.css('cursor', 'default');

    var nar = isNarrow();

    if (nar !== narrow) {
      narrow = nar;
      getGridGuide();
    }

    builtCurrentFrom = p;

    currentProblemId = id;
    attemptGetters.clear();
    onlyNormalGetters = true;
    buttons = {};
    currentAttemptGetter = undefined;
    problemPanel.children = [];
    keyboardRow = undefined;
    keyboard.getter = undefined;
    keyboard.agId = undefined;
    problemPanel.viewportSlideX = 0;
    problemPanel.viewportSlideY = 0;
    floater.clear();
    dragController = undefined;
    var y = rowVertSpacer;
    var r = p.r;

    imageFromRestoreTest = p.imageFromRestoreTest;

    if (narrow) {
      var rn = [];

      for (var row = 0; row < r.length; row++) {
        rn.push([]);

        // var agged = false;
        for (var psi = 0; psi < r[row].length; psi++) {
          var sup = r[row][psi];

          rn[rn.length - 1].push(sup);

          if (psi + 1 < r[row].length) {
            if (
              sup.ag ||
              sup.answerPlusSupplier ||
              r[row][psi + 1].ag ||
              r[row][psi + 1]
            ) {
              rn.push([]);
            }
          }
        }
      }

      r = rn;
    }

    // inject horizontal blue dividers ("lineAfters") after getters
    var tempRowsJson = [];

    r.forEach((rowJson, rowIndex) => {
      tempRowsJson.push(rowJson);

      let hasGetter = rowJson.some(
        panelSupplier => panelSupplier.ag || panelSupplier.answerPlusSupplier
      );

      // Determine if a row contains multiple attempt getters, and if so, split them
      // up into separate rows when not in narrow mode (narrow already handles this).
      // e.g. a row with [static panel/attempt getter/static panel/attempt getter]
      // will become:
      // [static panel, attempt getter]
      // [static panel, attempt getter]
      if (!narrow && hasGetter && rowJson.filter(ps => ps.ag).length > 1) {
        const newRows = [];
        let currentRow = [];

        rowJson.forEach(ps => {
          currentRow.push(ps);

          if (ps.ag) {
            newRows.push(currentRow);

            currentRow = [];
          }
        });

        if (currentRow.length > 0) newRows.push(currentRow);
        // replace the original row with the new rows
        tempRowsJson.splice(tempRowsJson.length - 1, 1, ...newRows);
      }

      // If the next row contains a normal getter, we will not add a lineAfter in order
      // to group getters between lineAfters. UNLESS, the getter from this row is a
      // non-normal getter, then we want to separate it from the next row's normal getter.
      const bypassLineAfter =
        rowIndex < r.length - 1 &&
        r[rowIndex + 1].some(ps => ps.ag && ps.ag.t === 'n') &&
        !r[rowIndex].some(ps => ps.ag && ps.ag.t !== 'n');

      if (hasGetter && !bypassLineAfter && rowIndex < r.length - 1) {
        const nextRowFirstPanelSupplier = r[rowIndex + 1][0];

        // Only inject if the server didn't already supply a lineAfter
        if (!nextRowFirstPanelSupplier.lineAfter) {
          tempRowsJson.push([{ lineAfter: true, injectedLineAfter: true }]);
        }
      }
    });

    r = tempRowsJson;

    imageNum = 1;
    var maxRowWidth = 0;

    let lineAfters = 0;

    // Loop through each row of panel suppliers sent from server
    r.forEach((row, rowIndex) => {
      const rowIndexWithoutLineAfters = rowIndex - lineAfters;
      var scribbleLevel = false;

      if (p.scribbleLevels) {
        for (var ab = 0; ab < p.scribbleLevels.length; ab++) {
          if (p.scribbleLevels[ab] == rowIndex) {
            scribbleLevel = true;
          }
        }
      }

      var rowObj = scribbleLevel
        ? new ScribbleObject(paintCanvas)
        : getBasicObject();

      rowObj.gmmName = 'row ' + rowIndexWithoutLineAfters;

      var specialRowHor = undefined;

      // If there is more than one panel supplier in a row,
      // the first one will own the ReadAloudButton. Subsequent
      // panel suppliers will automatically get 'read' when
      // read aloud for the first one is triggered.
      let firstPanelSupplierPerRow = undefined;

      for (var psi = 0; psi < row.length; psi++) {
        var sup = row[psi];
        var height = 0;
        var width = 0;

        if (sup.lineAfter) {
          lineAfters++;
          var lineAfter = getBasicObject();

          lineAfter.gmmName = 'lineAfter';
          lineAfter.row = rowIndex;
          lineAfter.setAllDim(100, lineAfterH);

          lineAfter.paintMe = function (ctx) {
            ctx.save();

            // Snap to integer pixel row for deterministic rendering (avoiding CanvasCapture variation, hopefully)
            const y = Math.round((lineAfterH - 1) / 2);

            ctx.fillStyle = getThemeColor();
            ctx.fillRect(-world.viewportW, y, world.viewportW * 2, 1);

            ctx.restore();
          };

          height = lineAfterH;
          width = 100;
          rowObj.add(lineAfter);
          rowObj.gmmName = 'lineAfter';

          y += lineAfterH;
        } else if (sup.answerPlusSupplier) {
          var obj = getBasicObject();

          obj.gmmName = 'answerPlusSupplier';
          var above = getStaticPanel(sup.answerPlusSupplier.above, 0, 0);
          var ag = sup.answerPlusSupplier.ag.ag;
          var gr = buildGetter(ag, rowIndex);
          var getter = gr.ag;

          rowObj.getter = getter;
          getter.viewportY = above.viewportH + 5;
          getter.parentForKeyboardX = obj;
          obj.add(above);
          obj.add(getter);
          obj.centerHorizontally();
          obj.setAllDim(
            Math.max(above.viewportW, getter.viewportW),
            above.viewportH + getter.viewportH + 5
          );

          // Setup for size changes
          getter.parent = obj;
          obj.getter = getter;
          obj.above = above;

          obj.buildSizeRecursive = function () {
            if (this.getter) {
              if (this.getter.buildSizeRecursive) {
                this.getter.buildSizeRecursive();
              }

              this.centerHorizontally();
              this.setAllDim(
                this.getMaxChildViewportWidth(),
                this.above.viewportH + this.getter.viewportH + 5
              );
              height = this.viewportH;
            }
          };

          height = obj.viewportH;
          width = obj.viewportW;
          obj.parent = rowObj;
          rowObj.add(obj);
          specialRowHor = 100;
        } else if (sup.ag) {
          var ag = sup.ag;
          var ro = buildGetter(ag, rowIndex);

          width = ro.w;
          height = ro.h;

          // Need parent chain all the way up to row to handle size changes
          // for dynamic lines
          ro.ag.parent = rowObj;
          rowObj.add(ro.ag);
          rowObj.getter = ro.ag;
        } else {
          var obj = getStaticPanel(
            sup,
            0,
            0,
            undefined,
            // When there is more than one static panel supplier in a row,
            // we only want one ReadAloudButton at the beginning, so we use
            // the rowObj as the targetForReadaloud
            row.length > 1 ? rowObj : undefined
          );

          height = obj.viewportH;
          width = obj.viewportW;

          rowObj.add(obj);

          if (!firstPanelSupplierPerRow) {
            firstPanelSupplierPerRow = obj;
          }
        }

        // hor spacer
        if (!sup.lineAfter && psi != row.length - 1) {
          var fillerW = specialRowHor || rowHorSpacer;
          var filler = getFillerBasicObject(fillerW, 0);

          rowObj.add(filler);
        }
      }

      /**
       * Rows may need to resize themselves when they have
       * a descendant line that 'isDynamic'. This occurs
       * when lines are used to support editable cells
       * in tables. As the user alters the cell, the table
       * can widen or narrow, which means parent panels also
       * need to resize so that hitboxes are still accurate.
       */
      rowObj.buildSizeRecursive = function () {
        var priorW = rowObj.viewportW;

        for (var i = 0; i < rowObj.children.length; i++) {
          if (rowObj.children[i].buildSizeRecursive) {
            rowObj.children[i].buildSizeRecursive();
          }
        }

        rowObj.layoutChildrenLR();
        rowObj.sizeMeToLowestAndRightmostChildren();
        rowObj.centerVertically();

        var newW = rowObj.viewportW;

        if (newW !== priorW) {
          rowObj.viewportX += (priorW - newW) / 2;
        }
      };

      rowObj.layoutChildrenLR();
      rowObj.sizeMeToLowestAndRightmostChildren();
      rowObj.centerVertically();

      rowObj.viewportX = rowVertSpacer;
      rowObj.viewportY = y;

      problemPanel.add(rowObj);
      y += rowObj.viewportH + rowVertSpacer;

      maxRowWidth = Math.max(rowObj.viewportW, maxRowWidth);

      var extraWidth = 0;
      var agFound = false;
      var kids = rowObj.children;

      for (var j = 0; j < kids.length; j++) {
        var kid = kids[j];

        if (
          kid.gmmName === 'normalGetter' ||
          kid.gmmName === 'tableGetter' ||
          kid.gmmName === 'valueTableGetter' ||
          kid.gmmName === 'answerPlusSupplier'
        ) {
          // Calculate the difference in midpoint offsets to determine extra width
          var agMidpoint =
            kid.viewportX + Math.max(kid.viewportW, getKeyboardW()) / 2;
          var midpoint = rowObj.viewportW / 2;

          extraWidth = Math.max(
            extraWidth,
            Math.abs(agMidpoint - midpoint) / 2
          );

          agFound = true;
        }
      }

      // Add any extra width that was calculated based on an offset ag/keyboard
      maxRowWidth += extraWidth;

      // Always ensure keyboard width if attempt getter found
      if (agFound && maxRowWidth < getKeyboardW()) maxRowWidth = getKeyboardW();
    });

    changeFocus();
    maxRowWidth = Math.min(maxRowWidth, canvasW);
    problemPanel.width = maxRowWidth;
    problemPanel.viewportW = maxRowWidth;

    // to force inner width/height to be accurate
    repositionProblemRows();
  };

  var applySelectedButton = function (g) {
    var getter = g || currentAttemptGetter;

    // saves button to json/dto that is held by 'owner' (problemContext).
    // this way, if you click off then click on, owner will hand back button state
    getter.getJsonFromServer().selectedButton = getter.selectedButton;

    for (var a = 0; a < buttonKeys.length; a++) {
      var s = buttonKeys[a].text === getter.selectedButton;

      buttonKeys[a].sel = s;
    }
  };

  var buildGetter = function (ag, row, x) {
    var ret = buildGetterH(ag, row, x);

    ret.ag.selectedButton = ag.selectedButton;

    return ret;
  };

  var buildGetterH = function (attemptGetterJson, row, x) {
    // internal tooling CanvasCapture tracks how many getters exist per Problem
    if (selfCheckListener) {
      selfCheckListener.addTarget();
    }

    // 'c' (non-exam, correct)
    // 'wr' (non-exam, wrong)
    // 'u' (non-exam, untried)
    // 'ready' (exam, has valid entry)
    // 'invalid' (exam, does not have valid entry)

    let status;

    // starting with MultiSelectGetter, created fall, 2022,
    // server signals AttemptGetter status with human-readable status fields 'correct' or 'validAttempt'
    // instead of prior overloaded 's' (status)
    if (
      attemptGetterJson.correct !== undefined ||
      attemptGetterJson.validAttempt !== undefined
    ) {
      // not a test, so we'll display whether this getter has been marked correct or wrong ('u' means never validly submitted)
      if (attemptGetterJson.correct !== undefined) {
        status =
          attemptGetterJson.correct === 'correct'
            ? 'c'
            : attemptGetterJson.correct === 'wrong'
            ? 'wr'
            : 'u';
      }
      // it's an exam: just mark whether the problem is ready to be handed in (has valid attempt)
      // ('u' means not known to be ready for grading, possibly invalid or possibly not submitted in current form)
      else {
        status = attemptGetterJson.validAttempt === true ? 'ready' : 'invalid';
      }
    } else {
      if (attemptGetterJson.s == 'c') status = 'c';
      if (attemptGetterJson.s == 'wr') status = 'wr';
      if (!isTesting() && attemptGetterJson.s == 'u') status = 'u';
      if (attemptGetterJson.s == 'ready') status = 'ready';
      if (attemptGetterJson.s == 'invalid') status = 'invalid';
      if (isTesting() && !status) status = 'invalid';
    }

    if (attemptGetterJson.buttons) {
      buttons[attemptGetterJson.agId + ''] = attemptGetterJson.buttons;
    }

    var getterType = attemptGetterJson.t;
    var getter;

    if (getterType === 'normal' || getterType === 'n') {
      var lines = [];

      if (attemptGetterJson.lines) {
        for (var a = 0; a < attemptGetterJson.lines.length; a++) {
          lines.push(
            toLine({
              problemJS: getProblemJSDependencies(),
              xml: attemptGetterJson.lines[a].xml,
              x: 3,
            })
          );
        }
      } else {
        lines.push(
          toLine({
            problemJS: getProblemJSDependencies(),
            x: x + 3,
          })
        );
      }

      for (var a = 0; a < lines.length; a++) {
        lines[a].balanceEmptyTexts();
        lines[a].buildSizeRecursive();
        lines[a].setLineOfChildren();
        lines[a].lockViewportDim = false;
      }

      getter = getNormalGetter({
        x: x,
        y: 0,
        jsonFromServer: attemptGetterJson,
        lines: lines,
        row: row,
        agId: attemptGetterJson.agId,
      });

      keyboard.setShowBarred(attemptGetterJson.allowBarred);
      keyboard.setShowDivision(
        attemptGetterJson.allowMultiplicationAndDivisionSymbols
      );
      keyboard.setShowDollarSymbol(attemptGetterJson.showDollarSymbol);
    } else {
      onlyNormalGetters = false;

      if (getterType === 'valueTable') {
        getter = getValueTableGetter(x, 0, attemptGetterJson, row);
      } else if (getterType === 'numberLine' || getterType === 'nag') {
        getter = getNumberLineGetter(x, 0, attemptGetterJson, row);
      } else if (getterType === 'grid' || getterType === 'g') {
        getter = getGridGetter(x, 0, attemptGetterJson);
      } else if (getterType === 'multipleChoice' || getterType === 'm') {
        getter = getMultiChoiceGetter(x, 0, attemptGetterJson);
      } else if (getterType === 'multiSelect') {
        getter = getMultiSelectGetter(x, 0, attemptGetterJson);
      } else if (getterType === 'matchTableGrid') {
        getter = getMatchTableGrid(x, 0, attemptGetterJson);
      } else if (getterType === 'dragDrop') {
        getter = new DragDropGetter(
          getProblemJSDependencies(),
          attemptGetterJson,
          status,
          changedMaybe,
          x
        );
        dragController = getter;
      } else if (getterType === 'hotSpot') {
        getter = new HotSpotGetter(
          getProblemJSDependencies(),
          attemptGetterJson,
          status,
          changedMaybe,
          x
        );
      } else if (getterType === 'fractionShade') {
        getter = new FractionShadeGetter(
          getProblemJSDependencies(),
          attemptGetterJson,
          status,
          changedMaybe,
          x
        );
      }
    }
    // Paused as of March, 2023
    // else if (getterType === 'fractionModel') {
    //   getter = new FractionModelGetter(
    //     getProblemJSDependencies(),
    //     attemptGetterJson,
    //     status,
    //     changedMaybe,
    //     x
    //   );
    // }

    // set the letters to display on the touch keyboard if the getter's toJSON method has sent them
    if (attemptGetterJson.letters) {
      getter.letters = attemptGetterJson.letters;
    }

    attemptGetters.set(attemptGetterJson.agId, getter);

    // inlineChoice needs special handling because it must be added to
    // the attemptGetters map before it can trigger building its UI
    if (getterType === 'inlineChoice') {
      getter = new InlineChoiceGetter(
        getProblemJSDependencies(),
        attemptGetterJson,
        status,
        changedMaybe,
        x
      );

      attemptGetters.set(attemptGetterJson.agId, getter);

      getter.buildUI(
        getSubmitButton(
          getter,
          paintCanvas,
          submitAttempt,
          getThemeColor,
          'inlineChoice',
          $canvas,
          getButtonW()
        )
      );
    }

    getter.setStatus(status);

    return {
      ag: getter,
      w: getter.viewportW,
      h: getter.viewportH,
    };
  };

  /* JSON DTO
   * {
   *   "correct": "correct" | "wrong" | "untried" // result of prior submission, 'untried' if not test and never had valid attempt
   *   "validAttempt": true | false // whether current state is 'turn-in-able' for test, only provided if test
   *   "t": "matchTableGrid"
   *   "prompt": string with directions for student
   *   "missingChoiceInvalid": string with message for invalid if student has not made a choice in one or more rows
   *   "agId": #
   *   "headers" : [strings] // first row after prompt
   *   "rows" : [ // not including header row
   *     {
   *       "display" : first column display json here
   *       "boxes" : [
   *         {
   *           "state" : "checked" | "unchecked",
   *           "correct": true | false, // result of prior submission, not provided if test
   *         }
   *       ]
   *     }
   *   ]
   * }
   */
  var getMatchTableGrid = function (x, y, jsonFromServer) {
    // Separate borders and text by at least margin
    const MARGIN = 5;
    const VERTICAL_ROW_MARGIN = MARGIN * 2;
    const CHECKBOX_HEIGHT = 25;
    const MARGIN_UNDER_LAST_OPTION_PANEL = 15;

    // horizontal
    const MARGIN_BETWEEN_GETTER_AND_CHOICE_ZONE = 5;
    // vertical
    const MARGIN_BETWEEN_CHOICE_ZONES = 10;
    const CHOICE_ZONE_WIDTH =
      NARROW_PROBLEM_WIDTH - 2 * MARGIN_BETWEEN_GETTER_AND_CHOICE_ZONE;
    const CHOICE_ZONE_BORDER_COLOR = 'lightgray';
    const GETTER_BACKGROUND = '#707070';

    const MIN_ROW_HEIGHT = CHECKBOX_HEIGHT + 2 * VERTICAL_ROW_MARGIN;
    const MIN_OPTION_WIDTH = CHECKBOX_HEIGHT + 2 * MARGIN;

    const WIDE_HEADER_FONT = 'bold ' + lineFontSize + 'px ' + lineFont;
    const narrowHeaderFontSize = 1.125 * lineFontSize;
    const NARROW_HEADER_FONT =
      'bold ' + narrowHeaderFontSize + 'px ' + lineFont;

    var matchTableGrid = getBasicObject(x, y);

    matchTableGrid.args = jsonFromServer;
    matchTableGrid.agId = jsonFromServer.agId;
    // gmmName is used for convenience when debugging
    matchTableGrid.gmmName = 'matchTableGrid';
    matchTableGrid.t = jsonFromServer.t;
    matchTableGrid.headers = jsonFromServer.headers;
    matchTableGrid.rows = jsonFromServer.rows;

    matchTableGrid.getJsonFromServer = function () {
      return matchTableGrid.args;
    };

    // track minimum cell dimensions as we create ui elements
    var columnWidths = [];
    var rowHeights = [];

    var prompt = getStaticPanelFromText(
      jsonFromServer.prompt,
      undefined,
      buildTTSDTO(jsonFromServer.prompt)
    );

    // vertical spacer between bottom of prompt and top border of header row
    prompt.viewportH += MARGIN;

    matchTableGrid.add(prompt);

    // ************** FULL layout ******************

    if (!isNarrow()) {
      rowHeights[0] = MIN_ROW_HEIGHT;

      var table = getBasicObject();
      var tableWrapperForReadAloud = getBasicObject();
      var headerRow = getBasicObject();

      // Build one row of cells containing column headers.
      // Header horizontal positioning occurs later, after column widths are determined.
      matchTableGrid.headers.forEach((headerJson, headerIndex) => {
        // first column header is bold and bigger (matching appearance in narrow layout)
        var header = getStaticPanelFromText(
          headerJson,
          WIDE_HEADER_FONT,
          buildTTSDTO(headerJson),
          tableWrapperForReadAloud
        );

        // set tentative values for column widths
        if (headerIndex == 0) {
          columnWidths[headerIndex] = header.viewportW + 2 * MARGIN;
        } else {
          columnWidths[headerIndex] = Math.max(
            header.viewportW + 2 * MARGIN,
            MIN_OPTION_WIDTH
          );
        }

        // probe to see if this header forces row's height to increase
        rowHeights[0] = Math.max(
          rowHeights[0],
          header.viewportH + 2 * VERTICAL_ROW_MARGIN
        );

        headerRow.add(header);
      });

      headerRow.viewportH = rowHeights[0];
      headerRow.centerVertically();

      table.add(headerRow);

      var optionRowY = rowHeights[0];

      // Build series of rows, each containing a display and checkboxes
      matchTableGrid.rows.forEach((rowJson, rowIndex) => {
        var row = getMatchTableGridOptionGroup();

        var display = getStaticPanel(
          rowJson.display,
          0,
          0,
          undefined,
          tableWrapperForReadAloud
        );

        // probe to see if this display forces first column's width to increase
        columnWidths[0] = Math.max(
          columnWidths[0],
          display.viewportW + 2 * MARGIN
        );
        // store height for row
        rowHeights[rowIndex + 1] = Math.max(
          display.viewportH + 2 * VERTICAL_ROW_MARGIN,
          MIN_ROW_HEIGHT
        );

        row.add(display);

        // Build checkboxes on 'holders' for each option
        rowJson.boxes.forEach((boxJson, boxIndex) => {
          var boxHolder = getMatchTableGridBoxHolder({
            matchTableGridGetter: matchTableGrid,
            optionGroup: row,
            rowJson,
            boxIndex,
            // boxHolder sized to fill entire cell -- so clicking anywhere in cell checks box
            width: columnWidths[boxIndex + 1],
            height: rowHeights[rowIndex + 1],
          });

          row.add(boxHolder);
        });

        row.sizeMeToLowestAndRightmostChildren();
        row.centerVertically();
        row.viewportY = optionRowY;
        optionRowY += rowHeights[rowIndex + 1];

        table.add(row);
      });

      // set horizontal positioning now that columnWidths are determined
      table.children.forEach((row, rowIndex) => {
        var x = 0;

        row.children.forEach((cell, columnIndex) => {
          cell.viewportX = x;

          // display column and header row cells need margin
          // box cell contents have margin built in
          if (columnIndex === 0 || rowIndex === 0) {
            cell.viewportX += MARGIN;
          }

          x += columnWidths[columnIndex];
        });
        row.sizeMeToLowestAndRightmostChildren();
      });

      table.sizeMeToLowestAndRightmostChildren();

      if (isReadAloud()) {
        tableWrapperForReadAloud.viewportY = prompt.viewportH;
        tableWrapperForReadAloud.add(table);
        tableWrapperForReadAloud.layoutChildrenLR();
        tableWrapperForReadAloud.sizeMeToLowestAndRightmostChildren();
        matchTableGrid.add(tableWrapperForReadAloud);
      } else {
        table.viewportY = prompt.viewportH;
        matchTableGrid.add(table);
      }

      matchTableGrid.centerHorizontally();
      matchTableGrid.sizeMeToLowestAndRightmostChildren();

      // *************** PAINT GRID (full layout) *******************

      table.paintPost = function (ctx) {
        ctx.save();
        ctx.beginPath();

        // top border (between prompt and headers)
        ctx.moveTo(0, 0);
        ctx.lineTo(table.viewportW, 0);

        // left border
        ctx.moveTo(0, 0);
        ctx.lineTo(0, table.viewportH);

        // vertical dividers and right border
        var x = 0;

        columnWidths.forEach(columnWidth => {
          x += columnWidth;
          ctx.moveTo(x, 0);
          ctx.lineTo(x, table.viewportH);
        });

        // horizontal dividers bottom border
        var y = 0;

        rowHeights.forEach(rowHeight => {
          y += rowHeight;
          ctx.moveTo(0, y);
          ctx.lineTo(table.viewportW, y);
        });

        ctx.stroke();
        ctx.restore();
      };

      matchTableGrid.getOptionRows = function () {
        return matchTableGrid.getOptionRowsHelper(table);
      };
    }

    // ************** NARROW layout ***************
    else {
      prompt.viewportFill = 'white';
      prompt.viewportW = NARROW_PROBLEM_WIDTH;
      // use entire width of narrow canvas for getter
      matchTableGrid.viewportW = NARROW_PROBLEM_WIDTH;
      matchTableGrid.viewportFill = GETTER_BACKGROUND;

      // First cell of headers from 'full'
      var titleJson;
      // Headers from 'full' columns that represent options
      var optionsJson = [];

      jsonFromServer.headers.forEach((headerJson, headerIndex) => {
        if (headerIndex === 0) {
          titleJson = headerJson;
        } else {
          optionsJson.push(headerJson);
        }
      });

      // Each row from 'full' layout becomes a vertically arranged 'choice zone.'
      // A 'choice zone' has: title, display, and rows of options.
      var zoneY = prompt.viewportH + MARGIN_BETWEEN_CHOICE_ZONES;

      jsonFromServer.rows.forEach((jsonRow, choiceZoneIndex) => {
        // collection of vertically arranged rows for a single display and its options
        var choiceZone = getBasicObject();

        choiceZone.viewportFill = 'white';
        choiceZone.viewportX = MARGIN_BETWEEN_GETTER_AND_CHOICE_ZONE;

        var titleRow = getBasicObject();

        titleRow.viewportW = CHOICE_ZONE_WIDTH;

        var title = titleJson + ' ' + (1 + choiceZoneIndex);
        var titleRowText = getStaticPanelFromText(
          title,
          NARROW_HEADER_FONT,
          buildTTSDTO(title),
          titleRow
        );

        titleRow.add(titleRowText);

        titleRow.layoutChildrenLR();
        titleRow.sizeMeToLowestAndRightmostChildren();
        titleRow.viewportH = titleRow.viewportH + 2 * VERTICAL_ROW_MARGIN;
        titleRow.viewportX = MARGIN;
        titleRow.centerVertically();

        choiceZone.add(titleRow);

        var displayRow = getBasicObject();

        var display = getStaticPanel(
          jsonRow.display,
          0,
          0,
          undefined,
          titleRow
        );

        displayRow.add(display);

        displayRow.viewportY = titleRow.gB();
        displayRow.viewportW = CHOICE_ZONE_WIDTH;
        displayRow.viewportH = display.viewportH + 2 * VERTICAL_ROW_MARGIN;
        displayRow.centerVertically();
        displayRow.centerHorizontally();

        choiceZone.add(displayRow);

        // Bundle options as if in 'full' width to communicate
        // between checkboxes during selection events (mouse/touch on a checkbox).
        var optionGroup = getMatchTableGridOptionGroup();

        var nextY = displayRow.gB();

        jsonRow.boxes.forEach((boxJson, boxIndex) => {
          var optionRow = getBasicObject();

          optionRow.viewportW = CHOICE_ZONE_WIDTH;

          var text = getStaticPanelFromText(
            optionsJson[boxIndex],
            null,
            buildTTSDTO(optionsJson[boxIndex]),
            titleRow
          );

          text.viewportX = MARGIN;

          optionRow.add(text);

          var boxHolder = getMatchTableGridBoxHolder({
            matchTableGridGetter: matchTableGrid,
            optionGroup,
            rowJson: jsonRow,
            boxIndex,
            width: CHECKBOX_HEIGHT,
            height: CHECKBOX_HEIGHT,
          });

          boxHolder.viewportX =
            optionRow.viewportW - boxHolder.viewportW - MARGIN;

          optionRow.add(boxHolder);

          optionRow.viewportH =
            Math.max(text.viewportH, boxHolder.viewportH) +
            2 * VERTICAL_ROW_MARGIN;
          optionRow.centerVertically();
          optionRow.viewportY = nextY;
          nextY += optionRow.viewportH;

          choiceZone.add(optionRow);
        });

        // Match expectations of data structure used for 'full' layout so
        // that update method still works.
        choiceZone.boxes = optionGroup.boxes;

        choiceZone.viewportY = zoneY;
        choiceZone.sizeMeToLowestAndRightmostChildren();
        choiceZone.viewportW = CHOICE_ZONE_WIDTH;
        zoneY += choiceZone.viewportH + MARGIN_BETWEEN_CHOICE_ZONES;

        matchTableGrid.add(choiceZone);

        // *************** Paint CHOICE_ZONE grid (narrow layout) *******************

        choiceZone.paintMe = function (ctx) {
          ctx.save();
          ctx.beginPath();

          ctx.strokeStyle = CHOICE_ZONE_BORDER_COLOR;

          // horizontal divider under each child
          var y = 0;
          var first = true;

          choiceZone.children.forEach(child => {
            y += child.viewportH;

            if (!first) {
              ctx.moveTo(0, y);
              ctx.lineTo(choiceZone.viewportW, y);
            }

            first = false;
          });

          ctx.stroke();
          ctx.restore();
        };
      });

      matchTableGrid.getOptionRows = function () {
        return matchTableGrid.getOptionRowsHelper(matchTableGrid);
      };
    }

    // filter out non option rows (headers, submit button, propmt)
    matchTableGrid.getOptionRowsHelper = function (rowHolder) {
      var ret = [];

      rowHolder.children.forEach(child => {
        if (child.boxes) ret.push(child);
      });

      return ret;
    };

    matchTableGrid.sizeMeToLowestAndRightmostChildren();

    // ************** SUBMIT BUTTON ******************

    var submitButton = getSubmitButton(
      matchTableGrid,
      paintCanvas,
      submitAttempt,
      getThemeColor,
      'matchTableGrid',
      $canvas,
      getButtonW()
    );

    submitButton.viewportX =
      (matchTableGrid.viewportW - submitButton.viewportW) / 2;
    submitButton.viewportY =
      matchTableGrid.viewportH + MARGIN_UNDER_LAST_OPTION_PANEL;

    matchTableGrid.add(submitButton);

    // Recompute getter height now that submit button has been added.
    matchTableGrid.viewportH +=
      submitButton.viewportH + MARGIN_UNDER_LAST_OPTION_PANEL;

    // A small vertical dark gray continuation under the submit button when in narrow
    if (isNarrow()) matchTableGrid.viewportH += MARGIN_BETWEEN_CHOICE_ZONES;

    // ************ UNIQUE MatchTableGrid METHODS ************

    // Helps evaluate whether submitAttempt should send state to server.
    // If invalid, returns string with message to user.
    matchTableGrid.validateAttempt = function () {
      var invalidMessage;

      // each row should have a selected box
      matchTableGrid.getOptionRows().forEach(optionRow => {
        const isSelected = optionRow.boxes.some(box => box.isSelected());

        if (!isSelected) {
          // pull in localized invalid message
          invalidMessage = jsonFromServer.missingChoiceInvalid;
        }
      });

      return invalidMessage;
    };

    // Package current checked/unchecked state of boxes using DTO for MatchTableGrid.
    // Used to send submission to server.
    matchTableGrid.serializeAttempt = function () {
      if (!isTesting()) {
        var errorMessage = matchTableGrid.validateAttempt();

        if (errorMessage) {
          getProblemContext().addStackableDialog({
            msg: errorMessage,
            top: 'Invalid',
          });

          return;
        }
      }

      var ret = {};

      ret.rows = [];
      matchTableGrid.args.rows.forEach(rowJson => {
        ret.rows.push({ boxes: rowJson.boxes });
      });

      return ret;
    };

    // Apply checkbox state sent from server. For example, mark previously
    // submitted options incorrect or correct and disable them (if not testing).
    matchTableGrid.update = function (updatedJson) {
      // each DTO.row has field 'boxes' than can replace local version
      matchTableGrid.args.rows.forEach((jsonRow, rowIndex) => {
        jsonRow.boxes = updatedJson.rows[rowIndex].boxes;
      });

      // each ui box needs to call setSelected and setCorrectlySubmitted
      // based on inbound json box state
      var rowIndex = 0;

      matchTableGrid.getOptionRows().forEach(optionRow => {
        var jsonBoxes = updatedJson.rows[rowIndex].boxes;

        jsonBoxes.forEach((jsonBox, boxIndex) => {
          var uiBox = optionRow.boxes[boxIndex];

          uiBox.setSelected(jsonBox.state === 'checked');
          uiBox.setCorrectlySubmitted(jsonBox.correct);
        });

        rowIndex++;
      });
    };

    // Apply box state originally received from server
    matchTableGrid.update(jsonFromServer);

    // ************ REQUIRED GETTER METHODS *************

    // All getters are expected to support this function
    matchTableGrid.setStatus = function (status) {
      matchTableGrid.st = status;
      // Disable getter if status is correct ('c')
      // Note: 'e' means enabled
      matchTableGrid.e = status !== 'c';
    };

    // All getters are expected to implement this function
    matchTableGrid.loseFocus = function () {};

    // All getters are expected to implement this function
    matchTableGrid.grabFocus = function () {
      if (
        currentAttemptGetter &&
        currentAttemptGetter.gmmName == 'gridGetter'
      ) {
        currentAttemptGetter.g.setSelected(false);
      }

      setFocused(undefined);

      removeKeyboard();
      currentAttemptGetter = matchTableGrid;
    };

    // All getters are expected to support this function
    matchTableGrid.isEnabled = function () {
      return matchTableGrid.e;
    };

    return matchTableGrid;
  };

  var getMatchTableGridOptionGroup = function () {
    var optionGroup = getBasicObject();

    optionGroup.boxes = [];

    optionGroup.deselectAll = function () {
      optionGroup.boxes.forEach(checkbox => {
        checkbox.setSelected(false);
      });
    };

    // disable row if it has been correctly answered (and not testing)
    optionGroup.isEnabled = function () {
      return (
        isTesting() ||
        !optionGroup.boxes.some(box => box.hasBeenCorrectlySubmitted())
      );
    };

    return optionGroup;
  };

  var getMatchTableGridBoxHolder = function ({
    matchTableGridGetter,
    optionGroup,
    rowJson,
    boxIndex,
    width,
    height,
  }) {
    var boxHolder = getBasicObject();
    var checkbox = buildAttemptGetterCheckbox({
      isTesting,
      onSelectedChange: function (b) {
        rowJson.boxes[boxIndex].state = b ? 'checked' : 'unchecked';
      },
      paintCanvas,
    });

    optionGroup.boxes.push(checkbox);

    var mouseDownResponse = function () {
      // if the getter is not enabled, ignore mouse down
      if (!matchTableGridGetter.isEnabled()) return true;
      // if the checkbox is marked correct or wrong from data from server, we disable it
      if (!checkbox.isEnabled()) return true;
      // If the group of options does not permit further user interaction, ignore mouse down.
      // (essentially, if the user has submitted a correct answer and this is not a test)
      if (!optionGroup.isEnabled()) return true;

      changedMaybe(matchTableGridGetter.agId);
      optionGroup.deselectAll();
      checkbox.setSelected(true);
      matchTableGridGetter.grabFocus();

      return true;
    };

    // Entire holder is mouse-sensitive. In 'full', this is size of cell, but in 'Narrow', this is
    // just sized to the checkbox to prevent attempts to vertically scroll from clicking box.
    boxHolder.mouseDownResponse = mouseDownResponse;
    boxHolder.add(checkbox);
    boxHolder.setAllDim(width, height);
    boxHolder.centerHorizontally();
    boxHolder.centerVertically();

    return boxHolder;
  };

  var repositionProblemRows = function () {
    if (!problemPanel || problemPanel.children.length === 0) {
      return;
    }

    var y = 0;
    var w = 0;
    var lastGetter = null;

    for (var r = 0; r < problemPanel.children.length; r++) {
      var h = 0;
      var child = problemPanel.children[r];

      if (child.gmmName === 'lineAfter') {
        h = lineAfterH;
      } else if ((keyboardRow && r != keyboardRow) || !keyboardRow) {
        for (var c = 0; c < child.children.length; c++) {
          h = Math.max(h, child.children[c].viewportH);
        }

        // Check if getter and store last instance
        if (child.getter) lastGetter = child;
      } else if (keyboardRow && r == keyboardRow) {
        h = keyboard.viewportH;
      }

      child.viewportH = h;
      child.viewportY = y;

      // Re-center regular row contents after recalculating row height, but
      // leave the keyboard row alone because its children manage their own y positions.
      if ((keyboardRow && r != keyboardRow) || !keyboardRow) {
        child.centerVertically?.();
      }

      y += h + rowVertSpacer;
    }

    w = problemPanel.viewportW;

    // Set height, text is middle vertical aligned so add an additional half height for final row if text
    var last = problemPanel.children[problemPanel.children.length - 1];
    var height = y;

    height += hasText(last) ? last.viewportH / 2 : 0;
    // Check if the panel is vertically shifted
    height += problemPanel.viewportY ?? 0;

    // Add additional margin based on the size of the last attempt getter so that it can be scrolled vertically for centering
    if (lastGetter)
      height += Math.min(EXTRA_PIXELS_UNDER_PROBLEM, lastGetter.viewportH / 2);

    problemPanel.height = height;
    problemPanel.viewportH = height;

    world.height = height;
    world.viewportH = height;
    resizeCanvas($canvas.get(0), world.viewportW, world.viewportH);

    resetLeftSideSlideOfProblemPanel();

    for (var r = 0; r < problemPanel.children.length; r++) {
      if (problemPanel.children[r].gmmName === 'lineAfter') {
        problemPanel.children[r].setAllDim(w, lineAfterH);
        problemPanel.children[r].children[0].setAllDim(w, lineAfterH);
      }
    }

    problemPanel.centerHorizontally();

    if (keyboard.getter) {
      var getterRow = problemPanel.children[keyboardRow - 1];
      var keyTar = keyboard.getter.keyboardX;

      if (!keyTar) {
        var getterX = keyboard.getter.parentForKeyboardX
          ? keyboard.getter.parentForKeyboardX.viewportX
          : keyboard.getter.viewportX;

        if (keyboard.getter.parentForKeyboardX) {
          if (
            keyboard.getter.parentForKeyboardX.gmmName === 'answerPlusSupplier'
          ) {
            var children = keyboard.getter.parentForKeyboardX.children;

            for (var z = 0; z < children.length; z++) {
              if (children[z].gmmName === 'normalGetter') {
                getterX += children[z].viewportX;
              }
            }
          }
        }

        var midX =
          getterRow.viewportX + getterX + keyboard.getter.viewportW / 2;
        var useX = midX - getKeyboardW() / 2;

        keyTar = Math.max(0, useX);
        if (keyTar + getKeyboardW() > canvasW)
          keyTar = canvasW - getKeyboardW();
      } else {
        keyTar += getterRow.viewportX;
      }

      keyboard.viewportX = keyTar;

      var overRight =
        keyboard.viewportW +
        keyboard.viewportX +
        problemPanel.viewportSlideX -
        (problemPanel.viewportW - 2 * problemPanel.viewportMargin);

      if (overRight > 0) {
        problemPanel.slide(-overRight, 0);
      }

      var overLeft = keyboard.viewportX + problemPanel.viewportSlideX;

      if (overLeft < 0) {
        problemPanel.slide(-overLeft, 0);
      }

      var overTop =
        keyboard.viewportY +
        keyboard.viewportH +
        problemPanel.viewportSlideY -
        (problemPanel.viewportH - 2 * problemPanel.viewportMargin);

      if (overTop > 0) {
        problemPanel.slide(0, -overTop);
      }
    }

    paintCanvas();
  };

  var resetLeftSideSlideOfProblemPanel = function () {
    problemPanel.viewportX = (canvasW - problemPanel.width) / 2;
  };

  var insertKeyboard = function (row, getter, agId) {
    if (keyboardRow) {
      problemPanel.children.splice(keyboardRow, 1);
    }

    keyboard.agId = agId;
    keyboard.submitter.cancelAnimation();
    setKeyboardBottom(KEYBOARD_BOTTOM_MODE.NUMBERS);
    problemPanel.children.splice(row + 1, 0, keyboard);
    keyboardRow = row + 1;
    keyboard.getter = getter;

    applySelectedButton(getter);

    repositionProblemRows();
  };

  var removeKeyboard = function () {
    if (!keyboardRow) {
      return;
    }

    problemPanel.children.splice(keyboardRow, 1);

    keyboardRow = undefined;
    keyboard.agId = -1;
    keyboard.getter = undefined;

    repositionProblemRows();
  };

  var getNumberLine = function (x, y, args, nlg) {
    var nl = getBasicObject(x, y);

    nl.nlg = nlg;
    nl.parent = nlg;
    nl.gmmName = NUMBER_LINE_GMM_NAME;

    if (!args) {
      args = {};
      args.width = 300;
      args.height = 80;
      args.mode = '3';
    }

    args.ppb = args.ppb || 20;

    nl.args = args;

    nl.setAllDim(args.width, args.height);

    nl.paintMe = function (ctx) {
      ctx.save();
      ctx.beginPath();

      ctx.moveTo(0, 15);
      ctx.lineTo(args.width, 15);
      arrow(ctx, 0, 15, args.width, 15, 'small');
      arrow(ctx, args.width, 15, 0, 15, 'small');
      ctx.stroke();
      ctx.beginPath();

      if (nl.args.mode === '1') {
        ctx.moveTo(args.width / 2, 7);
        ctx.lineTo(args.width / 2, 23);
      } else if (nl.args.mode === '2') {
        var off = args.ppb * 3;
        var mid = args.width / 2;

        ctx.moveTo(mid - off, 7);
        ctx.lineTo(mid - off, 23);
        ctx.moveTo(mid + off, 7);
        ctx.lineTo(mid + off, 23);
      } else {
        var mid = args.width / 2;

        ctx.moveTo(mid, 4);
        ctx.lineTo(mid, 26);
        ctx.lineWidth = 3;
        ctx.stroke();
        ctx.lineWidth = 1;
        ctx.beginPath();
        var t = Math.floor((mid - args.ppb) / args.ppb);

        for (var a = 1; a <= t; a++) {
          var x = mid - a * args.ppb;

          ctx.moveTo(x, 9);
          ctx.lineTo(x, 21);
          var x = mid + a * args.ppb;

          ctx.moveTo(x, 9);
          ctx.lineTo(x, 21);
        }

        const labelsY = 40;

        // show "-5, 0, & 5" for mode 3 number lines to indicate scale is 1
        ctx.fillStyle = 'black';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.font = '18px ' + lineFont;
        ctx.fillText('0', mid, labelsY);
        ctx.fillText('-5', mid - 5 * args.ppb - 2, labelsY); // extra shift for negative sign
        ctx.fillText('5', mid + 5 * args.ppb, labelsY);
      }

      ctx.stroke();

      ctx.restore();

      if (nl.args.middleStamp) {
        nl.args.middleStamp.paint(ctx);
      }

      if (nl.args.leftStamp) {
        nl.args.leftStamp.paint(ctx);
      }

      if (nl.args.rightStamp) {
        nl.args.rightStamp.paint(ctx);
      }

      ctx.save();

      if (nl.args.mode === 'allCentered' && nl.args.scale) {
        if (!nl.scaleStamp) {
          nl.scaleStamp = getText();
        }

        var mid = args.width / 2;
        var t = Math.floor((mid - args.ppb) / args.ppb);
        var x = mid - t * args.ppb;
        var ss = -t * nl.args.scale;

        ctx.translate(x, 26);

        for (var a = 0; a <= 2 * t; a++) {
          nl.scaleStamp.text = ss + a * nl.args.scale;
          nl.scaleStamp.sizeMe();
          ctx.translate(-nl.scaleStamp.viewportW / 2, 0);

          if (!nl.args.skip || a % 2 === 0) {
            nl.scaleStamp.paint(ctx);
          }

          ctx.translate(nl.scaleStamp.viewportW / 2 + args.ppb, 0);
        }
      }

      ctx.restore();

      if (nl.args.locationIndicator) {
        ctx.save();

        // Define the arrow character and its styling
        const arrow = '\u02C5'; // Unicode for the down arrow
        const fontSize = 20;

        // Set the font and alignment for the arrow
        ctx.fillStyle = DEFAULT_GRAPH_OBJECT_COLOR;
        ctx.font = `${fontSize}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        const xShift = 8;
        const yShift = 20;

        const locationX = nl.args.locationIndicator.x;
        const locationY = nl.args.locationIndicator.y - yShift;

        // Draw arrows pointing at the specified x position
        // upper-left arrow
        ctx.translate(locationX - xShift, locationY);
        ctx.rotate(-Math.PI / 4);
        ctx.fillText(arrow, 0, 0);

        // upper-right arrow
        ctx.rotate(Math.PI / 4);
        ctx.translate(2 * xShift, 0);
        ctx.rotate(Math.PI / 4);
        ctx.fillText(arrow, 0, 0);

        // lower-right arrow
        ctx.rotate(-Math.PI / 4);
        ctx.translate(0, yShift);
        ctx.rotate((3 * Math.PI) / 4);
        ctx.fillText(arrow, 0, 0);

        // lower-left arrow
        ctx.rotate(-(3 * Math.PI) / 4);
        ctx.translate(-2 * xShift, 0);
        ctx.rotate(-(3 * Math.PI) / 4);
        ctx.fillText(arrow, 0, 0);

        ctx.restore();
      }

      if (nl.args.objs) {
        ctx.save();
        ctx.translate(0, 15);
        ctx.globalAlpha = 1;

        for (var a = 0; a < nl.args.objs.length; a++) {
          var obj = nl.args.objs[a];
          var col = DEFAULT_GRAPH_OBJECT_COLOR;

          if (obj.t === 'Ray') {
            ctx.beginPath();
            ctx.arc(obj.cx, 0, 5, 0, 2 * Math.PI, false);

            if (obj.fill) {
              ctx.fillStyle = col;
              ctx.fill();
            }

            ctx.lineWidth = 3;
            ctx.strokeStyle = col;
            ctx.stroke();

            if (obj.dir) {
              var x1, x2;

              if (obj.dir === 'l') {
                x1 = obj.cx - 6;
                x2 = 10;
              } else {
                x1 = obj.cx + 6;
                x2 = nl.args.width - 10;
              }

              ctx.lineWidth = 4;
              ctx.beginPath();
              ctx.moveTo(x1, 0);
              ctx.lineTo(x2, 0);
              arrow(ctx, x1, 0, x2, 0, 'smaller');
              ctx.stroke();
            }
          } else if (obj.t === 'Segment') {
            ctx.beginPath();
            ctx.arc(obj.first.cx, 0, 6, 0, 2 * Math.PI, false);

            if (obj.first.fill) {
              ctx.fillStyle = col;
              ctx.fill();
            }

            ctx.lineWidth = 3;
            ctx.strokeStyle = col;
            ctx.stroke();

            if (obj.second) {
              ctx.beginPath();
              ctx.arc(obj.second.cx, 0, 6, 0, 2 * Math.PI, false);

              if (obj.second.fill) {
                ctx.fillStyle = col;
                ctx.fill();
              }

              ctx.lineWidth = 3;
              ctx.strokeStyle = col;
              ctx.stroke();

              var x1 = Math.min(obj.first.cx, obj.second.cx) + 6;
              var x2 = Math.max(obj.first.cx, obj.second.cx) - 6;

              ctx.lineWidth = 4;
              ctx.beginPath();
              ctx.moveTo(x1, 0);
              ctx.lineTo(x2, 0);
              ctx.stroke();
            }
          }
        }

        ctx.restore();
      }
    };

    nl.paintPostClip = function (ctx) {
      paintStatus(
        nl,
        ctx,
        -3,
        // in mode 3, we need extra space above the getter to leave room for the
        // location indicators that appear when a student clicks on the line
        nl.args.mode === '3' ? -5 : 0,
        nl.viewportW + 6,
        nl.viewportH + (nl.args.mode === '3' ? 7 : 3),
        undefined,
        nl.args.mode === '3' ? NL_MODE_3_STATUS_DIAMETER : undefined,
        nl.args.mode === '3' ? 2 : 3,
        nl.args.mode === '3' ? 6 : 4
      );
    };

    nl.getRealX = function (x) {
      return (x - nl.args.width / 2) / nl.args.ppb;
    };

    nl.setStatus = function (st) {
      nl.st = st;
    };

    nl.getNearestX = function (x) {
      var hf = nl.args.width / 2;

      if (nl.args.mode === '1') {
        return [hf, false];
      } else if (nl.args.mode === '2') {
        if (x < nl.args.width / 2) {
          return [hf - nl.args.ppb * 3, false];
        } else return [hf + nl.args.ppb * 3, true];
      }

      var fromO = Math.abs(nl.args.width / 2 - x);
      var xtra = fromO % nl.args.ppb;
      var ticks = Math.floor(fromO / nl.args.ppb);

      ticks += xtra < nl.args.ppb / 2 ? 0 : 1;
      var v =
        nl.args.width / 2 +
        (x >= nl.args.width / 2 ? ticks * nl.args.ppb : -ticks * nl.args.ppb);

      return [v, x >= nl.args.width / 2];
    };

    nl.addedGetters = false;

    nl.setGetter = function (pos, xml, row, agId) {
      var side = 56;
      var line = toLine({
        problemJS: getProblemJSDependencies(),
        xml: xml,
      });

      line.lockViewportDim = false;

      var off = nl.args.ppb * 3;
      var made = getNormalGetter({
        lines: [line],
        row: row,
        agId: agId,
        centerMe: true,
      });

      if (pos === 'left') {
        made.viewportX = nl.args.width / 2 - off;
        line.nlZone = 'leftXMLG';
        made.keyboardX = nl.viewportW / 2 - getKeyboardW() / 2;
      } else if (pos === 'right') {
        made.viewportX = nl.args.width / 2 + off;
        line.nlZone = 'rightXMLG';
        made.keyboardX = nl.viewportW / 2 - getKeyboardW() / 2;
      } else {
        made.viewportX = nl.args.width / 2;
        line.nlZone = 'midXMLG';
      }

      made.setAllDim(side, side);
      made.viewportX -= side / 2;
      made.center = true;
      made.repositionLines();
      made.nlg = nlg;
      made.parent = nl;
      // default getter "editable" property to true...
      // ...will be set to false after nlg status is set to 'c'
      made.e = true;

      if (!nl.addedGetters) {
        nl.getters = getBasicObject();
        nl.getters.viewportY = nl.args.height;
        nl.getters.gmmName = 'nlGetters';
        nl.add(nl.getters);

        nl.addedGetters = true;
      }

      nl.getters.add(made);
      refreshNumberLineGettersLayout();
      nl.resetHeight();

      return line;
    };

    nl.setStamp = function (pos, xml) {
      var line = toLine({
        problemJS: getProblemJSDependencies(),
        xml: xml,
      });

      line.e = false;
      line.viewportY = 26;
      var third = nl.args.width / 3;
      var half = line.viewportW / 2;

      if (pos === 'left') {
        line.viewportX = third - half;
        nl.args.leftStamp = line;
      } else if (pos === 'right') {
        line.viewportX = 2 * third - half;
        nl.args.rightStamp = line;
      } else {
        line.viewportX = nl.args.width / 2 - half;
        nl.args.middleStamp = line;
      }
    };

    nl.resetHeight = function () {
      var h = nl.args.mode === '3' ? 46 : 26;
      var stampH = 0;

      if (nl.args.leftStamp) {
        stampH = Math.max(stampH, nl.args.leftStamp.viewportH);
      }

      if (nl.args.rightStamp) {
        stampH = Math.max(stampH, nl.args.rightStamp.viewportH);
      }

      if (nl.args.middleStamp) {
        stampH = Math.max(stampH, nl.args.middleStamp.viewportH);
      }

      h += stampH;
      nl.viewportH = h;
      nl.height = h;

      if (nl.getters) {
        nl.getters.viewportY = h;
        nl.viewportH += nl.getters.viewportH;
        nl.height += nl.getters.viewportH;
      }
    };

    var refreshNumberLineGettersLayout = function () {
      if (!nl.getters) {
        return;
      }

      if (nl.switcher) {
        for (var a = 0; a < nl.getters.children.length; a++) {
          if (nl.getters.children[a].gmmName === 'normalGetter') {
            nl.switcher.viewportY =
              nl.getters.children[a].viewportH / 2 - nl.switcher.viewportH / 2;
            break;
          }
        }
      }

      nl.getters.sizeMeToFitChildren();
    };

    nl.buildSizeRecursive = function () {
      if (nl.getters) {
        for (var a = 0; a < nl.getters.children.length; a++) {
          if (nl.getters.children[a].buildSizeRecursive) {
            nl.getters.children[a].buildSizeRecursive();
          }
        }

        refreshNumberLineGettersLayout();
      }

      nl.resetHeight();
    };

    nl.setMode = function (m, row, agId, retainObjs, over) {
      if (nl.args.mode === m && !over) {
        return;
      }

      // clear the number line if student is changing from one tick to two or vice versa
      if (!retainObjs || (nl.args.mode && nl.args.mode !== m))
        nl.nlg.clear(false);
      nl.args.mode = m;

      if (nl.args.useGetters) {
        if (nl.getters) {
          nl.getters.children = [];
        }

        var xml = '<t></t>';
        var focusMe;

        if (m === '1') {
          if (nl.args.midXMLG) {
            xml = nl.args.midXMLG;
          }

          focusMe = nl.setGetter('middle', xml, nl.nlg.row, nl.nlg.agId);
        } else {
          if (nl.args.rightXMLG) {
            xml = nl.args.rightXMLG;
          }

          var rf = nl.setGetter('right', xml, nl.nlg.row, nl.nlg.agId);

          if (!nl.switcher) {
            var switcher = getButton();

            switcher.setAllDim(NL_SWAP_BUTTON_W, NL_SWAP_BUTTON_H);

            switcher.paintMe = function (ctx) {
              roundRect2(
                ctx,
                0,
                0,
                switcher.viewportW,
                switcher.viewportH,
                6,
                switcher.animated ? getThemeColor() : 'lightgray',
                undefined,
                undefined,
                true
              );

              ctx.save();
              // move to center of button (horizontally)
              ctx.translate(NL_SWAP_BUTTON_W / 2, NL_BUTTON_MARGIN);

              ctx.font = 'bold 14px ' + lineFont;
              ctx.fillStyle = 'gray';
              ctx.textAlign = 'center';
              ctx.textBaseline = 'top';

              ctx.fillText('Swap', 0, 0);
              ctx.fillText('Values', 0, 16);

              ctx.restore();
            };

            switcher.mouseDownResponse = function () {
              if (!nlg.e || nlg.nl.st === 'c') {
                switcher.animates = false;

                return false;
              }

              switcher.animated = true;
              var temp = nl.args.leftXMLG;

              nl.args.leftXMLG = nl.args.rightXMLG;
              nl.args.rightXMLG = temp;
              var fme = nl.setMode('2', undefined, undefined, true, true);

              if (fme) {
                fme.grabFocus();
              }

              // signal that getter should be auto-submitted if student clicks to new exam square
              if (nl.nlg) changedMaybe(nl.nlg.agId);

              // true --> consume event, no mouse reaction by parents!
              return true;
            };

            switcher.viewportY =
              nl.getters.children[0].viewportH / 2 - switcher.viewportH / 2;
            switcher.viewportX = nl.args.width / 2 - switcher.viewportW / 2;
            nl.switcher = switcher;
          }

          nl.getters.add(nl.switcher);

          xml = '<t></t>';

          if (nl.args.leftXMLG) {
            xml = nl.args.leftXMLG;
          }

          var lf = nl.setGetter('left', xml, nl.nlg.row, nl.nlg.agId);

          focusMe = lf || rf;
        }

        if (nl.nlg && nl.nlg.resetHeight) {
          nl.nlg.resetHeight();
        }

        return focusMe;
      }
    };

    nl.addObj = function (obj) {
      nl.args.objs = getNumberLineObjectsAfterAdd(
        nl.args.objs,
        obj,
        nl.args.mode
      );
    };

    if (nl.args.line1) {
      nl.addObj(nl.args.line1);
      nl.args.line1 = undefined;
    }

    if (nl.args.line2) {
      nl.addObj(nl.args.line2);
      nl.args.line2 = undefined;
    }

    if (nl.args.midXMLS) {
      nl.setStamp('middle', nl.args.midXMLS);
    }

    nl.resetHeight();

    return nl;
  };

  var getNumberLineGetter = function (x, y, ag, row) {
    var nlg = getBasicObject(x, y);

    nlg.e = true;

    nlg.gmmName = 'numberLineGetter';
    nlg.agId = ag.agId;
    nlg.t = 'numberLine';
    nlg.row = row;
    nlg.args = ag;

    nlg.getJsonFromServer = function () {
      return nlg.nl.args;
    };

    nlg.insertAttemptIntoJSON = function () {
      var attempt = {};

      if (nlg.nl.args.objs) {
        var objs = [];

        for (var a = 0; a < nlg.nl.args.objs.length; a++) {
          var obj = nlg.nl.args.objs[a];
          var x = { t: obj.t };

          if (obj.t === 'Ray') {
            x.cx = nlg.nl.getRealX(obj.cx);
            x.dir = obj.dir;
            x.mode = obj.fill ? 'rc' : 'ro';
          } else {
            var ff = obj.first;
            var ss = obj.second;

            x.first = { cx: nlg.nl.getRealX(ff.cx), fill: ff.fill };
            x.second = { cx: nlg.nl.getRealX(ss.cx), fill: ss.fill };

            if (ff.fill && ss.fill) {
              x.mode = 'bothClosed';
            } else if (!ff.fill && !ss.fill) {
              x.mode = 'bothOpen';
            } else if (ff.fill) {
              var tmp = x.first;

              x.first = x.second;
              x.second = tmp;
            }
          }

          objs.push(x);
        }

        attempt.objs = objs;
      }

      if (nlg.nl.args.mode === '1' || nlg.nl.args.mode === '2') {
        attempt.marks = parseInt(nlg.nl.args.mode);

        if (nlg.nl.getters) {
          if (attempt.marks == 1) {
            attempt.midXMLG = encodeText(nlg.nl.args.midXMLG);
          } else {
            if (nlg.nl.args.leftXMLG) {
              attempt.leftXMLG = encodeText(nlg.nl.args.leftXMLG);
            }

            if (nlg.nl.args.rightXMLG) {
              attempt.rightXMLG = encodeText(nlg.nl.args.rightXMLG);
            }
          }
        }
      }

      return attempt;
    };

    nlg.changedNag = function (changed) {
      if (!nlg.nl.getters) return;

      // if student graphed something, went to a different problem, then came back,
      // their graph is in a temporary state that could get wiped out below
      const savedObjs = nlg.nl.args.objs;

      // internal tooling for CanvasCapture ignores response from server to update nag
      if (selfCheckListener) {
        return;
      }

      const EMPTY = '<t></t>';
      // retainObjs parameter should be true if students already had a graph drawn
      // and now we are replacing a number on the line for them...
      // ...but if they have cleared out the inequality and there are are no longer
      // values to fill in, we should clear the graph as well

      if (changed.midXMLG) {
        nlg.nl.args.leftXMLG = EMPTY;
        nlg.nl.args.rightXMLG = EMPTY;

        var decoded = decodeText(changed.midXMLG);

        if (!decoded) decoded = changed.midXMLG;
        nlg.nl.args.midXMLG = decoded;

        nlg.nl.setMode('1', undefined, undefined, decoded !== EMPTY, true);
      } else {
        nlg.nl.args.midXMLG = EMPTY;

        var decodedLeft = decodeText(changed.leftXMLG);
        var decodedRight = decodeText(changed.rightXMLG);

        if (!decodedLeft) decodedLeft = changed.leftXMLG;
        nlg.nl.args.leftXMLG = decodedLeft;

        if (!decodedRight) decodedRight = changed.rightXMLG;
        nlg.nl.args.rightXMLG = decodedRight;

        nlg.nl.setMode(
          '2',
          undefined,
          undefined,
          decodedLeft !== EMPTY && decodedRight !== EMPTY,
          true
        );
      }

      // if a student graphs on the number line before they
      // submit an attempt to an earlier getter, we should
      // reapply the chosen drawMode to the number line
      if (nlg.drawMode && nlg.drawMode !== NL_DRAW_MODE.DEFAULT) {
        // verify that the drawMode is still valid
        if (
          (nlg.nl.args.mode === '1' &&
            (nlg.drawMode.includes('<-') || nlg.drawMode.includes('->'))) ||
          (nlg.nl.args.mode === '2' &&
            (nlg.drawMode.includes('--') ||
              (nlg.drawMode.startsWith('<') && nlg.drawMode.endsWith('>'))))
        ) {
          nlg.setDrawMode(nlg.drawMode);
        }
      } else if (savedObjs) {
        // verify that the savedObjs are still valid
        if (
          ((nlg.nl.args.mode === '1' || nlg.nl.args.mode === '3') &&
            savedObjs.length === 1 &&
            savedObjs[0].t === 'Ray') ||
          (nlg.nl.args.mode === '2' &&
            savedObjs.length === 1 &&
            savedObjs[0].t === 'Segment') ||
          (nlg.nl.args.mode === '2' &&
            savedObjs.length === 2 &&
            savedObjs[0].t === 'Ray' &&
            savedObjs[1].t === 'Ray')
        ) {
          nlg.nl.args.objs = savedObjs;
        }
      }
    };

    nlg.grabFocus = function () {
      if (!nlg.e) {
        return;
      }

      setFocused(undefined);

      removeKeyboard();
      currentAttemptGetter = nlg;

      if (lettersButton != null) {
        lettersButton.staysAnimatedColor = true;
      }

      if (
        nlg.nl.getters &&
        nlg.nl.getters.children &&
        nlg.nl.getters.children.length > 0
      ) {
        var ch = nlg.nl.getters.children;

        if (ch.length == 1) {
          ch[0].grabFocus();
        } else {
          ch[2].grabFocus();
        }
      }
    };

    nlg.isLastFocused = function () {
      return nlg.nl.getters.children[0].isFocused();
    };

    nlg.tab = function (dir) {
      var ch = nlg.nl.getters.children;

      if (ch.length == 1) return;

      // Terrible, but it appears these come in reverse order
      // Index 2 is the first child, Index 1 is the arrow image, and Index 0 is the last input field
      ch[2].loseFocus();
      ch[0].grabFocus();
    };

    nlg.setStatus = function (st) {
      nlg.nl.setStatus(st);
      nlg.st = st;
      nlg.e = st !== 'c';

      // also update "editable" status of child getter(s)
      if (nlg.nl.getters) {
        var ch = nlg.nl.getters.children;

        ch[0].e = st !== 'c';

        if (ch.length > 1) {
          ch[2].e = st !== 'c';
        }
      }
    };

    nlg.clear = function (clearBoxes) {
      if (!nlg.e) {
        return;
      }

      if (!clearBoxes) {
        if (nlg.nl.args.objs && nlg.nl.args.objs.length > 0) changedMaybe();
        nlg.nl.args.objs = [];
        nlg.currentObj = undefined;
      }

      if (nlg.nl.getters && clearBoxes) {
        var blank = '<t></t>';

        if (!focused) {
          nlg.nl.args.midXMLG = blank;
          nlg.nl.args.leftXMLG = blank;
          nlg.nl.args.rightXMLG = blank;
          var old = nlg.nl.args.mode;

          nlg.nl.args.mode = undefined;
          var focusMe = nlg.nl.setMode(old);

          if (focusMe) {
            focusMe.grabFocus();
          }
        } else {
          var line = focused.line;
          var wh = line.nlZone;

          if (wh === 'leftXMLG') {
            nlg.nl.args.leftXMLG = blank;
          } else if (wh === 'midXMLG') {
            nlg.nl.args.midXMLG = blank;
          } else if (wh === 'rightXMLG') {
            nlg.nl.args.rightXMLG = blank;
          }

          line.clear();
          line.grabFocus();
        }
      }
    };

    var modeChanger = getButton();

    modeChanger.animateTimeoutOverride = ANIMATE_TIMEOUT_200MS;

    modeChanger.setAllDim(NL_OPTIONS_BUTTON_W, NL_OPTIONS_BUTTON_H);

    modeChanger.paintMe = function (ctx) {
      ctx.save();
      roundRect2(
        ctx,
        0,
        0,
        modeChanger.viewportW,
        modeChanger.viewportH,
        6,
        modeChanger.animated ? getThemeColor() : 'lightgray',
        undefined,
        undefined,
        true
      );

      numLineDrawing(ctx, NL_DRAW_MODE.DEFAULT);

      ctx.restore();
    };

    modeChanger.mouseDownResponse = function () {
      if (nlg.st === 'c') {
        return false;
      }

      // number line grabs focus upon menu being opened
      nlg.grabFocus();

      var poss;

      if (nlg.nl.args.mode === '2') {
        poss = ['O--O', '<OO>', 'O--X', '<OX>', 'X--O', '<XO>', 'X--X', '<XX>'];
      } else {
        poss = ['<--O', 'O-->', '<--X', 'X-->'];
      }

      var listener = function (sel) {
        nlg.setDrawMode(sel);
      };

      var menu = getWordMenu(
        getProblemJSDependencies(),
        poss,
        listener,
        numLineDrawing,
        {
          w: 100,
          h: 20,
        },
        nlg.nl.args.mode == '2' ? 4 : 2,
        false,
        false,
        nlg.drawMode,
        $canvas
      );

      // if student's chosen themeColor is not gray, set it as the floater background color
      // otherwise, the menu's default dark gray color will be used
      if (getThemeName() !== 'gray') menu.setBackgroundColor(getThemeColor());
      menu.includeShadow = true;

      floater.setPanel(menu);

      var tl = getFullTopLeftLocation(nlg);

      menu.viewportX = tl.x + nlg.viewportW / 2 - menu.viewportW / 2;
      menu.viewportY = tl.y + nlg.viewportH + 2;
      modeChanger.animate(paintCanvas);

      return true;
    };

    nlg.modeChanger = modeChanger;

    // add trash can to clear number line
    // Note: requested by trainers so students can easily start over when frustrated
    var clearGraph = getClearAll(getButtonW(), true);

    clearGraph.mouseDownResponse = function () {
      if (!nlg.e || nlg.nl.st === 'c') {
        clearGraph.animates = false;

        return false;
      }

      // reset to no chosen type
      nlg.setDrawMode(NL_DRAW_MODE.DEFAULT);

      // clear anything currently graphed
      nlg.clear(false);

      return true;
    };

    var top = getBasicObject();

    var mode3Directions = getBasicObject();

    if (nlg.args.nl.mode !== '3') {
      top.add(modeChanger);

      top.add(clearGraph);

      top.layoutChildrenLR(15);

      top.sizeMeToFitChildren();

      top.centerVertically();
    } else {
      mode3Directions.gmmName = 'mode3Directions';

      mode3Directions.setAllDim(300, 20);

      mode3Directions.paintMe = function (ctx) {
        ctx.save();

        ctx.translate(150, 16);

        ctx.fillStyle = 'black';
        ctx.font = 'italic 16px ' + lineFont;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';

        if (nlg.drawMode === NL_DRAW_MODE.DEFAULT) {
          ctx.fillText('Click on the line to identify the endpoint.', 0, 0);
        } else if (nlg.drawMode == NL_DRAW_MODE.MID_SELECT) {
          ctx.fillText('Choose a ray.', 0, 0);
        } else {
          ctx.fillText('To make a change, click again.', 0, 0);
        }

        ctx.restore;
      };

      mode3Directions.centerHorizontally();

      nlg.add(mode3Directions);
    }

    nlg.add(top);

    nlg.setDrawMode = function (drawMode) {
      if (!nlg.e) {
        return;
      }

      var currentDrawMode = nlg.drawMode;

      nlg.centerHorizontally();

      if (
        nlg.drawMode === drawMode &&
        nlg.drawMode !== NL_DRAW_MODE.DEFAULT &&
        nlg.args.nl.mode !== '3'
      ) {
        // make sure chosen draw mode is applicable to the mode of the number line
        // e.g. if the student graphed a single ray before submitting a compound
        // inequality, we should clear their attempt because we can't apply
        // the ray to the number line with two marks
        if (
          !(
            nlg.args.nl.mode === '1' &&
            (nlg.drawMode.indexOf('<--') > -1 ||
              nlg.drawMode.indexOf('-->') > -1)
          ) &&
          !(
            nlg.args.nl.mode === '2' &&
            (nlg.drawMode.indexOf('<O') > -1 ||
              nlg.drawMode.indexOf('<X') > -1 ||
              nlg.drawMode.indexOf('O-') > -1 ||
              nlg.drawMode.indexOf('X-') > -1)
          )
        ) {
          // clear anything currently graphed
          nlg.clear(false);
          // reset to no chosen type
          nlg.drawMode = NL_DRAW_MODE.DEFAULT;
        }
      } else {
        nlg.drawMode = drawMode;
      }

      // clear any unfinished work
      if (nlg.currentObj) {
        var ar = nlg.nl.args.objs;

        for (var a = 0; a < ar.length; a++) {
          var o = ar[a];

          if (o === nlg.currentObj) {
            ar.splice(a, 1);
            break;
          }
        }

        nlg.currentObj = null;
      }

      // apply selected draw mode to the number line
      if (nlg.drawMode !== NL_DRAW_MODE.DEFAULT) {
        // if mode is 1 or 2, automatically apply the draw mode to the number line
        // if mode is 3, the user must click on the number line to draw the object
        // because they need to determine the location of the endpoint
        if (nlg.nl.args.mode !== '3') {
          nlg.nl.mouseDownResponse(0, 0);
        }
        // if mode is 3 and user already graphed an object, but is now choosing a
        // different mode, reuse the endpoint of the ray to apply the new mode
        else if (
          currentDrawMode !== drawMode &&
          nlg.nl.args.objs &&
          nlg.nl.args.objs.length > 0
        ) {
          nlg.nl.mouseDownResponse(nlg.nl.args.objs[0].cx, 0);
        }
      }
    };

    nlg.nl = getNumberLine(0, 0, ag.nl, nlg);
    nlg.nl.nlg = nlg;
    var md = ag.nl.mode;

    ag.nl.mode = undefined;
    nlg.nl.setMode(md, row, nlg.agId, true);

    nlg.add(nlg.nl);
    nlg.nl.viewportY = top.viewportH + 5 + mode3Directions.viewportH;

    nlg.nl.mouseDownResponse = function (x, y) {
      if (!nlg.e || nlg.nl.st === 'c') {
        return false;
      }

      if (nlg.nl.getters && y >= nlg.nl.getters.viewportY) {
        // since this only gets called if call to children containing the linegetters has already returned false!
        // want false so that touch users can scroll
        return false;
      }

      nlg.grabFocus();

      changedMaybe();

      if (!nlg.nl.args.objs) {
        nlg.nl.args.objs = [];
      }

      var ni = nlg.nl.getNearestX(x);
      var nx = ni[0];
      var rightMost = ni[1];
      // if mode is 2 and nx is right val rightMost is true

      // if the user is in mode 3, we need to set the drawMode to the selected value
      if (nlg.nl.args.mode === '3') {
        if (nlg.drawMode === NL_DRAW_MODE.DEFAULT) {
          nlg.setDrawMode(NL_DRAW_MODE.MID_SELECT);
        }
        // if the user has already graphed something, we need to clear it
        else if (nlg.nl.args.objs.length > 0) {
          nlg.clear(false);
          nlg.nl.args.objs = [];

          nlg.setDrawMode(NL_DRAW_MODE.MID_SELECT);
        }

        if (nlg.drawMode === NL_DRAW_MODE.MID_SELECT) {
          nlg.nl.args.locationIndicator = {};
          nlg.nl.args.locationIndicator.x = nx;
          nlg.nl.args.locationIndicator.y = nlg.nl.viewportY;
        }

        var poss = ['<--O', 'O-->', '<--X', 'X-->'];

        var listener = function (sel) {
          // set to chosen type
          nlg.setDrawMode(sel);

          // number line grabs focus upon choice being selected
          nlg.grabFocus();

          // simulate a mouse click on the number line to apply selection
          nlg.nl.mouseDownResponse(nx, 0);
        };

        var menu = getWordMenu(
          getProblemJSDependencies(),
          poss,
          listener,
          numLineDrawing,
          {
            w: 100,
            h: 20,
          },
          2,
          false,
          false,
          undefined,
          $canvas
        );

        // if student's chosen themeColor is not gray, set it as the floater background color
        // otherwise, the menu's default dark gray color will be used
        if (getThemeName() !== 'gray') menu.setBackgroundColor(getThemeColor());
        menu.includeShadow = true;

        floater.setPanel(menu);

        var tl = getFullTopLeftLocation(nlg.nl);

        menu.viewportX = tl.x + nlg.nl.viewportW / 2 - menu.viewportW / 2;
        menu.viewportY = tl.y + nlg.nl.viewportH + 4;
      }

      if (
        nlg.drawMode === NL_DRAW_MODE.DEFAULT ||
        nlg.drawMode === NL_DRAW_MODE.MID_SELECT
      ) {
        return;
      }

      if (nlg.nl.args.mode === '1' || nlg.nl.args.mode === '3') {
        var isFill = nlg.drawMode === 'X-->' || nlg.drawMode === '<--X';
        var isLeft = nlg.drawMode === '<--O' || nlg.drawMode === '<--X';

        nlg.currentObj = {
          t: 'Ray',
          fill: isFill,
          cx: nx,
          dir: isLeft ? 'l' : 'r',
        };
        nlg.nl.addObj(nlg.currentObj);
      } else {
        if (
          nlg.drawMode === 'O--O' ||
          nlg.drawMode === 'O--X' ||
          nlg.drawMode === 'X--O' ||
          nlg.drawMode === 'X--X'
        ) {
          nlg.currentObj = { t: 'Segment' };
          nlg.currentObj.first = {
            cx: rightMost ? nx - 174 : nx,
            fill: nlg.drawMode === 'X--O' || nlg.drawMode === 'X--X',
          };
          nlg.currentObj.second = {
            cx: rightMost ? nx : nx + 174,
            fill: nlg.drawMode === 'O--X' || nlg.drawMode === 'X--X',
          };
          nlg.nl.addObj(nlg.currentObj);
        } else {
          // two rays
          var isFillLeft = nlg.drawMode === '<XO>' || nlg.drawMode === '<XX>';
          var isFillRight = nlg.drawMode === '<OX>' || nlg.drawMode === '<XX>';

          // left ray
          nlg.currentObj = {
            t: 'Ray',
            fill: isFillLeft,
            cx: rightMost ? nx - 174 : nx,
            dir: 'l',
          };
          nlg.nl.addObj(nlg.currentObj);
          // right ray
          nlg.currentObj = {
            t: 'Ray',
            fill: isFillRight,
            cx: rightMost ? nx : nx + 174,
            dir: 'r',
          };
          nlg.nl.addObj(nlg.currentObj);
        }
      }

      nlg.currentObj = undefined; // resest currentObj

      nlg.nl.args.locationIndicator = undefined; // remove location indicator

      return true;
    };

    nlg.top = top;

    var subH = 0;

    if (nlg.nl.args.mode === '3') {
      var bottom = getBasicObject();

      bottom.gmmName = 'numberLineBottom';

      bottom.add(clearGraph);

      var sub = getSubmitButton(
        nlg,
        paintCanvas,
        submitAttempt,
        getThemeColor,
        NUMBER_LINE_GMM_NAME,
        $canvas,
        getButtonW()
      );

      bottom.add(sub);

      bottom.layoutChildrenLR(20);

      bottom.sizeMeToFitChildren();
      bottom.centerVertically();

      nlg.add(bottom);

      subH = sub.viewportH;
      bottom.viewportY = nlg.nl.viewportY + nlg.nl.viewportH + 7;
    }

    nlg.resetHeight = function () {
      if (bottom) {
        bottom.viewportY = nlg.nl.viewportY + nlg.nl.viewportH + 7;
      }

      nlg.setAllDim(
        nlg.nl.viewportW,
        nlg.nl.viewportY + nlg.nl.viewportH + (subH === 0 ? 0 : subH + 7)
      );
    };

    nlg.buildSizeRecursive = function () {
      if (nlg.nl && nlg.nl.buildSizeRecursive) {
        nlg.nl.buildSizeRecursive();
      }

      nlg.resetHeight();
    };

    nlg.resetHeight();

    nlg.centerHorizontally();

    nlg.setDrawMode(nlg.drawMode ?? NL_DRAW_MODE.DEFAULT);

    // Required getter function
    nlg.serializeAttempt = function () {
      var g = nlg.insertAttemptIntoJSON();

      return g !== 'invalid' ? g : undefined;
    };

    return nlg;
  };

  var getGrid = function (args) {
    if (!args) {
      args = {};
      args.width = 240;
      args.height = 240;
      args.pixelsPerBlock = 20;
    } else {
      args.width = args.w;
      args.height = args.h;
      args.pixelsPerBlock = args.p;
    }

    var gridWidth = args.width;
    var gridHeight = args.height;

    // obscure workaround to vertical size of grid-based number line
    if (args && args.o && args.o.u) {
      for (var az = 0; az < args.o.u.length; az++) {
        var obj = args.o.u[az];

        if (obj.t === 'Label') {
          if (obj.line === undefined) {
            obj.line = toLine({
              problemJS: getProblemJSDependencies(),
              xml: obj.xml,
            });
            var hSluff = gridHeight / 2 - 10;
            var lineH = obj.line.viewportH;

            if (lineH > hSluff) {
              var diff = lineH - hSluff;

              gridHeight += diff * 2;
              args.h = gridHeight;
            }
          }
        }
      }
    }

    var grid = getBasicObject();

    grid.clip = true;

    grid.args = args || {};

    grid.viewportMargin = 1;
    grid.viewportW = gridWidth + grid.viewportMargin * 2;
    grid.viewportH = gridHeight + grid.viewportMargin * 2;
    grid.width = gridWidth;
    grid.height = gridHeight;

    grid.gridWidth = gridWidth;
    grid.gridHeight = gridHeight;

    grid.gmmName = GRID_GMM_NAME;

    grid.viewportMargin = 1;

    grid.setSelected = function (b) {
      grid.selected = b;

      if (grid.getter) {
        grid.getter.disabled = !b;

        if (b) {
          grid.getter.mShower.stamp.fillStyle = 'gray';
        } else {
          grid.getter.mShower.stamp.fillStyle = 'lightgray';
        }
      }

      if (b) {
        grid.viewportMarginColor = getThemeColor();
      } else {
        grid.viewportMarginColor = 'gray';
      }
    };

    grid.setSelected(false);

    grid.viewportSlideX = grid.gridWidth / 2;
    grid.viewportSlideY = grid.gridHeight / 2;

    if (args.eF) {
      grid.viewportSlideX = 45;
      grid.viewportSlideY = args.width - 45;
      grid.hideTails = true;
    } else if (args.sX) {
      grid.viewportSlideX += args.sX;
    }

    args.uPV = args.uPV || 1;
    args.uPH = args.uPH || 1;

    grid.leftMostUserX = function () {
      return grid.args.eF ? 0 : grid.pixelToUserH(-grid.width / 2);
    };

    grid.rightMostUserX = function () {
      if (grid.args.eF) {
        return grid.pixelToUserH(grid.width - grid.viewportSlideX);
      }

      return grid.pixelToUserH(grid.width / 2);
    };

    grid.leftMostPixelX = function () {
      return grid.args.eF ? 0 : -grid.viewportSlideX;
    };

    grid.rightMostPixelX = function () {
      return grid.leftMostPixelX() + grid.width;
    };

    grid.topPixelY = function () {
      return grid.viewportSlideY;
    };

    grid.bottomPixelY = function () {
      return grid.args.eF ? 0 : grid.topPixelY() - grid.height;
    };

    grid.pixelToUserH = function (p) {
      var upHb = grid.args.uPH || 1;

      return processFloat((p / grid.args.pixelsPerBlock) * upHb);
    };

    grid.pixelToUserV = function (p) {
      var upVb = grid.args.uPV || 1;

      return processFloat((p / grid.args.pixelsPerBlock) * upVb);
    };

    grid.userToPixelH = function (u) {
      var upHb = grid.args.uPH || 1;

      return processFloat((u / upHb) * grid.args.pixelsPerBlock);
    };

    grid.userToPixelV = function (u) {
      var upVb = grid.args.uPV || 1;

      return processFloat((u / upVb) * grid.args.pixelsPerBlock);
    };

    grid.getYParabola = function (x, h, k, a) {
      return a * Math.pow(x - h, 2) + k;
    };

    grid.getXMinusParabola = function (y, h, k, a) {
      return h - Math.pow((y - k) / a, 0.5);
    };

    grid.getXPlusParabola = function (y, h, k, a) {
      return h + Math.pow((y - k) / a, 0.5);
    };

    grid.isFractionObj = function (obj) {
      return obj.vl.indexOf('<f>') != -1;
    };

    grid.getFontFromObj = function (obj) {
      return obj.xAxisLabel || obj.yAxisLabel
        ? 'bold ' + (this.isFractionObj(obj) ? '10' : '14') + 'px ' + lineFont
        : null;
    };

    grid.isAboveItemLimit = function (gridObjects) {
      // Bail out early if there isn't even close to limits
      if (gridObjects.length < 4) {
        return false;
      }

      // Max Limits
      var maxLines = 5;
      var maxRays = 5;
      var maxSegments = 5;
      var maxParabolas = 4;
      var maxCombo = 8;
      var maxPoints = grid.args.eF ? 169 : 225;

      // Counters
      var lineCount = 0;
      var rayCount = 0;
      var segmentCount = 0;
      var parabolaCount = 0;
      var pointCount = 0;
      var comboCount = 0;

      var messageReplacement = 'objects';
      var pastLimit = false;

      for (var a = 0; a < gridObjects.length; a++) {
        var gridItem = gridObjects[a];

        // Make sure it is complete
        if (gridItem.second) {
          if (gridItem.t === 'Line') {
            lineCount++;
            comboCount++;

            if (lineCount > maxLines) {
              messageReplacement = 'lines';
              pastLimit = true;
              break;
            }
          } else if (gridItem.t === 'Ray') {
            rayCount++;
            comboCount++;

            if (rayCount > maxRays) {
              messageReplacement = 'rays';
              pastLimit = true;
              break;
            }
          } else if (gridItem.t === 'Segment') {
            segmentCount++;
            comboCount++;

            if (segmentCount > maxSegments) {
              messageReplacement = 'segments';
              pastLimit = true;
              break;
            }
          } else if (gridItem.t === 'Parabola') {
            parabolaCount++;
            comboCount++;

            if (parabolaCount > maxParabolas) {
              messageReplacement = 'parabolas';
              pastLimit = true;
              break;
            }
          }

          if (comboCount > maxCombo) {
            pastLimit = true;
            break;
          }
        } else {
          if (gridItem.t === 'dot') {
            pointCount++;

            if (pointCount > maxPoints) {
              messageReplacement = 'dots';
              pastLimit = true;
              break;
            }
          }
        }
      }

      if (pastLimit) {
        // Remove last entry
        gridObjects.splice(gridObjects.length - 1, 1);
        getProblemContext().addStackableDialog({
          msg:
            'You have reached the limit of ' +
            messageReplacement +
            ' GMM will allow on this grid.',
          top: 'Reached Limit',
        });
      }

      return pastLimit;
    };

    grid.paintGridDragHoverGlow = function (ctx, obj, linePoints) {
      var activeDragTarget =
        activeGridDrag && activeGridDrag.gag === grid.getter
          ? activeGridDrag.target
          : undefined;

      paintGridDragModeGlow({
        moveModeActive: Boolean(grid.getter?.canUseGridMoveMode?.()),
        ctx,
        obj,
        hoveredObject: grid.hoveredGridDragObject,
        hoveredPoint: grid.hoveredGridDragPoint,
        activeDragTarget,
        linePoints,
      });
    };

    grid.paintObjects = function (
      ctx,
      objects,
      permanent,
      left,
      right,
      top,
      bottom
    ) {
      var xAxisLabelHasFractions = false;

      for (var a = 0; a < objects.length; a++) {
        var obj = objects[a];

        if (obj.xAxisLabel && this.isFractionObj(obj)) {
          xAxisLabelHasFractions = true;
          break;
        }
      }

      var shadeRegions;
      var customRegionColors = false;
      var regionColors = [];

      for (var a = 0; a < objects.length; a++) {
        var obj = objects[a];

        // Check for special opacity request
        if (obj.opacity) ctx.globalAlpha = obj.opacity;
        else ctx.globalAlpha = 1;

        if (obj.t === 'dot' && !obj.high) {
          var col =
            gridGuide && gridGuide.on && gridGuide.dot === obj
              ? 'green'
              : obj.color;

          preCircle(ctx, obj, obj.r, col, obj.open, 2);

          if (!permanent) {
            grid.paintGridDragHoverGlow(ctx, obj);
          }
        } else if (obj.t === 'Parabola') {
          // only dot if user midway
          if (obj.userParabola) {
            // not pre-made, server side
            if (obj.second) {
              obj.h = grid.pixelToUserH(obj.first.cx);
              obj.k = grid.pixelToUserV(obj.first.cy);

              var x = grid.pixelToUserH(obj.second.cx);
              var y = grid.pixelToUserV(obj.second.cy);
              var f = y - obj.k;
              var d = x - obj.h;

              d = d * d;
              obj.a = f / d;

              obj.minX = grid.args.eF ? 0 : grid.leftMostUserX();
              obj.maxX = grid.rightMostUserX();

              // wasteful rebuild of path per paint to account for second point changing
              obj.path = null;
            }
          }

          if (obj.a) {
            ctx.strokeStyle = obj.color || 'black';
            ctx.lineWidth = 3;

            if (!obj.path) {
              var path = [];
              var aa = obj.a;
              var h = obj.h;
              var k = obj.k;
              var inc = grid.pixelToUserH(1) / grid.args.pixelsPerBlock;
              var x;

              obj.minY = grid.args.eF
                ? 0
                : grid.pixelToUserV(grid.bottomPixelY());

              for (x = obj.minX; x <= obj.maxX; x += inc) {
                var y = grid.getYParabola(x, h, k, aa);

                // Check to make sure y is not out of bounds
                if (y >= obj.minY) {
                  path.push([grid.userToPixelH(x), grid.userToPixelV(y)]);
                }
              }

              if (x - inc < obj.maxX) {
                var y = grid.getYParabola(obj.maxX, h, k, aa);

                // Check to make sure y is not out of bounds
                if (y >= obj.minY) {
                  path.push([
                    grid.userToPixelH(obj.maxX),
                    grid.userToPixelV(y),
                  ]);
                }
              }

              obj.path = path;

              if (obj.arrows) {
                var ar = obj.arrows;

                for (var x = 0; x < ar.length; x++) {
                  if (x % 2 == 0) {
                    ar[x] = grid.userToPixelH(ar[x]);
                  } else {
                    ar[x] = grid.userToPixelV(ar[x]);
                  }
                }
              } else {
                // Keep units as user values during calculations
                var leftMostX = grid.args.eF
                  ? 0
                  : grid.pixelToUserH(grid.leftMostPixelX());
                var rightMostX = grid.pixelToUserH(grid.rightMostPixelX());
                var topY = grid.pixelToUserV(grid.topPixelY());
                var bottomY = grid.args.eF
                  ? 0
                  : grid.pixelToUserV(grid.bottomPixelY());

                var y = aa > 0 ? topY : bottomY;

                var leftY = grid.getYParabola(leftMostX, h, k, aa);

                var x = grid.getXMinusParabola(y, h, k, aa);

                if (x > leftMostX) {
                  leftY = y;
                }

                var leftArrowX2 = grid.getXMinusParabola(leftY, h, k, aa);
                var leftArrowX1 = leftArrowX2 + inc;
                var leftArrowY1 = grid.getYParabola(leftArrowX1, h, k, aa);
                var leftArrowY2 = leftY;

                var rightY = grid.getYParabola(rightMostX, h, k, aa);

                x = grid.getXPlusParabola(y, h, k, aa);

                if (x < rightMostX) {
                  rightY = y;
                }

                var rightArrowX2 = grid.getXPlusParabola(rightY, h, k, aa);
                var rightArrowX1 = rightArrowX2 - inc;
                var rightArrowY1 = grid.getYParabola(rightArrowX1, h, k, aa);
                var rightArrowY2 = rightY;

                // Convert units to pixels for display
                var ar1 = [
                  grid.userToPixelH(leftArrowX1),
                  grid.userToPixelV(leftArrowY1),
                  grid.userToPixelH(leftArrowX2),
                  grid.userToPixelV(leftArrowY2),
                ];
                var ar2 = [
                  grid.userToPixelH(rightArrowX1),
                  grid.userToPixelV(rightArrowY1),
                  grid.userToPixelH(rightArrowX2),
                  grid.userToPixelV(rightArrowY2),
                ];

                obj.ar1 = ar1;
                obj.ar2 = ar2;
              }
            }

            if (!permanent) {
              grid.paintGridDragHoverGlow(ctx, obj);
            }

            ctx.save();

            if (obj.sideways) {
              ctx.rotate(Math.PI / 2);
            }

            ctx.beginPath();
            ctx.moveTo(obj.path[0][0], obj.path[0][1]);

            for (var x = 1; x < obj.path.length; x++) {
              ctx.lineTo(obj.path[x][0], obj.path[x][1]);
            }

            ctx.stroke();
            ctx.restore();

            if (obj.arrows) {
              ctx.beginPath();
              var ar = obj.arrows;

              if (!obj.skipFirstArrow || obj.showArrows)
                arrow(ctx, ar[0], ar[1], ar[2], ar[3]);
              if (!obj.skipSecondArrow || obj.showArrows)
                arrow(ctx, ar[4], ar[5], ar[6], ar[7]);
              ctx.stroke();
            } else if (obj.ar1 || obj.ar2) {
              var ar;

              if ((obj.ar1 && !obj.skipFirstArrow) || obj.showArrows) {
                ar = obj.ar1;
                arrow(ctx, ar[0], ar[1], ar[2], ar[3]);
              }

              if ((obj.ar2 && !obj.skipSecondArrow) || obj.showArrows) {
                ar = obj.ar2;
                arrow(ctx, ar[0], ar[1], ar[2], ar[3]);
              }

              ctx.stroke();
            }
          }

          if (!obj.first.hideDot && !obj.hideFirst) {
            paintHistoryRestoredIncompleteHalo(ctx, obj);
            preCircle(ctx, obj.first, 5, ctx.strokeStyle, obj.first.open);
          }

          if (obj.second) {
            if (!obj.hideSecond) {
              preCircle(ctx, obj.second, 5, ctx.strokeStyle, obj.second.open);
            }
          }

          ctx.strokeStyle = 'black';
        } else if (obj.t === 'Exponential') {
          if (!obj.path) {
            var path = [];
            var min = obj.min != null ? obj.min : grid.leftMostUserX();
            var max = obj.max != null ? obj.max : grid.rightMostUserX();
            var inc = grid.pixelToUserH(1) / 20;

            var lastOnScreen;
            var lastXPixel;
            var lastYPixel;

            for (var x = min; x <= max; x += inc) {
              var xPixel = grid.userToPixelH(x);
              var yPixel = grid.userToPixelV(
                obj.a * Math.pow(obj.b, x) + (obj.c != null ? obj.c : 0)
              );

              var onScreen = grid.args.eF
                ? xPixel >= 0 &&
                  xPixel < grid.width &&
                  yPixel >= 0 &&
                  yPixel < grid.height
                : xPixel >= -grid.width / 2 &&
                  xPixel < grid.width / 2 &&
                  yPixel >= -grid.height / 2 &&
                  yPixel < grid.height / 2;

              if (onScreen && !lastOnScreen)
                path.push([lastXPixel, lastYPixel]);

              if (onScreen || lastOnScreen) path.push([xPixel, yPixel]);

              lastOnScreen = onScreen;
              lastXPixel = xPixel;
              lastYPixel = yPixel;
            }

            obj.path = path;
          }

          ctx.strokeStyle = obj.color || 'black';
          ctx.lineWidth = 3;
          ctx.save();

          if (obj.arrowType && obj.arrowType != 'NONE') {
            var minX = 10000;
            var maxX = -10000;

            // Track the extent of visible x values for arrow positioning later
            for (var i = 0; i < obj.path.length; i++) {
              var x = obj.path[i][0];

              if (x < minX) minX = x;
              if (x > maxX) maxX = x;
            }

            var inc = grid.pixelToUserH(1) * 3;
            var leftArrowX2 = grid.pixelToUserH(minX);
            var leftArrowY2 = exponentialY(obj.a, obj.b, leftArrowX2, obj.c);
            var leftArrowX1 = leftArrowX2 + inc;
            var leftArrowY1 = exponentialY(obj.a, obj.b, leftArrowX1, obj.c);

            var rightArrowX2 = grid.pixelToUserH(maxX);
            var rightArrowY2 = exponentialY(obj.a, obj.b, rightArrowX2, obj.c);
            var rightArrowX1 = rightArrowX2 - inc;
            var rightArrowY1 = exponentialY(obj.a, obj.b, rightArrowX1, obj.c);

            if (obj.arrowType == 'LEFT' || obj.arrowType == 'BOTH') {
              ctx.beginPath();
              arrow(
                ctx,
                grid.userToPixelH(leftArrowX1),
                grid.userToPixelV(leftArrowY1),
                grid.userToPixelH(leftArrowX2),
                grid.userToPixelV(leftArrowY2)
              );
              ctx.stroke();
            }

            if (obj.arrowType == 'RIGHT' || obj.arrowType == 'BOTH') {
              ctx.beginPath();
              arrow(
                ctx,
                grid.userToPixelH(rightArrowX1),
                grid.userToPixelV(rightArrowY1),
                grid.userToPixelH(rightArrowX2),
                grid.userToPixelV(rightArrowY2)
              );
              ctx.stroke();
            }
          }

          ctx.beginPath();
          ctx.moveTo(obj.path[0][0], obj.path[0][1]);

          for (var x = 1; x < obj.path.length; x++) {
            ctx.lineTo(obj.path[x][0], obj.path[x][1]);
          }

          ctx.stroke();

          if (obj.leftDotType) {
            var min = obj.min != null ? obj.min : grid.leftMostUserX();
            var inc = grid.pixelToUserH(1);
            var leftDotX = min;
            var leftDotY = exponentialY(obj.a, obj.b, leftDotX, obj.c);

            dot(
              ctx,
              grid.userToPixelH(leftDotX),
              grid.userToPixelV(leftDotY),
              5,
              obj.leftDotType == 'CLOSED'
            );
          }

          if (obj.rightDotType) {
            var max = obj.max != null ? obj.max : grid.rightMostUserX();
            var inc = grid.pixelToUserH(1);
            var rightDotX = max;
            var rightDotY = exponentialY(obj.a, obj.b, rightDotX, obj.c);

            dot(
              ctx,
              grid.userToPixelH(rightDotX),
              grid.userToPixelV(rightDotY),
              5,
              obj.rightDotType == 'CLOSED'
            );
          }

          ctx.restore();
        } else if (obj.t === 'ValueGridObject') {
          ctx.strokeStyle = obj.color || 'black';
          ctx.lineWidth = 3;
          ctx.save();

          if (obj.arrowType && obj.arrowType != 'NONE') {
            var inc = grid.pixelToUserH(1) * 3;
            var leftArrowX1 = obj.leftArrowX1;
            var leftArrowY1 = obj.leftArrowY1;
            var leftArrowX2 = obj.leftArrowX2;
            var leftArrowY2 = obj.leftArrowY2;
            var rightArrowX1 = obj.rightArrowX1;
            var rightArrowY1 = obj.rightArrowY1;
            var rightArrowX2 = obj.rightArrowX2;
            var rightArrowY2 = obj.rightArrowY2;

            if (obj.arrowType == 'LEFT' || obj.arrowType == 'BOTH') {
              ctx.beginPath();
              arrow(
                ctx,
                grid.userToPixelH(leftArrowX1),
                grid.userToPixelV(leftArrowY1),
                grid.userToPixelH(leftArrowX2),
                grid.userToPixelV(leftArrowY2)
              );
              ctx.stroke();
            }

            if (obj.arrowType == 'RIGHT' || obj.arrowType == 'BOTH') {
              ctx.beginPath();
              arrow(
                ctx,
                grid.userToPixelH(rightArrowX1),
                grid.userToPixelV(rightArrowY1),
                grid.userToPixelH(rightArrowX2),
                grid.userToPixelV(rightArrowY2)
              );
              ctx.stroke();
            }
          }

          ctx.strokeStyle = obj.color || 'black';
          ctx.lineWidth = 3;
          ctx.save();

          for (var looper = 0; looper < obj.paths.length; looper++) {
            var path = obj.paths[looper];

            ctx.beginPath();
            ctx.moveTo(path[0][0], path[0][1]);

            for (var x = 1; x < path.length; x++) {
              ctx.lineTo(path[x][0], path[x][1]);
            }

            ctx.stroke();
          }

          ctx.restore();

          if (obj.leftDotType) {
            var min = obj.min != null ? obj.min : grid.leftMostUserX();
            var inc = grid.pixelToUserH(1);
            var leftDotX = obj.leftDotX;
            var leftDotY = obj.leftDotY;

            dot(
              ctx,
              grid.userToPixelH(leftDotX),
              grid.userToPixelV(leftDotY),
              5,
              obj.leftDotType == 'CLOSED'
            );
          }

          if (obj.rightDotType) {
            var max = obj.max != null ? obj.max : grid.rightMostUserX();
            var inc = grid.pixelToUserH(1);
            var rightDotX = obj.rightDotX;
            var rightDotY = obj.rightDotY;

            dot(
              ctx,
              grid.userToPixelH(rightDotX),
              grid.userToPixelV(rightDotY),
              5,
              obj.rightDotType == 'CLOSED'
            );
          }

          ctx.restore();
        } else if (obj.t === 'SquareRoot') {
          if (!obj.path) {
            var path = [];
            var min = grid.leftMostUserX();
            var max = grid.rightMostUserX();
            var inc = grid.pixelToUserH(1);

            var lastOnScreen;
            var lastXPixel;
            var lastYPixel;

            for (var x = min; x <= max; x += inc) {
              var xPixel = grid.userToPixelH(x);
              var yPixel = grid.userToPixelV(
                obj.a * Math.sqrt(x + obj.b) + obj.c
              );

              var onScreen = grid.args.eF
                ? xPixel >= 0 &&
                  xPixel < grid.width &&
                  yPixel >= 0 &&
                  yPixel < grid.height
                : xPixel >= -grid.width / 2 &&
                  xPixel < grid.width / 2 &&
                  yPixel >= -grid.height / 2 &&
                  yPixel < grid.height / 2;

              if (onScreen && !lastOnScreen)
                path.push([lastXPixel, lastYPixel]);

              if (onScreen || lastOnScreen) path.push([xPixel, yPixel]);

              lastOnScreen = onScreen;
              lastXPixel = xPixel;
              lastYPixel = yPixel;
            }

            obj.path = path;
          }

          ctx.strokeStyle = obj.color || 'black';
          ctx.lineWidth = 3;
          ctx.save();

          ctx.beginPath();
          ctx.moveTo(obj.path[0][0], obj.path[0][1]);

          for (var x = 1; x < obj.path.length; x++) {
            ctx.lineTo(obj.path[x][0], obj.path[x][1]);
          }

          ctx.stroke();
          ctx.restore();
        } else if (obj.t === 'Hyperbola') {
          if (!obj.paths) {
            var paths = [];
            var min = grid.leftMostUserX();
            var max = grid.rightMostUserX();
            var inc = grid.pixelToUserH(1);

            var x = min;

            for (var asi = 0; asi <= obj.asym.length; asi++) {
              var as = 0;

              if (asi < obj.asym.length) {
                as = obj.asym[asi];
              } else {
                as = max + 1;
              }

              if (x < as) var path = [];

              while (x < as) {
                var den = 0;

                for (var xx = 0; xx < obj.den.length; xx++) {
                  den += obj.den[xx] * Math.pow(x, xx);
                }

                if (den !== 0) {
                  var num = 0;

                  for (var xx = 0; xx < obj.num.length; xx++) {
                    num += obj.num[xx] * Math.pow(x, xx);
                  }

                  var output = num / den;

                  if (Math.abs(output) < 1000) {
                    path.push([
                      grid.userToPixelH(x),
                      grid.userToPixelV(output),
                    ]);
                  }
                }

                x += inc;
              }

              if (path && path.length > 1) {
                paths.push(path);
              }
            }

            obj.paths = paths;
          }

          ctx.strokeStyle = obj.color || 'black';
          ctx.lineWidth = 3;
          ctx.save();

          for (var looper = 0; looper < obj.paths.length; looper++) {
            var path = obj.paths[looper];

            ctx.beginPath();
            ctx.moveTo(path[0][0], path[0][1]);

            for (var x = 1; x < path.length; x++) {
              ctx.lineTo(path[x][0], path[x][1]);
            }

            ctx.stroke();
          }

          ctx.restore();
        } else if (obj.t === 'Segment' || obj.t === 'Line' || obj.t === 'Ray') {
          var GG = gridGuide && gridGuide.on && gridGuide.dot;
          var skipFirstForGG = GG && !obj.second;
          // compare x and y-coordinates of the second point to the gridGuide dot
          // to determine if we should draw the second point
          var skipSecondForGG =
            GG &&
            obj.second &&
            gridGuide.dot &&
            obj.second.cx === gridGuide.dot.cx &&
            obj.second.cy === gridGuide.dot.cy;

          ctx.strokeStyle = obj.color || 'black';
          ctx.lineWidth = obj.lw || 3;

          var linePoints = [];
          var useInters = [];

          if (obj.second) {
            var x1 = obj.first.cx;
            var y1 = obj.first.cy;
            var x2 = obj.second.cx;
            var y2 = obj.second.cy;
            var lx = grid.args.eF ? 0 : -grid.viewportSlideX;
            var rx = grid.width - grid.viewportSlideX;
            var by = grid.args.eF ? 0 : -(grid.height - grid.viewportSlideY);
            var ty = grid.viewportSlideY;

            useInters = grid.getLinePoints(x1, y1, x2, y2, lx, by, rx, ty);
            linePoints = grid.getLinePoints(x1, y1, x2, y2, lx, by, rx, ty);
          }

          if (!permanent) {
            grid.paintGridDragHoverGlow(ctx, obj, linePoints);
          }

          var x1, y1, x2, y2;

          paintHistoryRestoredIncompleteHalo(ctx, obj);

          if (obj.t === 'Segment') {
            if (obj.second) {
              if (obj.dotted) {
                dottedLine(
                  ctx,
                  obj.first.cx,
                  obj.first.cy,
                  obj.second.cx,
                  obj.second.cy,
                  obj.color
                );
              } else {
                ctx.beginPath();
                ctx.moveTo(obj.first.cx, obj.first.cy);
                ctx.lineTo(obj.second.cx, obj.second.cy);
                ctx.stroke();
              }

              if (obj.arrows) {
                for (var az = 0; az < obj.arrows.length; az++) {
                  var ar = obj.arrows[az];

                  ctx.beginPath();
                  arrow(ctx, ar[0], ar[1], ar[2], ar[3]);
                  ctx.stroke();
                }
              }

              if (!obj.second.hideDot && !skipSecondForGG) {
                preCircle(
                  ctx,
                  obj.second,
                  5,
                  ctx.strokeStyle,
                  obj.second.open,
                  2
                );
              }

              // for shading
              if (obj.first.cx == obj.second.cx) {
                if (obj.first.cy < obj.second.cy) {
                  x1 = obj.first.cx;
                  y1 = obj.first.cy;
                  x2 = obj.second.cx;
                  y2 = obj.second.cy;
                } else {
                  x1 = obj.second.cx;
                  y1 = obj.second.cy;
                  x2 = obj.first.cx;
                  y2 = obj.first.cy;
                }
              } else {
                x1 = useInters[0].x;
                y1 = useInters[0].y;
                x2 = useInters[1].x;
                y2 = useInters[1].y;
              }
            }

            if (!obj.first.hideDot && !skipFirstForGG) {
              preCircle(ctx, obj.first, 5, ctx.strokeStyle, obj.first.open, 2);
            }
          } else if (obj.t === 'Line') {
            if (!obj.hideFirst) {
              preCircle(ctx, obj.first, 5, ctx.strokeStyle);
            }

            if (obj.second) {
              x1 = linePoints[0].x;
              y1 = linePoints[0].y;
              x2 = linePoints[1].x;
              y2 = linePoints[1].y;

              if (obj.dotted) {
                dottedLine(ctx, x1, y1, x2, y2, obj.color);
              } else {
                ctx.beginPath();
                ctx.moveTo(x1, y1);
                ctx.lineTo(x2, y2);
                ctx.stroke();
              }

              ctx.beginPath();
              arrow(ctx, x1, y1, x2, y2);
              arrow(ctx, x2, y2, x1, y1);
              ctx.stroke();

              if (!obj.hideSecond) {
                preCircle(ctx, obj.second, 5, ctx.strokeStyle);
              }
            }
          } else if (obj.t === 'Ray') {
            if (obj.second) {
              if (!obj.hideSecond) {
                preCircle(ctx, obj.second, 5, ctx.strokeStyle);
              }

              x1 = obj.first.cx;
              y1 = obj.first.cy;
              x2 = undefined;
              y2 = undefined;

              if (obj.second.cx == x1) {
                x2 = obj.second.cx;

                if (obj.second.cy > y1) {
                  y2 = useInters[1].y;
                } else {
                  y2 = useInters[0].y;
                }
              } else if (obj.second.cy == y1) {
                y2 = obj.second.cy;

                if (obj.second.cx > obj.first.cx) {
                  x2 = useInters[1].x;
                } else {
                  x2 = useInters[0].x;
                }
              } else {
                if (obj.second.cx > obj.first.cx) {
                  x2 = useInters[1].x;
                  y2 = useInters[1].y;
                } else {
                  x2 = useInters[0].x;
                  y2 = useInters[0].y;
                }
              }

              if (obj.dotted) {
                dottedLine(ctx, x1, y1, x2, y2, obj.color);
              } else {
                ctx.beginPath();
                ctx.moveTo(x1, y1);
                ctx.lineTo(x2, y2);
                arrow(ctx, x1, y1, x2, y2);
                ctx.stroke();
              }

              ctx.beginPath();
              arrow(ctx, x1, y1, x2, y2);
              ctx.stroke();
            }

            if (!obj.hideFirst) {
              preCircle(ctx, obj.first, 5, ctx.strokeStyle, obj.open);
            }
          }

          if (obj.shade) {
            if (obj.second) {
              if (obj.shadeColor != null) customRegionColors = true;

              var region = [];

              // ensure single quadrant graphs use points on the grid for shading
              if (args.eF) {
                left = 0;
                bottom = 0;
              }

              // precisely vertical
              if (x1 === x2) {
                shadeHelper(region, x1, bottom);

                if (obj.shade === 'L') {
                  shadeHelper(region, left, bottom);
                  shadeHelper(region, left, top);
                  shadeHelper(region, x2, top);
                } else {
                  shadeHelper(region, x2, top);
                  shadeHelper(region, right, top);
                  shadeHelper(region, right, bottom);
                }
              } else if (y1 === y2) {
                // horizontal
                shadeHelper(region, left, y1);

                if (obj.shade === 'G') {
                  shadeHelper(region, left, top);
                  shadeHelper(region, right, top);
                  shadeHelper(region, right, y1);
                } else {
                  shadeHelper(region, right, y1);
                  shadeHelper(region, right, bottom);
                  shadeHelper(region, left, bottom);
                }
              } else if (Math.abs(x1 - x2) <= 3) {
                // technically not vertical, but intended as vertical by user
                var topX = x1;
                var topY = y1;
                var bottomX = x2;
                var bottomY = y2;

                if (y2 > y1) {
                  topX = x2;
                  topY = y2;
                  bottomX = x1;
                  bottomY = y1;
                }

                shadeHelper(region, bottomX, bottomY);
                shadeHelper(region, topX, topY);

                if (obj.shade == 'L') {
                  shadeHelper(region, left, top);
                  shadeHelper(region, left, bottom);
                } else {
                  shadeHelper(region, right, top);
                  shadeHelper(region, right, bottom);
                }
              } else {
                // up/down
                shadeHelper(region, x1, y1);

                // pos slope
                if ((y2 - y1) / (x2 - x1) > 0) {
                  if (obj.shade === 'G') {
                    // bot, left?
                    if (y1 == bottom) {
                      shadeHelper(region, left, bottom);
                    }

                    shadeHelper(region, left, top);

                    // top right inc?
                    if (y2 < top) {
                      shadeHelper(region, right, top);
                    }

                    shadeHelper(region, x2, y2);
                  } else {
                    shadeHelper(region, x2, y2);

                    // top right?
                    if (y2 == top) {
                      shadeHelper(region, right, top);
                    }

                    shadeHelper(region, right, bottom);

                    if (y1 > bottom) {
                      shadeHelper(region, left, bottom);
                    }
                  }
                } else {
                  // Point order must be swapped for rays to ensure clockwise order of polygon point pairs
                  var needsSwap = obj.t === 'Ray' && x1 > x2;
                  var lx = needsSwap ? x2 : x1;
                  var rx = needsSwap ? x1 : x2;
                  var ly = needsSwap ? y2 : y1;
                  var ry = needsSwap ? y1 : y2;

                  region = [];
                  shadeHelper(region, lx, ly);

                  // neg slope
                  if (obj.shade == 'G') {
                    if (ly < top) {
                      shadeHelper(region, left, top);
                    }

                    shadeHelper(region, right, top);

                    // bot right?
                    if (ry == bottom || (needsSwap && ry == 0)) {
                      shadeHelper(region, right, bottom);
                    }

                    shadeHelper(region, rx, ry);
                  } else {
                    shadeHelper(region, rx, ry);

                    if (ry > bottom) {
                      shadeHelper(region, right, bottom);
                    }

                    shadeHelper(region, left, bottom);

                    if (ly == top) {
                      shadeHelper(region, left, top);
                    }
                  }
                }
              }

              if (!shadeRegions) {
                shadeRegions = [];
              }

              shadeRegions.push(region);
              regionColors.push(
                obj.shadeColor != null ? obj.shadeColor : 'gray'
              );
            }
          }
        } else if (obj.t === 'Label') {
          ctx.save();
          ctx.scale(1, -1);

          obj.line.viewportX = grid.userToPixelH(obj.cx);
          obj.line.viewportY = 10;

          if (obj.anchor === 'TOP_CENTER') {
            obj.line.viewportX -= obj.line.viewportW / 2;
          }

          obj.line.paint(ctx);
          ctx.restore();
        } else if (obj.t === 'ValueLabel') {
          ctx.save();
          ctx.scale(1, -1);

          var line = toLine({
            problemJS: getProblemJSDependencies(),
            xml: obj.vl,
            font: this.getFontFromObj(obj),
          });
          var vlWidth = line.viewportW;
          var vlHeight = line.viewportH;
          var margin = 8;

          if (obj.margin) margin = obj.margin;

          line.viewportX = obj.cx;
          line.viewportY =
            obj.xAxisLabel && xAxisLabelHasFractions ? obj.cy - 4 : obj.cy;

          switch (obj.layout) {
            case 'TOP':
              line.viewportX -= vlWidth / 2;
              line.viewportY -= vlHeight + margin;
              break;
            case 'BOTTOM':
              line.viewportX -= vlWidth / 2;
              line.viewportY += margin;
              break;
            case 'LEFT':
              line.viewportX -= vlWidth + margin;
              line.viewportY -= vlHeight / 2;
              break;
            case 'RIGHT':
              line.viewportX += margin;
              line.viewportY -= vlHeight / 2;
              break;
            case 'TOP_LEFT':
              line.viewportX -= vlWidth + margin;
              line.viewportY -= vlHeight + margin;
              break;
            case 'TOP_RIGHT':
              line.viewportX += margin;
              line.viewportY -= vlHeight + margin;
              break;
            case 'BOTTOM_LEFT':
              line.viewportX -= vlWidth + margin;
              line.viewportY += margin;
              break;
            case 'BOTTOM_RIGHT':
              line.viewportX += margin;
              line.viewportY += margin;
              break;
            case 'CENTER':
              line.viewportX -= vlWidth / 2;
              line.viewportY -= vlHeight / 2;
              break;
          }

          if (obj.background) {
            ctx.fillColor = obj.background;
            ctx.fillRect(line.viewportX, line.viewportY, vlWidth, vlHeight);
          }

          line.paint(ctx);
          ctx.restore();
        } else if (obj.t === 'Circle') {
          ctx.save();
          ctx.strokeStyle = obj.color;
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.arc(
            grid.userToPixelH(obj.cx),
            grid.userToPixelV(obj.cy),
            grid.userToPixelH(obj.r),
            0,
            2 * Math.PI
          );
          ctx.stroke();

          ctx.restore();
        } else if (obj.t === 'Arc') {
          ctx.save();
          ctx.strokeStyle = obj.color;
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.arc(
            grid.userToPixelH(obj.cx),
            grid.userToPixelV(obj.cy),
            grid.userToPixelH(obj.r),
            obj.start,
            obj.extent
          );
          ctx.stroke();

          ctx.restore();
        } else if (obj.t === 'Cubic') {
          if (!obj.path) {
            var path = [];
            var min = grid.leftMostUserX();
            var max = grid.rightMostUserX();
            var inc = grid.pixelToUserH(1);

            var lastOnScreen;
            var lastXPixel;
            var lastYPixel;

            for (var x = min; x <= max; x += inc) {
              var xPixel = grid.userToPixelH(x);
              var yPixel = grid.userToPixelV(obj.a * Math.pow(x, 3) + obj.c);

              var onScreen =
                xPixel >= -grid.width / 2 &&
                xPixel < grid.width / 2 &&
                yPixel >= -grid.height / 2 &&
                yPixel < grid.height / 2;

              if (onScreen && !lastOnScreen)
                path.push([lastXPixel, lastYPixel]);

              if (onScreen || lastOnScreen) path.push([xPixel, yPixel]);

              lastOnScreen = onScreen;
              lastXPixel = xPixel;
              lastYPixel = yPixel;
            }

            obj.path = path;
          }

          ctx.strokeStyle = obj.color || 'black';
          ctx.lineWidth = 3;
          ctx.save();
          ctx.beginPath();
          ctx.moveTo(obj.path[0][0], obj.path[0][1]);

          for (var x = 1; x < obj.path.length; x++) {
            ctx.lineTo(obj.path[x][0], obj.path[x][1]);
          }

          ctx.stroke();
          ctx.restore();
        } else if (obj.t != 'dot') {
          console.error('paintObject not coded for grid object ' + obj.t);
        }
      }

      if (shadeRegions) {
        ctx.save();
        var intersection;

        if (customRegionColors) {
          for (var a = 0; a < shadeRegions.length; a++) {
            // try rebuilding intersection with just x, y
            var i = shadeRegions[a];
            var ni = [];

            for (var b = 0; b < i.length; b++) {
              ni.push({ x: Math.round(i[b].x), y: Math.round(i[b].y) });
            }

            intersection = ni;

            if (grid.args.eF) {
              var gridR = [
                { x: 0, y: 0 },
                { x: 0, y: top },
                { x: right, y: top },
                { x: right, y: 0 },
              ];

              intersection = getIntersectingPolygon(
                ni,
                gridR,
                getProblemContext().errorToServer
              );
            }

            ctx.beginPath();

            for (var b = 0; b < intersection.length; b++) {
              if (b === 0) {
                ctx.moveTo(intersection[b].x, intersection[b].y);
              } else {
                ctx.lineTo(intersection[b].x, intersection[b].y);
              }
            }

            ctx.closePath();
            ctx.fillStyle = regionColors[a];
            ctx.globalAlpha = 0.65;
            var restore = ctx.globalCompositeOperation;

            ctx.globalCompositeOperation = 'multiply';
            ctx.fill();
            ctx.globalAlpha = 1;
            ctx.globalCompositeOperation = restore;
          }
        } else {
          for (var a = 0; a < shadeRegions.length; a++) {
            var region = shadeRegions[a];

            if (intersection === undefined) {
              intersection = region;
            } else {
              intersection = getIntersectingPolygon(
                intersection,
                region,
                getProblemContext().errorToServer
              );
              var i = intersection;
              var ni = [];

              for (var z = 0; z < i.length; z++) {
                ni.push({ x: Math.round(i[z].x), y: Math.round(i[z].y) });
              }

              intersection = ni;
            }
          }

          // try rebuilding intersection with just x, y
          var i = intersection;
          var ni = [];

          for (var a = 0; a < i.length; a++) {
            ni.push({ x: Math.round(i[a].x), y: Math.round(i[a].y) });
          }

          intersection = ni;

          if (grid.args.eF) {
            var gridR = [
              { x: 0, y: 0 },
              { x: 0, y: top },
              { x: right, y: top },
              { x: right, y: 0 },
            ];

            intersection = getIntersectingPolygon(
              ni,
              gridR,
              getProblemContext().errorToServer
            );
          }

          ctx.beginPath();

          for (var b = 0; b < intersection.length; b++) {
            if (b === 0) {
              ctx.moveTo(intersection[b].x, intersection[b].y);
            } else {
              ctx.lineTo(intersection[b].x, intersection[b].y);
            }
          }

          ctx.closePath();
          ctx.fillStyle = 'gray';
          ctx.globalAlpha = 0.4;
          ctx.fill();
          ctx.globalAlpha = 1;
        }

        ctx.restore();
      }

      for (var a = 0; a < objects.length; a++) {
        var obj = objects[a];

        if (obj.t === 'dot' && obj.high) {
          preCircle(ctx, obj, obj.r, obj.color, obj.open);
        }
      }
    };

    grid.getLinePoints = function (x1, y1, x2, y2, minX, minY, maxX, maxY) {
      var linePoints = [];

      if (x2 - x1 === 0) {
        linePoints.push({ x: x1, y: minY });
        linePoints.push({ x: x1, y: maxY });
      } else if (y2 - y1 === 0) {
        linePoints.push({ x: minX, y: y1 });
        linePoints.push({ x: maxX, y: y1 });
      } else {
        var m = (y2 - y1) / (x2 - x1);
        var b = y1 - m * x1;
        var inters = [];

        inters.push({ x: minX, y: m * minX + b });
        inters.push({ x: maxX, y: m * maxX + b });
        inters.push({ x: (maxY - b) / m, y: maxY });
        inters.push({ x: (minY - b) / m, y: minY });

        while (inters.length > 0) {
          var minX = 10000;
          var minI = -1;

          for (var z = 0; z < inters.length; z++) {
            if (inters[z].x < minX) {
              minI = z;
              minX = inters[z].x;
            }
          }

          linePoints.push(inters[minI]);
          inters.splice(minI, 1);
        }

        linePoints.splice(0, 1);
        linePoints.splice(2, 1);
      }

      return linePoints;
    };

    grid.clearCurrentMaybeSave = function () {
      if (!grid.currentObj) return;

      if (!grid.currentObj.second) {
        for (var a = 0; a < grid.args.o.u.length; a++) {
          if (grid.currentObj === grid.args.o.u[a]) {
            grid.args.o.u.splice(a, 1);
            break;
          }
        }
      }

      grid.currentObj = undefined;
    };

    grid.addLiney = function (liney, perm) {
      if (perm) {
        grid.args.o.p.push(liney);
      } else {
        grid.args.o.u.push(liney);
      }
    };

    grid.gridGuideX = function () {
      var user = grid.args.o.u;

      if (gridGuide.currentObj && gridGuide.currentObj.first) {
        if (gridGuide.currentObj.first === gridGuide.dot) {
          // remove currentObj from user
          for (var a = 0; a < user.length; a++) {
            if (gridGuide.currentObj === user[a]) {
              user.splice(a, 1);
              break;
            }
          }

          gridGuide.currentObj = undefined;
          grid.currentObj = undefined;
        } else {
          gridGuide.currentObj.second = undefined;
        }

        gridGuide.dot = undefined;
      } else {
        // just a point
        for (var a = 0; a < user.length; a++) {
          if (gridGuide.dot === user[a]) {
            user.splice(a, 1);
            gridGuide.dot = undefined;
            break;
          }
        }
      }
    };

    grid.gridCoordinatesToPixelsFromTopLeftOfGrid = function (gx, gy) {
      var rx = gx + grid.viewportSlideX;
      var ry = grid.viewportSlideY - gy;

      return { x: rx, y: ry };
    };

    grid.paintMe = function (ctx) {
      var numberLine = grid.args.nl;

      ctx.save();

      if (grid.scaledDown < 1) {
        // Position based of scale ratio
        tx = (grid.width - grid.width * grid.scaledDown) / 2;
        ty = (grid.height - grid.height * grid.scaledDown) / 2;

        if (grid.args.eF) {
          // Readjust slide offsets based on scale ratio
          tx = grid.viewportSlideX - grid.scaledDown * grid.viewportSlideX;
          ty +=
            (grid.viewportSlideY - grid.scaledDown * grid.viewportSlideY) / 2;
        }

        ctx.translate(-tx, -ty);
      }

      ctx.scale(1, -1);

      var units = grid.gridWidth / grid.args.pixelsPerBlock;
      var leftMostXPixels = -grid.viewportSlideX;
      var rightMostXPixels = leftMostXPixels + grid.width;
      var topYPixels = grid.viewportSlideY;
      var bottomYPixels = topYPixels - grid.height;
      var firstX = Math.abs(leftMostXPixels / grid.args.pixelsPerBlock);
      var lastX = Math.abs(rightMostXPixels / grid.args.pixelsPerBlock);

      if (leftMostXPixels < 0) {
        firstX = Math.floor(firstX);
        firstX *= -1;
      } else {
        firstX = Math.ceil(firstX);
      }

      if (rightMostXPixels < 0) {
        lastX = Math.ceil(lastX);
        lastX *= -1;
      } else {
        lastX = Math.floor(lastX);
      }

      var firstY = Math.floor(
        Math.abs(bottomYPixels) / grid.args.pixelsPerBlock
      );

      if (bottomYPixels < 0) {
        firstY *= -1;
      }

      if (grid.hideTails) {
        ctx.save();
        ctx.beginPath();
        ctx.rect(
          -2,
          -2,
          grid.width - grid.viewportSlideX + 2,
          grid.viewportSlideY + 2
        );
        ctx.clip();
      }

      // Draw grid lines
      for (var looper = 0; looper <= units; looper++) {
        var x = firstX + looper;
        var y = firstY + looper;

        // vertical

        ctx.lineWidth = 1;

        if (Math.abs(x) % 5 === 0) {
          ctx.strokeStyle = 'blue';
        } else {
          ctx.strokeStyle = 'gray';
        }

        if (x === 0) {
          ctx.strokeStyle = AXIS_COLOR;
          ctx.lineWidth = AXIS_WIDTH;
        }

        ctx.beginPath();
        ctx.moveTo(x * grid.args.pixelsPerBlock, bottomYPixels);
        ctx.lineTo(x * grid.args.pixelsPerBlock, topYPixels);

        if (!numberLine) {
          ctx.stroke();
        } else if (grid.args.sM) {
          var pix = Math.round(grid.userToPixelV(1) / 2 + 3);

          // I could hardly follow this code enough to figure out where this was expected to come from
          // eslint-disable-next-line no-undef
          line(gr, 0, pix, 0, -pix);
        }
        // horizontal

        ctx.lineWidth = 1;

        if (Math.abs(y) % 5 === 0) {
          ctx.strokeStyle = 'blue';
        } else {
          ctx.strokeStyle = 'gray';
        }

        if (y === 0) {
          ctx.strokeStyle = AXIS_COLOR;
          ctx.lineWidth = AXIS_WIDTH;
        }

        ctx.beginPath();
        ctx.moveTo(leftMostXPixels, y * grid.args.pixelsPerBlock);
        ctx.lineTo(rightMostXPixels, y * grid.args.pixelsPerBlock);

        if (y === 0 || !numberLine) {
          ctx.stroke();
        }

        if (y === 0 && numberLine) {
          ctx.beginPath();

          // Default for backwards compatibility
          if (!grid.args.arrowType) grid.args.arrowType = 'BOTH';

          if (grid.args.arrowType != 'NONE') {
            switch (grid.args.arrowType) {
              case 'BOTH':
                arrow(ctx, rightMostXPixels, 0, leftMostXPixels, 0, 'small');
                arrow(ctx, leftMostXPixels, 0, rightMostXPixels, 0, 'small');
                break;
              case 'LEFT_OR_TOP':
                arrow(ctx, rightMostXPixels, 0, leftMostXPixels, 0, 'small');
                break;
              case 'RIGHT_OR_BOTTOM':
                arrow(ctx, leftMostXPixels, 0, rightMostXPixels, 0, 'small');
                break;
            }
          }

          ctx.stroke();
        }
      }

      if (grid.hideTails) {
        ctx.restore();
      }

      // highlight light blue outer box
      if (grid.selected) {
        ctx.save();
        ctx.strokeStyle = getThemeColor();
        ctx.lineWidth = 5;
        // ctx.rect(leftMostXPixels, 0, rightMostXPixels, bottomYPixels);
        ctx.beginPath();
        ctx.lineTo(leftMostXPixels, bottomYPixels, leftMostXPixels, topYPixels);
        ctx.lineTo(leftMostXPixels, topYPixels, rightMostXPixels, topYPixels);
        ctx.lineTo(
          rightMostXPixels,
          topYPixels,
          rightMostXPixels,
          bottomYPixels
        );
        ctx.lineTo(
          rightMostXPixels,
          bottomYPixels,
          leftMostXPixels,
          bottomYPixels
        );
        ctx.closePath();
        ctx.stroke();
        ctx.restore();
      }

      // numberLine
      var minBlock = firstX;
      var maxBlock = lastX;

      if (grid.args.arrowType) {
        switch (grid.args.arrowType) {
          case 'BOTH':
            minBlock++;
            maxBlock--;
            break;
          case 'LEFT_OR_TOP':
            minBlock++;
            break;
          case 'RIGHT_OR_BOTTOM':
            maxBlock--;
            break;
          case 'NONE':
            break;
        }
      }

      if (grid.args.sI) {
        var h = 6;
        var incrementHeightMultiplier = 1.75; // same multiplier used in ProblemAuthoring
        var block = minBlock;

        while (block <= maxBlock) {
          var x = block * grid.args.pixelsPerBlock;

          // check if this is a number line with a label at this position
          var markHasLabel = false;

          if (grid.args.o.p != null) {
            for (var a = 0; a < grid.args.o.p.length; a++) {
              var obj = grid.args.o.p[a];

              if (obj.xAxisLabel && Math.abs(obj.cx - x) == 0) {
                markHasLabel = true;
                break;
              }
            }
          }

          ctx.beginPath();
          ctx.moveTo(x, -h * (markHasLabel ? incrementHeightMultiplier : 1));
          ctx.lineTo(x, h * (markHasLabel ? incrementHeightMultiplier : 1));
          ctx.lineWidth = 2;
          ctx.strokeStyle = 'black';
          ctx.stroke();
          block++;
        }
      }

      var maxVertScaleStampW = 0;
      var maxHorzHeight = 0;
      var xAxisLabelHasFractions = false;

      // Find the widest axis label
      if (grid.args.o.p != null) {
        for (var a = 0; a < grid.args.o.p.length; a++) {
          var obj = grid.args.o.p[a];

          if (obj.yAxisLabel) {
            var line = toLine({
              problemJS: getProblemJSDependencies(),
              xml: obj.vl,
              font: this.getFontFromObj(obj),
            });

            maxVertScaleStampW = Math.max(maxVertScaleStampW, line.viewportW);
          } else if (obj.xAxisLabel) {
            if (this.isFractionObj(obj)) xAxisLabelHasFractions = true;
            var line = toLine({
              problemJS: getProblemJSDependencies(),
              xml: obj.vl,
              font: this.getFontFromObj(obj),
            });

            maxHorzHeight = Math.max(maxHorzHeight, line.viewportH);
          }
        }
      }

      // Units
      if (!grid.scaleStamp) {
        grid.scaleStamp = getText();
        grid.scaleStamp.setFont('bold 12px ' + lineFont);
      }

      if (grid.args.xU) {
        grid.scaleStamp.text = grid.args.xU;
        grid.scaleStamp.sizeMe();
        ctx.save();
        grid.scaleStamp.fillStyle = grid.selected ? 'black' : 'gray';
        ctx.scale(1, -1);
        // leave room for the status indicator (if grid is first-quadrant)
        var tx =
          rightMostXPixels -
          grid.scaleStamp.width -
          5 -
          (grid.args.eF && grid.st && grid.st !== 'u' ? STATUS_DIAMETER : 0);
        var ty = maxHorzHeight + (xAxisLabelHasFractions ? 5 : 10);

        ctx.translate(tx, ty);
        grid.scaleStamp.paintMe(ctx);
        ctx.restore();
      }

      if (grid.args.yU) {
        grid.scaleStamp.text = grid.args.yU;
        grid.scaleStamp.sizeMe();
        ctx.save();
        grid.scaleStamp.fillStyle = grid.selected ? 'black' : 'gray';
        var tx = Math.max(
          leftMostXPixels,
          -maxVertScaleStampW - grid.scaleStamp.viewportH - 5
        );
        var ty = topYPixels - grid.scaleStamp.viewportW - 3;

        ctx.translate(tx, ty);
        ctx.rotate(Math.PI / 2);
        ctx.scale(1, -1);
        grid.scaleStamp.paintMe(ctx);
        ctx.restore();
      }

      // Objects (lines, dots, ...)

      if (grid.args.o) {
        if (grid.args.o.p) {
          grid.paintObjects(
            ctx,
            grid.args.o.p,
            true,
            leftMostXPixels,
            rightMostXPixels,
            topYPixels,
            bottomYPixels
          );
        }

        if (grid.args.o.u) {
          ctx.save();
          ctx.globalAlpha = 1;
          grid.paintObjects(
            ctx,
            grid.args.o.u,
            false,
            leftMostXPixels,
            rightMostXPixels,
            topYPixels,
            bottomYPixels
          );
          ctx.restore();
        }
      }

      ctx.restore();
    };

    grid.paintPostClip = function (ctx) {
      if (grid.args.eF || grid.args.nl) {
        paintStatus(
          grid,
          ctx,
          -grid.viewportSlideX,
          -grid.viewportSlideY,
          grid.viewportW,
          grid.viewportH,
          0
        );
      } else {
        paintStatus(
          grid,
          ctx,
          -grid.viewportW / 2 + 1,
          -grid.viewportH / 2 + 1,
          grid.viewportW,
          grid.viewportH,
          0
        );
      }
    };

    return grid;
  };

  var shadeHelper = function (region, x, y) {
    // patch
    var len = region.length;

    if (len > 0) {
      var last = region[len - 1];

      if (last.x == x && last.y == y) return;
    }

    region.push({ x: x, y: y });
  };

  var isSlopeNearlyVertical = function (s) {
    // 6 blocks up, 7 pixels over
    return Math.abs(s) >= 120 / 7;
  };

  var isNearlyHorizontal = function (s) {
    // 7 pixels up, 6 blocks over
    return Math.abs(s) <= 7 / 120;
  };

  var modeE = function (i) {
    var r;

    if (i == 'L') r = 'Line';
    if (i == 'Pa') r = 'Parabola';
    if (i == 'Sh') r = 'Shade';
    if (i == 'R') r = 'Ray';
    if (i == 'Se') r = 'Segment';
    if (i == 'Po') r = 'Point';
    if (i == 'H') r = 'Highlight Point';

    return r ? r : i;
  };

  var getGridGetter = function (x, y, ag) {
    var gag = getBasicObject(x, y);

    gag.gmmName = 'gridGetter';
    gag.agId = ag.agId;
    gag.t = 'g';
    gag.fwd = ag.fwd;
    gag.offAxis = ag.offAxis;
    gag.actingAsNumberLineGetter = ag.actingAsNumberLineGetter;

    gag.axisLabels = ag.axisLabels;

    ag.m = modeE(ag.m);

    if (ag.ms) {
      for (var x = 0; x < ag.ms.length; x++) {
        ag.ms[x] = modeE(ag.ms[x]);
      }
    }

    gag.disabled = true;
    gag.enableOnTouch = true;

    gag.enable = function () {
      gag.grabFocus();
    };

    gag.ms = ag.ms;

    if (gag.ms && !ag.m) {
      ag.m = gag.ms[0];
    }

    gag.grabFocus = function () {
      setFocused(undefined);

      removeKeyboard();
      currentAttemptGetter = gag;

      if (lettersButton != null) {
        lettersButton.staysAnimatedColor = true;
      }

      gag.g.setSelected(true);
    };

    gag.loseFocus = function () {
      gag.g.setSelected(false);

      if (gridDragTransientState) {
        gridDragTransientState.clear({ clearActiveDrag: true });
      }
    };

    if (ag.m) {
      var top = getBasicObject();

      var modeShower = getButton();
      var modeSizerText = getStandardGridModeSelectorSizerText() || ag.m || '';
      var sizer = getText(0, 0, modeSizerText);

      modeShower.viewportW = sizer.viewportW + 30;
      modeShower.viewportH = sizer.viewportH * 1.4;
      modeShower.stamp = getText();
      modeShower.stamp.fillStyle = 'lightgray';
      modeShower.stamp.xtraForLowHang = 5;
      modeShower.add(modeShower.stamp);

      gag.mShower = modeShower;

      var justOne = !(gag.ms && gag.ms.length > 1);
      var initialGridMode = gag.m || ag.m;

      var modeChanger;
      var isGridCreationSelectorClickable = function () {
        return gag.g && gag.g.selected && gag.st !== 'c';
      };

      var isGridCreationSelectorActive = function () {
        return (
          isGridCreationSelectorClickable() &&
          !(gag.isGridMoveModeActive && gag.isGridMoveModeActive())
        );
      };

      var getGridCreationSelectorIconColor = function () {
        if (isGridCreationSelectorActive()) {
          return ACTIVE_GRID_CREATION_MODE_ICON_COLOR;
        }

        if (isGridCreationSelectorClickable()) {
          return 'gray';
        }

        return gag.g && gag.g.selected ? '#a8a8a8' : 'lightgray';
      };

      modeShower.paintPre = function (ctx) {
        var selectorClickable = isGridCreationSelectorClickable();
        var selectorActive = isGridCreationSelectorActive();

        modeShower.stamp.fillStyle = getGridCreationSelectorIconColor();

        if (justOne && selectorClickable) {
          roundRect2(
            ctx,
            0,
            0,
            modeShower.viewportW,
            modeShower.viewportH,
            6,
            selectorActive ? ACTIVE_GRID_CREATION_MODE_COLOR : 'lightgray',
            undefined,
            undefined,
            true
          );
        }
      };

      if (!justOne) {
        for (var a = gag.ms.length; a > -1; a--) {
          var mo = gag.ms[a];

          if (!isSupportedGridMode(mo)) {
            gag.ms.splice(a, 1);
          }
        }

        var listener = function (sel) {
          gag.setMode(sel);
        };

        modeChanger = getButton();
        modeChanger.setAllDim(30, 30);

        modeChanger.paintMe = function (ctx) {
          if (gag.g.selected && gag.st !== 'c') {
            ctx.save();

            var w = 30;
            var h = 30;

            ctx.beginPath();
            ctx.moveTo(0.3 * w, 0.35 * h);
            ctx.lineTo(0.7 * w, 0.35 * h);
            ctx.lineTo(w / 2, 0.7 * h);
            ctx.closePath();
            ctx.fillStyle = getGridCreationSelectorIconColor();
            ctx.fill();

            ctx.restore();
          }
        };

        gag.modeChanger = modeChanger;

        var mdr = function () {
          if (gag.st === 'c') {
            return true;
          }

          if (!gag.g.selected) {
            gag.grabFocus();

            return true;
          }

          if (gag.isGridMoveModeActive()) {
            gag.setGridAddMoveMode(GRID_ADD_MODE);
          }

          var options = [];

          for (var zz = 0; zz < gag.ms.length; zz++) {
            if (gag.m !== gag.ms[zz]) options.push(gag.ms[zz]);
          }

          if (options.length === 0) return false;

          floater.setPanel(
            getWordMenu(
              getProblemJSDependencies(),
              options,
              listener,
              undefined,
              undefined,
              undefined,
              false,
              false,
              undefined,
              $canvas
            )
          );

          var tl = getFullTopLeftLocation(gag.mShower);
          var selectorLocation = getFullTopLeftLocation(modeBoss);

          floater.setLocation(tl.x, selectorLocation.y + modeBoss.viewportH);

          modeBoss.animate(paintCanvas);

          return true;
        };

        modeChanger.mouseDownResponse = mdr;

        modeChanger.mouseMoveResponse = function () {
          $canvas.css(
            'cursor',
            getGridControlCursor({
              enabled: isGridCreationSelectorClickable(),
              moveModeActive: false,
            })
          );
        };

        modeShower.mouseDownResponse = mdr;
        modeShower.mouseMoveResponse = modeChanger.mouseMoveResponse;
        top.mouseDownResponse = mdr;
      }

      modeShower.viewportY += 4;

      var dragModeButton;

      if (!justOne || isDraggableGridGetterMode(initialGridMode)) {
        dragModeButton = addGridDragModeButtonToRow({
          $canvas,
          row: top,
          getter: gag,
          getThemeColor,
          hasMovableGridContent: function () {
            return hasDraggableGridObjects(gag.g.args.o && gag.g.args.o.u);
          },
          onIncompleteShapeDragAttempt: function (shapeType) {
            var shapeName = shapeType.toLowerCase();

            getProblemContext().addStackableDialog({
              msg:
                'Complete or undo the current ' +
                shapeName +
                ' before dragging.',
              top: 'Incomplete ' + shapeName + '?',
            });
          },
          width: getButtonW(),
          height: getButtonW(),
        });
      }

      if (justOne) {
        top.add(
          getLockedGridModeSelector({
            $canvas: $canvas,
            mode: initialGridMode,
            label: modeShower,
            getter: gag,
            showDialog: function (dialog) {
              getProblemContext().addStackableDialog(dialog);
            },
          })
        );
      } else {
        var modeBoss = getButton();

        modeBoss.add(modeChanger);
        modeBoss.add(modeShower);
        modeBoss.layoutChildrenLR(8);
        modeBoss.sizeMeToFitChildren();

        modeBoss.paintPre = function (ctx) {
          var selectorClickable = isGridCreationSelectorClickable();
          var selectorActive = isGridCreationSelectorActive();

          modeShower.stamp.fillStyle = getGridCreationSelectorIconColor();

          if (gag.g.selected && gag.st !== 'c')
            roundRect2(
              ctx,
              0,
              0,
              modeBoss.viewportW,
              modeBoss.viewportH,
              6,
              modeBoss.animated
                ? getThemeColor()
                : selectorActive
                ? ACTIVE_GRID_CREATION_MODE_COLOR
                : selectorClickable
                ? 'lightgray'
                : '#eeeeee',
              undefined,
              undefined,
              true
            );
        };

        modeBoss.mouseMoveResponse = modeChanger.mouseMoveResponse;
        top.add(modeBoss);
      }

      var lineMode = getButton();

      lineMode.states = ['solid', 'dotted'];
      lineMode.i = 0;
      lineMode.visible = isGridLineStyleMode(initialGridMode);

      var isGridLineStyleEnabled = function () {
        return canUseGridAddControls({
          selected: gag.g.selected,
          status: gag.st,
          moveModeActive: gag.isGridMoveModeActive(),
          visible: lineMode.visible,
        });
      };

      var mouseDown = function () {
        if (!lineMode.visible) {
          return false;
        }

        if (!isGridLineStyleEnabled()) {
          return true;
        }

        var interactionStartState = gag.cloneGridStateForHistory
          ? gag.cloneGridStateForHistory()
          : undefined;
        var changedCommittedObject = false;

        lineMode.i++;

        if (lineMode.i == lineMode.states.length) {
          lineMode.i = 0;
        }

        var img = lineMode.states[lineMode.i];

        lineMode.mode = img;

        if (gag.g.currentObj && gag.g.currentObj.second) {
          gag.g.currentObj.dotted = img === 'dotted' ? true : false;
          changedCommittedObject = true;
        } else if (!gag.g.currentObj) {
          if (gag.g.args.o && gag.g.args.o.u) {
            var us = gag.g.args.o.u;

            if (us.length > 0) {
              var ls = us[us.length - 1];

              if (ls.second) {
                ls.dotted = img === 'dotted' ? true : false;
                changedCommittedObject = true;
              }
            }
          }
        }

        lineMode.animated = true;

        if (changedCommittedObject) {
          changedMaybe();
          gag.recordGridInteraction(interactionStartState);
        }

        return true;
      };

      var lineStyleButtonSize = getButtonW();

      lineMode.setAllDim(lineStyleButtonSize, lineStyleButtonSize);

      lineMode.paintMe = function (ctx) {
        var sel = gag.g.selected;

        if (sel && gag.st !== 'c' && lineMode.visible) {
          paintGridLineStyleButton({
            ctx,
            width: lineMode.viewportW,
            height: lineMode.viewportH,
            enabled: isGridLineStyleEnabled(),
            animated: lineMode.animated,
            mode: lineMode.i === 0 ? 'solid' : 'dotted',
            themeColor: getThemeColor(),
          });
        }
      };

      lineMode.mouseDownResponse = mouseDown;

      lineMode.mouseMoveResponse = function () {
        $canvas.css(
          'cursor',
          getGridControlCursor({
            enabled: isGridLineStyleEnabled(),
            moveModeActive: gag.isGridMoveModeActive(),
          })
        );
      };

      if (!justOne || lineMode.visible) {
        top.add(lineMode);
      }

      top.layoutChildrenLR(15);
      top.sizeMeToFitChildren();
      top.viewportH += 5;

      if (
        shouldShowGridModeSelectorRow({
          fixed: justOne,
          hasDragButton: Boolean(dragModeButton),
          actingAsNumberLineGetter: Boolean(gag.actingAsNumberLineGetter),
        })
      )
        gag.add(top);
      else gag.justOneM = true;
      gag.lineMode = lineMode;

      gag.top = top;
    }

    var updateGridModeSelector = function (chosen) {
      gag.m = chosen;
      gag.mShower.stamp.text = chosen;
      gag.mShower.stamp.sizeMe();
      gag.mShower.centerVertically();
      gag.mShower.stamp.viewportY += 2;

      gag.lineMode.visible = isGridLineStyleMode(chosen);
    };

    gag.setMode = function (chosen) {
      if (gag.m === chosen) {
        return;
      }

      if (isDraggableGridGetterMode(chosen)) {
        gag.m = chosen;

        if (
          !gag.cancelIncompleteGridInteraction ||
          !gag.cancelIncompleteGridInteraction()
        ) {
          gag.g.clearCurrentMaybeSave();
        }

        if (gag.updateUndoRedoButtons) {
          gag.updateUndoRedoButtons();
        }
      }

      updateGridModeSelector(chosen);
    };

    gag.clearShade = function () {
      var was = gag.g.shadePoint;

      gag.g.shadePoint = undefined;
      gag.g.args.shadePoint = undefined;
      clearGridShade({
        userObjects: gag.g.args.o && gag.g.args.o.u,
        permanentObjects: gag.g.args.o && gag.g.args.o.p,
        hadShadePoint: Boolean(was),
      });
    };

    gag.clearAllDots = function () {
      var objs = grid.args.o.u;

      for (var a = objs.length - 1; a >= 0; a--) {
        if (objs[a].t === 'dot') {
          objs.splice(a, 1);
        }
      }
    };

    gag.addHighlightDot = function (x, y) {
      var objs = grid.args.o.u;
      var point = {};

      point.t = 'dot';
      point.cx = x;
      point.cy = y;
      point.r = 7;
      point.color = '#B93B8F';
      point.high = true;
      objs.push(point);
    };

    gag.maybeUndoSelectedLineEntryButton = function () {
      if (!gag.selectedButton) return false;
      gag.selectedButton = undefined;
      gag.deselectAllButtons();
      submitAttempt({
        blank: true,
        eventSource: 'gag.maybeUndoSelectedLineEntryButton',
      });

      return true;
    };

    var grid = getGrid(ag.g);
    var gridDragTransientState = createGridDragTransientState({
      grid,
      paintCanvas,
      isActiveDragOwned: function () {
        return Boolean(activeGridDrag && activeGridDrag.gag === gag);
      },
      resetActiveDrag: function () {
        activeGridDrag = undefined;
      },
    });

    gag.e = true;
    configureGridAddMoveMode({
      getter: gag,
      getGridGuide: function () {
        return gridGuide;
      },
      onAddModeActivated: gridDragTransientState.clear,
      paintCanvas,
    });

    var gridHistory = createGridGetterHistory({
      grid,
      getLineMode: function () {
        return gag.lineMode;
      },
      getGridGuide: function () {
        return gridGuide;
      },
      paintCanvas,
      changedMaybe,
      beforeHistoryChange: function () {
        gridDragTransientState.clear({
          clearActiveDrag: true,
          repaint: false,
        });
      },
      restoreIncompleteShapeMode: function (mode) {
        gag.setGridAddMoveMode(GRID_ADD_MODE);
        updateGridModeSelector(mode);
      },
      rehydrateHistoryObject: function (obj) {
        if (obj && obj.t === 'Label' && obj.line === undefined) {
          try {
            obj.line = toLine({
              problemJS: getProblemJSDependencies(),
              xml: obj.xml,
            });
          } catch (_error) {
            obj.line = {
              viewportW: 0,
              viewportH: 0,
              paint: function () {},
            };
          }
        }
      },
    });

    gag.undoStack = gridHistory.undoStack;
    gag.redoStack = gridHistory.redoStack;
    gag.maxHistoryEntries = gridHistory.maxHistoryEntries;
    gag.cloneGridStateForHistory = gridHistory.cloneGridStateForHistory;
    gag.gridHistoryStatesEqual = gridHistory.gridHistoryStatesEqual;
    gag.applyGridStateFromHistory = gridHistory.applyGridStateFromHistory;
    gag.updateUndoRedoButtons = gridHistory.updateUndoRedoButtons;
    gag.clearIncompleteCurrentShape = gridHistory.clearIncompleteCurrentShape;
    gag.cancelIncompleteGridInteraction =
      gridHistory.cancelIncompleteGridInteraction;
    gag.recordGridInteraction = gridHistory.recordGridInteraction;
    gag.recordIncompleteGridInteraction =
      gridHistory.recordIncompleteGridInteraction;
    gag.recordCoalescedGridInteraction =
      gridHistory.recordCoalescedGridInteraction;
    gag.undoGridInteraction = gridHistory.undoGridInteraction;
    gag.redoGridInteraction = gridHistory.redoGridInteraction;

    grid.setShadePoint = function (pt, options) {
      return setGridShadePoint({
        grid,
        shadePoint: pt,
        fillWithDots: Boolean(gag.fwd),
        preserveExistingShade: Boolean(
          options && options.preserveExistingShade
        ),
        validateShadeGeometry:
          !options || options.validateShadeGeometry !== false,
        getBounds: function () {
          return gag.getGridDragBounds();
        },
        clearHighlightDots: function () {
          gag.clearAllDots();
        },
        addHighlightDot: function (x, y) {
          gag.addHighlightDot(x, y);
        },
        reportError: function (error) {
          getProblemContext().addStackableDialog(error);
        },
      });
    };

    gag.getGridRawPointFromLocal = function (x, y) {
      return getGridRawPointFromLocal({
        x,
        y,
        actingAsNumberLineGetter: gag.actingAsNumberLineGetter,
      });
    };

    gag.getGridLocalPointFromCanvas = function (canvasX, canvasY) {
      var gridLocation = getFullTopLeftLocation(grid);

      return {
        x: canvasX - gridLocation.x - grid.viewportSlideX - grid.viewportMargin,
        y: canvasY - gridLocation.y - grid.viewportSlideY - grid.viewportMargin,
      };
    };

    gag.getGridDragBounds = function () {
      return getGridDragBounds({
        firstQuadrant: Boolean(grid.args.eF),
        viewportW: grid.viewportW,
        viewportSlideX: grid.viewportSlideX,
      });
    };

    gag.findGridDragTargetAtLocalPoint = function (x, y, evt) {
      if (!gag.canUseGridMoveMode || !gag.canUseGridMoveMode()) {
        return undefined;
      }

      var rawPoint = gag.getGridRawPointFromLocal(x, y);

      return findGridDragTarget(
        grid.args.o && grid.args.o.u,
        rawPoint.cx,
        rawPoint.cy,
        {
          allowObjectDrag: true,
          inputEvent: evt,
          allowTouchInput: gag.isGridMoveModeActive(),
          preferredObject: grid.preferredGridDragObject,
        }
      );
    };

    gag.refreshGridShadeAfterDrag = function (validateShadeGeometry) {
      if (grid.args && grid.args.shadePoint) {
        return grid.setShadePoint(grid.args.shadePoint, {
          preserveExistingShade: true,
          validateShadeGeometry: validateShadeGeometry !== false,
        });
      }
    };

    gag.applyDraggedPoint = function (drag, localX, localY) {
      if (!drag || drag.target.kind !== 'point') {
        return false;
      }

      var targetPoint = drag.target.point;
      var otherPointKey = drag.target.pointKey === 'first' ? 'second' : 'first';
      var anchorPoint = drag.target.obj[otherPointKey];
      var nextPoint = resolveDraggedPoint({
        startMouse: drag.startMouse,
        rawMouse: gag.getGridRawPointFromLocal(localX, localY),
        startPoint: drag.startPoint,
        previousPoint: { cx: targetPoint.cx, cy: targetPoint.cy },
        bounds: gag.getGridDragBounds(),
        snapIncrement: grid.args.stg ? grid.args.pixelsPerBlock : undefined,
        axisSnapThreshold:
          grid.args.sta && !gag.actingAsNumberLineGetter
            ? grid.args.pixelsPerBlock / 2
            : undefined,
        anchorPoint,
        parabolaThreshold: GRID_GUIDE_PARABOLA_VERTEX_THRESHOLD,
        shapeType: drag.target.obj.t,
      });

      if (nextPoint.cx === targetPoint.cx && nextPoint.cy === targetPoint.cy) {
        return false;
      }

      targetPoint.cx = nextPoint.cx;
      targetPoint.cy = nextPoint.cy;
      drag.moved = true;
      gag.refreshGridShadeAfterDrag(false);
      changedMaybe();
      paintCanvas();

      return true;
    };

    gag.applyDraggedObject = function (drag, localX, localY) {
      if (
        !drag ||
        drag.target.kind !== 'object' ||
        !drag.startFirst ||
        !drag.startSecond
      ) {
        return false;
      }

      var nextShape = resolveGridDraggedShape({
        startMouse: drag.startMouse,
        rawMouse: gag.getGridRawPointFromLocal(localX, localY),
        startFirst: drag.startFirst,
        startSecond: drag.startSecond,
        bounds: gag.getGridDragBounds(),
        shape: drag.target.obj,
        grid,
        getter: gag,
      });

      if (
        nextShape.first.cx === drag.target.obj.first.cx &&
        nextShape.first.cy === drag.target.obj.first.cy &&
        nextShape.second.cx === drag.target.obj.second.cx &&
        nextShape.second.cy === drag.target.obj.second.cy
      ) {
        return false;
      }

      drag.target.obj.first.cx = nextShape.first.cx;
      drag.target.obj.first.cy = nextShape.first.cy;
      drag.target.obj.second.cx = nextShape.second.cx;
      drag.target.obj.second.cy = nextShape.second.cy;
      drag.moved =
        drag.moved || nextShape.deltaX !== 0 || nextShape.deltaY !== 0;
      gag.refreshGridShadeAfterDrag(false);
      changedMaybe();
      paintCanvas();

      return true;
    };

    gag.finishGridDrag = function (drag, canvasX, canvasY) {
      if (!drag) {
        return;
      }

      if (canvasX != null && canvasY != null) {
        var localPoint = gag.getGridLocalPointFromCanvas(canvasX, canvasY);

        if (drag.target.kind === 'point') {
          gag.applyDraggedPoint(drag, localPoint.x, localPoint.y);
        } else {
          gag.applyDraggedObject(drag, localPoint.x, localPoint.y);
        }
      }

      if (rollbackInvalidShadedGridDrag(drag, gag)) {
        changedMaybe();
      }

      if (drag.moved) {
        if (
          removeDuplicateGridPointAtTarget(
            grid.args.o && grid.args.o.u,
            drag.target
          )
        ) {
          gag.refreshGridShadeAfterDrag(false);
          changedMaybe();
          paintCanvas();
        }

        gag.recordGridInteraction(drag.startState);
      }

      activeGridDrag = undefined;
      gag.updateUndoRedoButtons();

      if ($canvas) {
        $canvas.css('cursor', 'default');
      }
    };

    gag.startGridDrag = function (target, localX, localY) {
      if (!target) {
        return false;
      }

      gridDragTransientState.setPreferredObject(target.obj);
      gridDragTransientState.setHoveredObject(undefined);
      grid.currentObj =
        target.obj && target.obj.second ? target.obj : undefined;

      if (gridGuide) {
        gridGuide.close();
        gridGuide.dot = undefined;
        gridGuide.currentObj = undefined;
      }

      activeGridDrag = {
        gag,
        target,
        startState: gag.cloneGridStateForHistory(),
        moved: false,
        startMouse: gag.getGridRawPointFromLocal(localX, localY),
        startPoint:
          target.kind === 'point'
            ? { cx: target.point.cx, cy: target.point.cy }
            : undefined,
        startFirst: target.obj.first
          ? { cx: target.obj.first.cx, cy: target.obj.first.cy }
          : undefined,
        startSecond: target.obj.second
          ? { cx: target.obj.second.cx, cy: target.obj.second.cy }
          : undefined,
        updateFromCanvasPoint: function (canvasX, canvasY) {
          var localPoint = gag.getGridLocalPointFromCanvas(canvasX, canvasY);

          if (target.kind === 'point') {
            gag.applyDraggedPoint(activeGridDrag, localPoint.x, localPoint.y);
          } else {
            gag.applyDraggedObject(activeGridDrag, localPoint.x, localPoint.y);
          }
        },
        finish: function (canvasX, canvasY) {
          gag.finishGridDrag(activeGridDrag, canvasX, canvasY);
        },
      };

      gag.updateUndoRedoButtons();

      return true;
    };

    gag.startGridDragAtCanvasPoint = function (canvasX, canvasY, evt) {
      if (!gag.isGridMoveModeActive() || !grid.isEnabled()) {
        return false;
      }

      var localPoint = gag.getGridLocalPointFromCanvas(canvasX, canvasY);
      var rawPoint = gag.getGridRawPointFromLocal(localPoint.x, localPoint.y);
      var dragBounds = gag.getGridDragBounds();

      if (!isGridRawPointWithinDragBounds(rawPoint, dragBounds)) {
        return false;
      }

      gag.grabFocus();

      var dragTarget = gag.findGridDragTargetAtLocalPoint(
        localPoint.x,
        localPoint.y,
        evt
      );

      if (!dragTarget) {
        gridDragTransientState.clear();

        return true;
      }

      return gag.startGridDrag(dragTarget, localPoint.x, localPoint.y);
    };

    grid.mouseMove = function (parentX, parentY, results) {
      if (!grid.isEnabled()) {
        gridDragTransientState.clear();

        return;
      }

      if (!gag.canUseGridMoveMode()) {
        gridDragTransientState.clear();

        return;
      }

      var localX =
        parentX - grid.viewportX - grid.viewportSlideX - grid.viewportMargin;
      var localY =
        parentY - grid.viewportY - grid.viewportSlideY - grid.viewportMargin;

      if (activeGridDrag && activeGridDrag.gag === gag) {
        gridDragTransientState.setHoveredObject(undefined);
        results.kill = true;
        results.mousePointerChangeTo = 'grabbing';

        return;
      }

      var dragTarget = gag.findGridDragTargetAtLocalPoint(
        localX,
        localY,
        currentCanvasMoveEvent
      );

      if (dragTarget) {
        if (dragTarget.kind === 'object') {
          gridDragTransientState.setHoveredObject(dragTarget.obj);
        } else {
          gridDragTransientState.setHoveredPoint(dragTarget.point);
        }

        results.kill = true;
        results.mousePointerChangeTo = 'grab';
      } else {
        gridDragTransientState.clear();
      }
    };

    grid.mouseDownResponse = function (x, y, evt) {
      if (!gag.e) {
        return false;
      }

      if (gag.st === 'c') {
        return false;
      }

      changedMaybe();

      var saveX;
      var saveY;

      var wasF = currentAttemptGetter == gag;

      gag.grabFocus();
      if (!wasF) return true;

      gag.maybeUndoSelectedLineEntryButton();

      if (gag.isGridMoveModeActive()) {
        var moveModeDragTarget = gag.findGridDragTargetAtLocalPoint(x, y, evt);

        if (moveModeDragTarget) {
          return gag.startGridDrag(moveModeDragTarget, x, y);
        }

        gridDragTransientState.clear();

        return true;
      }

      y = -y;

      x = Math.round(x * 100) / 100;
      y = Math.round(y * 100) / 100;

      if (gag.actingAsNumberLineGetter) y = 0;

      var dotForGridGuide;
      var currentObjForGridGuide;
      var killGridGuide = false;

      if (!grid.args.o) {
        grid.args.o = {};
      }

      if (!grid.args.o.u) {
        grid.args.o.u = [];
      }

      if (grid.args.stg) {
        saveX = x;
        saveY = y;
        var px = Math.abs(x);
        var xtra = px % grid.args.pixelsPerBlock;

        if (x > 0) {
          x =
            xtra > grid.args.pixelsPerBlock / 2
              ? x + (grid.args.pixelsPerBlock - xtra)
              : x - xtra;
        } else if (x < 0) {
          x =
            xtra > grid.args.pixelsPerBlock / 2
              ? x - (grid.args.pixelsPerBlock - xtra)
              : x + xtra;
        }

        var py = Math.abs(y);

        xtra = py % grid.args.pixelsPerBlock;

        if (y > 0) {
          y =
            xtra > grid.args.pixelsPerBlock / 2
              ? y + (grid.args.pixelsPerBlock - xtra)
              : y - xtra;
        } else if (y < 0) {
          y =
            xtra > grid.args.pixelsPerBlock / 2
              ? y - (grid.args.pixelsPerBlock - xtra)
              : y + xtra;
        }

        if (grid.args.eF) {
          var d = grid.viewportW - grid.viewportSlideX;

          if (x < 0 || y < 0 || x > d || y > d) {
            return false;
          }
        } else {
          var d = grid.viewportW / 2;

          if (x > d || y < -d || x > d || y < -d) {
            return false;
          }
        }
      }

      if (grid.args.sta && gag.m !== 'Shade') {
        if (Math.abs(x) <= grid.args.pixelsPerBlock / 2) {
          x = 0;
        }

        if (Math.abs(y) <= grid.args.pixelsPerBlock / 2) {
          y = 0;
        }
      }

      if (grid.args.eF && (x < 0 || y < 0)) return false;

      var objs = grid.args.o.u;
      var interactionStartState = gag.cloneGridStateForHistory(
        Boolean(grid.currentObj && !grid.currentObj.second)
      );

      if (gag.m === 'Point' || gag.m === 'Highlight Point') {
        var point = {};

        point.t = 'dot';

        if (gag.m === 'Highlight Point') {
          point.cx = x;
          point.cy = y;
          point.r = 7;
          point.color = '#B93B8F';
          point.high = true;
        } else {
          point.cx = x;
          point.cy = y;
          point.r = 5;
          point.color = DEFAULT_GRAPH_OBJECT_COLOR;
        }

        var pointPlacement = applyGridPointPlacement({
          actingAsNumberLineGetter: gag.actingAsNumberLineGetter,
          inactivePointColor: 'red',
          multiInput: gag.multiInput,
          objects: objs,
          point: point,
        });
        var remove = pointPlacement.action === 'removed';

        currentObjForGridGuide = undefined;

        if (!remove) {
          if (!grid.args.stg) {
            dotForGridGuide = point;
          }
        } else {
          dotForGridGuide = undefined;

          if (gridGuide) {
            gridGuide.close();
          }
        }
      } else if (gag.m === 'Shade') {
        var useX = saveX ? saveX : x;
        var useY = saveY ? saveY : y;

        grid.setShadePoint({ x: useX, y: useY });
      } else {
        var point = {};

        point.t = 'dot';
        point.cx = x;
        point.cy = y;
        point.r = 5;
        point.color = DEFAULT_GRAPH_OBJECT_COLOR;

        if (grid.currentObj && grid.currentObj.second) {
          grid.currentObj = undefined;
        }

        if (!grid.currentObj) {
          grid.currentObj = { t: gag.m, color: DEFAULT_GRAPH_OBJECT_COLOR };
          grid.currentObj.first = point;

          grid.addLiney(grid.currentObj);

          if (gag.m === 'Parabola') {
            grid.currentObj.userParabola = true;
          }
        } else {
          if (
            gag.m === 'Ray' &&
            grid.args.eF &&
            isAxial(grid.currentObj.first, true) &&
            !isAxial(grid.currentObj.first, false, true)
          ) {
            point.cx = grid.currentObj.first.cx;
          } else if (gag.m === 'Line' || gag.m === 'Segment') {
            var p1 = grid.currentObj.first;
            var p2 = point;
            var x1 = p1.cx;
            var y1 = p1.cy;
            var x2 = p2.cx;
            var y2 = p2.cy;

            if (x1 !== x2 && y1 !== y2) {
              var sl = (y2 - y1) / (x2 - x1);

              if (isSlopeNearlyVertical(sl)) {
                p2.cx = p1.cx;
                killGridGuide = true;
              } else if (isNearlyHorizontal(sl)) {
                p2.cy = p1.cy;
                killGridGuide = true;
                if (gridGuide) gridGuide.close();
              }
            }
          } else if (gag.m === 'Parabola' && grid.currentObj.first) {
            var diffVariance = GRID_GUIDE_PARABOLA_VERTEX_THRESHOLD;
            var cxDiff = Math.abs(grid.currentObj.first.cx - point.cx);
            var cyDiff = Math.abs(grid.currentObj.first.cy - point.cy);

            if (
              Math.round(cxDiff, 0) <= diffVariance ||
              Math.round(cyDiff, 0) <= diffVariance
            ) {
              getProblemContext().addStackableDialog({
                msg:
                  'Your vertex cannot have the same x- or y-coordinate as any other point.',
                top: 'Invalid Parabola',
              });

              return true;
            }
          }

          grid.currentObj.second = point;

          if (gag.lineMode.mode === 'dotted') {
            grid.currentObj.dotted = true;
          }
        }

        // Make sure the number of graphed objects does not exceed our allowed maximums
        if (grid.args.o.u && grid.isAboveItemLimit(grid.args.o.u)) {
          // Clear the objects
          grid.currentObj = undefined;
          point = undefined;
        }

        dotForGridGuide = point;
        currentObjForGridGuide = grid.currentObj;
      }

      if (dotForGridGuide) {
        setGridGuideToDot(dotForGridGuide, gag.g);
      }

      if (currentObjForGridGuide) {
        gridGuide.currentObj = currentObjForGridGuide;
      }

      if (killGridGuide) gridGuide.close();

      if (
        !(
          gag.m === 'Line' ||
          gag.m === 'Ray' ||
          gag.m === 'Segment' ||
          gag.m === 'Parabola'
        ) ||
        !grid.currentObj ||
        grid.currentObj.second
      ) {
        gag.recordGridInteraction(interactionStartState);
      } else if (gag.recordIncompleteGridInteraction) {
        gag.recordIncompleteGridInteraction(interactionStartState);
      }

      gag.updateUndoRedoButtons();

      return true;
    };

    gag.setStatus = function (st) {
      ag.s = st;
      ag.st = st;
      gag.g.st = st;
      gag.st = st;
      gag.e = st !== 'c';

      gag.updateUndoRedoButtons();
    };

    gag.add(grid);
    gag.g = grid;
    grid.getter = gag;

    // for now, ignore clear on bottom
    var submitRow = getBasicObject();

    gag.clearF = function (btn, shouldTrackHistory) {
      var trackHistory = shouldTrackHistory !== false;
      var interactionStartState = trackHistory
        ? gag.cloneGridStateForHistory()
        : undefined;

      changedMaybe();
      gag.g.args.o.u = [];
      gag.setGridAddMoveMode(GRID_ADD_MODE);
      gridDragTransientState.clear({
        clearActiveDrag: true,
        repaint: false,
      });

      if (gridGuide) {
        gridGuide.close();
        gridGuide.dot = undefined;
        gridGuide.currentObj = undefined;
      }

      gag.g.currentObj = undefined;
      gag.clearShade();

      if (btn) ag.selectedButton = btn;
      else gag.deselectAllButtons();

      if (trackHistory) {
        gag.recordGridInteraction(interactionStartState);
      }

      gag.updateUndoRedoButtons();

      return true;
    };

    var clear = getButton();

    clear.setAllDim(getButtonW(), getButtonW());

    clear.paintMe = function (ctx) {
      ctx.save();

      if ((gag.g.selected && gag.st !== 'c') || clear.animated) {
        roundRect2(
          ctx,
          0,
          0,
          getButtonW(),
          getButtonW(),
          8,
          clear.animated ? getThemeColor() : 'lightgray',
          undefined,
          undefined,
          true
        );
      }

      var w = clear.viewportW;
      var h = clear.viewportH;
      var c =
        !gag.g.selected || gag.st === 'c'
          ? 'trashlight'
          : clear.animated
          ? 'trashwhite'
          : 'trash';
      var i = getImage(c, paintCanvas);

      drawImage(ctx, i, 0.2 * w, 0.15 * h, 0.6 * w, 0.7 * h);

      ctx.restore();
    };

    clear.mouseDownResponse = function () {
      if (gag.st === 'c') {
        return false;
      }

      gag.clearF();

      // true --> consume event, no mouse reaction by parents!
      return true;
    };

    submitRow.add(clear);

    if (!gag.actingAsNumberLineGetter) {
      // Undo/redo is only available for two-dimensional graphing grids.
      // Number-line-backed grids keep the simpler clear/submit action row.
      var addGridHistoryButton = function (direction, canUseHistory, onClick) {
        var button = getGridUndoRedoButton({
          $canvas,
          direction,
          dimension: getButtonW(),
          getThemeColor,
          isSelected: function () {
            return gag.g.selected;
          },
          isCorrect: function () {
            return gag.st === 'c';
          },
          canUseHistory,
          onGrabFocus: function () {
            gag.grabFocus();
          },
          onClick,
        });

        submitRow.add(button);

        return button;
      };

      var undo = addGridHistoryButton(
        'left',
        function () {
          return gridHistory.canUndo();
        },
        function () {
          return gag.undoGridInteraction();
        }
      );

      gridHistory.setUndoButton(undo);
      gag.undoButton = undo;

      var redo = addGridHistoryButton(
        'right',
        function () {
          return gridHistory.canRedo();
        },
        function () {
          return gag.redoGridInteraction();
        }
      );

      gridHistory.setRedoButton(redo);
      gag.redoButton = redo;
    }

    if (ag.allowSelect) {
      var gearIcon = getButton();
      var clickGearIcon = function () {
        if (gag.st === 'c') {
          return false;
        }

        axesModalState().setXScale(gag.g.args.uPH);
        axesModalState().setYScale(gag.g.args.uPV);
        axesModalState().setXLabel(gag.g.args.xU || '');
        axesModalState().setYLabel(gag.g.args.yU || '');
        axesModalState().setShowLabelPickers(!ag.hlcb);
        axesModalState().setLabelChoices(gag.axisLabels);
        setAxesModalGridGetter(gag);
        axesModalState().setVisible(true);

        return true;
      };

      gearIcon.mouseDownResponse = clickGearIcon;
      var img = 'gear';
      var image = getImage(img, paintCanvas);

      gearIcon.image = image;
      gearIcon.pale = getImage('gear2', paintCanvas);
      let w = 40;
      var h = 40;

      gearIcon.setAllDim(w, h);

      gearIcon.paintMe = function (ctx) {
        ctx.save();
        var w = 40;
        var h = 40;
        var aW = gearIcon.viewportW - gearIcon.viewportMargin * 2;
        var aH = gearIcon.viewportH - gearIcon.viewportMargin * 2;

        if ((gag.g.selected || gearIcon.animated) && gag.st != 'c') {
          roundRect2(
            ctx,
            0,
            0,
            w,
            h,
            6,
            gearIcon.animated ? getThemeColor() : 'lightgray',
            undefined,
            undefined,
            true
          );
        }

        drawImage(
          ctx,
          !gag.g.selected || gag.st === 'c' ? gearIcon.pale : gearIcon.image,
          aW / 2 - gearIcon.image.width / 2,
          aH / 2 - gearIcon.image.height / 2
        );

        ctx.restore();
      };

      submitRow.add(gearIcon);
    }

    gag.updateFromGridDialog = () => {
      var grid = gag.g;
      var args = grid.args;

      var xScalePrev = args.uPH;
      var yScalePrev = args.uPV;

      var xScale = axesModalState().xScale;
      var yScale = axesModalState().yScale;

      args.uPH = xScale;
      args.uPV = yScale;

      var xLabel = axesModalState().xLabel;

      if (xLabel !== '') args.xU = xLabel;

      var yLabel = axesModalState().yLabel;

      if (yLabel !== '') args.yU = yLabel;

      changedMaybe();

      // Check for rescaling
      if (xScale != xScalePrev || yScale != yScalePrev) {
        for (var a = 0; a < args.o.p.length; a++) {
          var obj = args.o.p[a];

          var value = obj.vl.replace('<t>', '').replace('</t>', '');
          var first = value.charAt(0);
          var firstIsNan = isNaN(first);

          if (firstIsNan) value = value.substring(1);

          if (obj.xAxisLabel && xScale != xScalePrev) {
            // rescale value
            value /= xScalePrev;
            value *= xScale;
          }

          if (obj.yAxisLabel && yScale != yScalePrev) {
            // rescale value
            value /= yScalePrev;
            value *= yScale;
          }

          obj.vl = '<t>' + (firstIsNan ? first : '') + value + '</t>';
        }
      }

      paintCanvas();
    };

    var submitButton = getSubmitButton(
      gag,
      paintCanvas,
      submitAttempt,
      getThemeColor,
      'grid',
      $canvas,
      getButtonW()
    );

    submitRow.add(submitButton);
    submitRow.layoutChildrenLR(15);
    submitRow.sizeMeToFitChildren();
    submitRow.centerVertically();
    gag.submitRow = submitRow;

    gag.updateUndoRedoButtons();

    gag.add(submitRow);

    var bs = buttons[gag.agId + ''];
    var br;

    if (bs) {
      br = getBasicObject();

      for (var a = 0; a < bs.length; a++) {
        (function (z) {
          var key = getAnswerButton(bs[z]);

          br.add(key);
          key.sel = ag.selectedButton === bs[z];
        })(a);
      }

      br.layoutChildrenLR(10);
      br.centerHorizontally();
      br.sizeMeToFitChildren();
      gag.add(br);
    }

    gag.deselectAllButtons = function () {
      ag.selectedButton = undefined;

      if (br) {
        for (var j = 0; j < br.children.length; j++) {
          br.children[j].sel = false;
        }
      }
    };

    var vW = 0;
    var vH = 0;

    if (ag.m && !gag.justOneM) {
      vW += 5 + gag.top.viewportW;
      vH += gag.top.viewportH;
      gag.g.viewportY += vH;
    }

    submitRow.viewportY = grid.viewportY + grid.viewportH + 5;

    vW = Math.max(gag.g.viewportW, vW);
    vH += gag.g.viewportH + 5 + submitRow.viewportH;

    // if there are extra submit buttons, add them above the submit row
    if (br) {
      br.viewportY = submitRow.viewportY + 5;
      submitRow.viewportY = br.viewportY + br.viewportH + 10;
      vH += br.viewportH + 10;
    }

    gag.setAllDim(vW, vH);

    gag.centerHorizontally();

    if (gag.top) {
      gag.top.viewportX = Math.max(
        0,
        (gag.g.viewportW - gag.top.viewportW) / 2
      );
    }

    gag.insertAttemptIntoJSON = function () {
      var attempt;

      if (!grid.args.o.u) {
        attempt = { objects: [] };
      } else {
        attempt = {};

        attempt.objects = [];

        var myDots = [];
        var dotsHigh = false;

        var objs = grid.args.o.u;

        if (objs && objs.length > 0) {
          for (var a = objs.length - 1; a > -1; a--) {
            var obj = objs[a];

            if (obj.t === 'Parabola') {
              obj.path = null;
              obj.ar1 = null;
              obj.ar2 = null;
            }

            if (obj.t === 'dot') {
              myDots.push([obj.cx, obj.cy]);
              if (obj.high) dotsHigh = true;
            } else {
              // Check for no second point or a second point equal to the first
              if (
                !obj.second ||
                (obj.t != 'Line' &&
                  obj.first.cx == obj.second.cx &&
                  obj.first.cy == obj.second.cy)
              ) {
                var na = obj.t.toLowerCase();

                getProblemContext().addStackableDialog({
                  msg: `Hmm, you appear to have left a ${na} incomplete.`,
                  top: `Incomplete ${na}?`,
                });

                return 'invalid';
              } else {
                if (
                  obj.t === 'Ray' ||
                  obj.t === 'Line' ||
                  obj.t === 'Parabola'
                ) {
                  if (obj.t === 'Parabola') {
                    obj.showArrows = true;
                  }

                  obj.hideFirst = false;
                  obj.hideSecond = false;
                }
              }

              attempt.objects.push(obj);
            }
          }
        }

        if (myDots.length > 0) {
          attempt.dots = myDots;
          if (dotsHigh) attempt.dotsHigh = 't';
        }
      }

      var args = gag.g.args;

      attempt.set = {
        xS: args.uPH,
        yS: args.uPV,
        xU: args.xU,
        yU: args.yU,
      };

      if (grid.shadePoint) {
        attempt.sP = grid.shadePoint;
      }

      return attempt;
    };

    if (ag.m) {
      gag.setMode(ag.m);
    }

    gag.suppressPointCountOff = ag.g.sO;

    gag.multiInput = ag.g.mI;

    if (gag.lineMode !== undefined) {
      gag.lineMode.mode = gag.lineMode.states[gag.lineMode.i];

      var us = gag.g.args.o.u;

      if (us && us.length > 0) {
        if (us[us.length - 1].dotted) {
          gag.lineMode.i = 1;
          gag.lineMode.mode = gag.lineMode.states[gag.lineMode.i];
        }
      }
    }

    // Required getter function
    gag.serializeAttempt = function () {
      var g = gag.insertAttemptIntoJSON();

      return g !== 'invalid' ? g : undefined;
    };

    if (gag.g.args && gag.g.args.shadePoint) {
      gag.g.setShadePoint(gag.g.args.shadePoint);
    }

    return gag;
  };

  var isAxial = function (pt, xOnly, yOnly) {
    if (xOnly && yOnly) throw "Can't be both xOnly and yOnly";
    if (!xOnly && !yOnly) return pt.cx === 0 || pt.cy === 0;

    return xOnly ? pt.cy === 0 : pt.cx === 0;
  };

  var getGridGuide = function () {
    return createGridGuide({
      parabolaVertexThreshold: GRID_GUIDE_PARABOLA_VERTEX_THRESHOLD,
      pointColor: DEFAULT_GRAPH_OBJECT_PRE_COLOR,
      getCanvasWidth: function () {
        return canvasW;
      },
      getCanvasHeight: function () {
        return world.viewportH;
      },
      getThemeColor: getThemeColor,
      getThemeName: getThemeName,
      isSlopeNearlyVertical: isSlopeNearlyVertical,
      isNearlyHorizontal: isNearlyHorizontal,
      paintCanvas: paintCanvas,
      changedMaybe: changedMaybe,
      repositionProblemRows: repositionProblemRows,
      getCanvasElement: function () {
        return $canvas && $canvas[0];
      },
      setCanvasCursor: function (cursor) {
        $canvas.css('cursor', cursor);
      },
    });
  };

  var setGridGuideToDot = function (dot, grid) {
    if (!gridGuide) {
      gridGuide = getGridGuide();
    }

    gridGuide.open(dot, grid);
  };

  var getNormalGetter = function ({
    x,
    y,
    jsonFromServer,
    lines,
    row,
    agId,
    centerMe,
  }) {
    var ret = getBasicObject(x, y);

    ret.lines = lines;
    ret.row = row;
    ret.agId = agId;
    ret.centerMe = centerMe;

    var vH;
    var vW;

    if (jsonFromServer) {
      ret.maxLines = jsonFromServer.maxLines;
      ret.labelWarning = jsonFromServer.labelWarning;
      vH = jsonFromServer.vH;
      vW = jsonFromServer.vW;
      ret.args = jsonFromServer;
    }

    var minHeight = ret.maxLines > 1 ? 80 : vH ? Math.min(90, vH) : 50;

    ret.gmmName = 'normalGetter';

    ret.viewportW = vW || 180;
    ret.viewportH = minHeight;

    ret.setAllDim(ret.viewportW, ret.viewportH);
    ret.clip = true;

    for (var a = 0; a < lines.length; a++) {
      lines[a].normalHolder = ret;
      lines[a].agId = agId;
      lines[a].normalGetter = ret;
      lines[a].maxVW = ret.viewportW;
      lines[a].lineNumber = a;
      ret.add(lines[a]);
    }

    // If this 'normal' getter is being co-opted and used as a child
    // of a NumberLineGetter, then it has no json from server, as
    // that data is tracked by the parent NumberLineGetter
    ret.getJsonFromServer = function () {
      return ret.args || {};
    };

    ret.setXML = function (lineNumber, xml) {
      if (lineNumber) {
        ret.args.lines[lineNumber].xml = xml;
      } else {
        ret.args.lines[0].xml = xml;
      }
    };

    ret.childSizeChanged = function () {
      var parentObj = ret.parent;
      var needsReposition = false;

      while (parentObj) {
        if (parentObj.buildSizeRecursive) {
          parentObj.buildSizeRecursive();
          needsReposition = true;
        }

        parentObj = parentObj.parent;
      }

      if (needsReposition) {
        repositionProblemRows();
      }
    };

    // Only reports xml of first line
    ret.getXML = function () {
      return ret.args.lines[0].xml;
    };

    ret.swapString = function (old, replacer) {
      for (var a = 0; a < lines.length; a++) {
        lines[a].swapString(old, replacer);
      }
    };

    ret.nextLine = function () {
      var line;

      if (ret.maxLines && ret.lines.length < ret.maxLines) {
        line = toLine({
          problemJS: getProblemJSDependencies(),
          x: 3,
        });
        ret.args.lines.push({ xml: line.toXML() });
        line.normalHolder = ret;
        line.agId = agId;
        line.normalGetter = ret;
        line.maxVW = ret.viewportW;
        line.lineNumber = ret.lines.length;
        line.setLineOfChildren();
        line.lockViewportDim = false;
        ret.lines.push(line);
        ret.add(line);
        line.balanceEmptyTexts();
        line.buildSizeRecursive();

        ret.repositionLines();
        line.grabFocus();
      }
    };

    var repositionLinesInternal = function (notifyParent) {
      var margin = 10;
      var bottomPadding = ret.labelWarning
        ? LABEL_WARNING_RESERVED_SPACE
        : margin;

      ret.layoutChildrenUD(margin);

      var used = margin;

      for (var a = 0; a < ret.lines.length; a++) {
        used += ret.lines[a].viewportH;
        used += a === ret.lines.length - 1 ? bottomPadding : margin;
      }

      var priorHeight = ret.viewportH;

      // Resize height if needed
      if (used > ret.viewportH) ret.viewportH = used;
      else if (used < ret.viewportH && used != minHeight) {
        ret.viewportH = Math.max(minHeight, used);
      }

      var xtra = (ret.viewportH - used) / (ret.lines.length + 1);

      for (var a = 0; a < ret.lines.length; a++) {
        ret.lines[a].viewportY += margin + xtra;

        if (ret.centerMe) {
          ret.lines[a].viewportX = Math.max(
            0,
            ret.viewportW / 2 - ret.lines[a].viewportW / 2
          );
        }
      }

      ret.setAllDim(ret.viewportW, ret.viewportH);

      if (notifyParent && ret.viewportH !== priorHeight) {
        ret.childSizeChanged();
      }
    };

    ret.buildSizeRecursive = function () {
      for (var a = 0; a < ret.lines.length; a++) {
        ret.lines[a].buildSizeRecursive();
      }

      repositionLinesInternal(false);
    };

    ret.repositionLines = function () {
      repositionLinesInternal(true);
    };

    ret.grabFocus = function (dir) {
      lines[0].grabFocus(dir);

      if (ret.e === false) {
        console.error('error in focus #2');
      }
    };

    ret.childGainedFocus = function () {
      currentAttemptGetter = ret;
      insertKeyboard(ret.row, ret, agId);

      if (lettersButton != null) {
        lettersButton.staysAnimatedColor =
          currentAttemptGetter.letters === undefined ||
          currentAttemptGetter.letters.length != 1;
      }

      if (ret.e === false) {
        console.error('error in focus 18!');
      }
    };

    ret.loseFocus = function () {
      focused = undefined;
    };

    ret.setStatus = function (st) {
      ret.st = st;
      ret.e = st !== 'c';
    };

    ret.isFocused = function () {
      return focused && focused.line.normalHolder === ret;
    };

    ret.paintMe = function (ctx) {
      ctx.save();

      if (ret.isFocused()) {
        roundRect2(
          ctx,
          1,
          1,
          ret.viewportW - 2,
          ret.viewportH - 2,
          8,
          undefined,
          getThemeColor(),
          3
        );
      } else {
        roundRect2(
          ctx,
          1,
          1,
          ret.viewportW - 2,
          ret.viewportH - 2,
          8,
          undefined,
          UNFOCUSED_BOX_BORDER_COLOR,
          1
        );
      }

      var t = ret.selectedButton;

      if (t) {
        ctx.textBaseLine = 'middle';
        ctx.fillStyle = 'gray';
        ctx.font = '16px ' + lineFont;
        ctx.fillText(t, 10, ret.viewportH / 2 + 6);
      }

      if (ret.labelWarning) {
        ctx.textBaseLine = 'middle';
        ctx.textAlign = 'center';
        ctx.font = '12px ' + lineFont;

        const labelText = ret.labelWarning.toUpperCase();
        const labelWidth = ctx.measureText(labelText).width;
        const themeColor = getThemeColor();

        if (labelText.indexOf('REQUIRED') > -1) {
          ctx.fillStyle = 'white';

          roundRect2(
            ctx,
            ret.viewportW / 2 - labelWidth / 2 - 5,
            ret.viewportH - 20,
            labelWidth + 10,
            20,
            5,
            themeColor,
            themeColor,
            1
          );
        } else {
          ctx.fillStyle = themeColor;
        }

        ctx.fillText(labelText, ret.viewportW / 2, ret.viewportH - 7);
      }

      ctx.restore();
    };

    ret.paintPostClip = function (ctx) {
      paintStatus(ret, ctx);
    };

    ret.mouseDownResponse = function (x, y) {
      if (ret.e === false || (ret.nlg && ret.nlg.nl.st === 'c')) {
        return false;
      }

      var ul = lines[0];
      var bel = false;
      var abv = false;

      if (lines.length > 1) {
        var f = false;
        var my;

        for (var a = 0; a < lines.length - 1; a++) {
          var l = lines[a];

          my = l.viewportY + l.viewportH;

          if (y < my) {
            abv = a === 0;
            f = true;
            ul = l;
            break;
          }
        }

        if (!f) {
          bel = true;
          ul = lines[lines.length - 1];
        }
      } else {
        if (y < ul.viewportY) abv = true;
        else if (y > ul.viewportY + ul.viewportH) bel = true;
      }

      if (x < ul.viewportX) {
        ul.children[0].grabFocus('right');

        return false;
      }

      x -= ul.viewportX;

      var m = ul.children[0];

      for (var a = 0; a < m.children.length; a++) {
        var c = m.children[a];
        var right = c.viewportX + c.viewportW;

        if (x < right) {
          var left = c.viewportX;
          var mid = (left + right) / 2;
          var dr = x <= mid ? 'right' : 'left';

          if (c.t !== 'fraction') c.grabFocus(dr);
          else if (bel) c.denominator.grabFocus(dr);
          else if (abv) c.numerator.grabFocus(dr);
          else c.grabFocus(dr);

          return true;
        }
      }

      m.children[m.children.length - 1].grabFocus('left');

      return true;
    };

    ret.clear = function (clearButton) {
      if (ret.nlg) {
        ret.nlg.clear(true);

        return;
      }

      // check for request to clear selected button
      if (clearButton && ret.selectedButton) ret.selectedButton = null;

      var replacer = [];

      replacer[0] = ret.args.lines[0];
      ret.args.lines = replacer;

      var temp = ret.lines[0];

      temp.lineNumber = 0;
      var clearedLines = [];

      ret.lines = clearedLines;
      ret.lines.push(temp);
      ret.children = [];
      ret.add(temp);
      temp.balanceEmptyTexts();
      temp.buildSizeRecursive();

      ret.repositionLines();
      //* *plz leave the two grabs, they're both sorta bug patches
      temp.grabFocus();
      temp.clear();
      temp.grabFocus();
    };

    ret.repositionLines();

    // Required getter function
    ret.serializeAttempt = function () {
      // if systems and answer needs at least 2 or 3 statements, just add line
      if (ret.maxLines && !selfCheckListener) {
        if (ret.args.lines && ret.args.lines.length < ret.maxLines) {
          if (focused) {
            focused.nextLine();
            paintCanvas();
            console.log(
              'abandon serialization in favor of calling focused.nextLine()'
            );

            return;
          }
        }
      }

      // Checks if the given XML string contains a tag (e.g., <num>, <denom>, etc.)
      // that is either empty (e.g., <num></num>) or contains only a single space (e.g., <num><t> </t></num>).
      // Used to validate input and prevent submissions with blank or space-only tiles.
      var isTagEmptyOrSpaceOnly = function (xm, tag) {
        return (
          xm.indexOf('<' + tag + '></' + tag + '>') > -1 ||
          xm.indexOf('<' + tag + '><t> </t></' + tag + '>') > -1
        );
      };

      // Checks if the given XML string contains an empty subscript tag (i.e., <sub></sub>),
      // and that the base is not 'log' or 'ln' in which case empty subscripts are allowed.
      function isInvalidEmptySubscript(xm) {
        const baseStartTag = '<b><t>';
        const baseEndTag = '</t></b>';
        let idx = 0;

        while ((idx = xm.indexOf('<sub></sub>', idx)) !== -1) {
          // Look for the base just before this <sub></sub>
          // Find the last <b><t>...</t></b> before idx
          const baseEnd = xm.lastIndexOf(baseEndTag, idx);
          const baseStart = xm.lastIndexOf(baseStartTag, baseEnd);
          let base = '';

          if (baseStart !== -1 && baseEnd !== -1 && baseEnd > baseStart) {
            base = xm
              .substring(baseStart + baseStartTag.length, baseEnd)
              .trim();
          }

          if (base !== 'log' && base !== 'ln') {
            return true;
          }

          idx += 11; // Move past this <sub></sub>
        }

        return false;
      }

      var attempt = { lines: [] };

      for (var x = 0; x < ret.args.lines.length; x++) {
        var xm = ret.args.lines[x].xml;

        if (
          x === 0 &&
          (xm === '' ||
            // unique checks for <t> tags:
            // check if entire entry is just a space or if any other tile type contains an empty text tile
            // Not using isTagEmptyOrSpaceOnly because a student could put a space between two fully completed tiles
            // (and we don't want to mark that as invalid)
            xm === '<t> </t>' ||
            xm.indexOf('<t></t>') > -1 ||
            ['num', 'denom', 'q', 'e', 'b', 'r', 'a', 'rad'].some(tag =>
              isTagEmptyOrSpaceOnly(xm, tag)
            ) ||
            isInvalidEmptySubscript(xm))
        ) {
          if (!isTesting()) {
            getProblemContext().addStackableDialog({
              msg: 'Your attempt is not valid.',
              top: 'Invalid',
            });

            return;
          }
          // When testing, clicking submit for a blank normal getter when a button is already
          // selected should do nothing. It's not invalid, rather it's irrlevant.
          else if (currentAttemptGetter.selectedButton) return;
        }

        if (
          (!isTesting() && currentAttemptGetter === undefined) ||
          currentAttemptGetter.letters !== undefined
        ) {
          var copy = xm;

          while (copy.indexOf('<t>') > -1) {
            var i1 = copy.indexOf('<t>');
            var i2 = copy.indexOf('</t>');
            var t = copy.substring(i1 + 3, i2);

            copy = copy.substring(i2 + 4);
          }
        }

        xm = baseBugPatch(xm);
        xm = encodeText(xm);
        attempt.lines.push(xm);
      }

      return attempt;
    };

    return ret;
  };

  var getLineHolder = function (x, y, line, w, h) {
    var ret = getBasicObject(x, y);

    ret.line = line;
    ret.viewportW = w || 140;
    ret.viewportH = h || 56;
    line.maxVW = ret.viewportW;
    ret.viewportMargin = 1;
    ret.gmmName = 'lineHolder';

    ret.lineSizeChanged = function () {
      ret.width = ret.line.width;
      ret.height = ret.line.height;
      var dy =
        (ret.viewportH - ret.viewportMargin) / 2 - ret.line.viewportH / 2;

      ret.line.viewportY = dy;

      if (ret.centerText) {
        var dx =
          (ret.viewportW - ret.viewportMargin) / 2 - ret.line.viewportW / 2;

        ret.line.viewportX = dx;
      }
    };

    ret.grabFocus = function (dir) {
      ret.line.grabFocus(dir);

      if (ret.e === false) {
        console.error('focus error 3');
      }
    };

    ret.setEditable = function (b) {
      ret.e = b;
      ret.line.setEditable(b);
    };

    ret.add(line);
    line.lineHolder = ret;

    ret.lineSizeChanged();

    ret.setCenterText = function (b) {
      if (b == ret.centerText) {
        return;
      }

      ret.centerText = b;
      ret.lineSizeChanged();
    };

    ret.paintMe = function (ctx) {
      if (focused && focused.line === ret.line)
        roundRect2(
          ctx,
          0,
          0,
          ret.viewportW - 2,
          ret.viewportH - 2,
          6,
          undefined,
          getThemeColor(),
          6
        );
    };

    return ret;
  };

  var paintStatus = function (
    obj,
    ctx,
    x,
    y,
    w,
    h,
    radius,
    statusSizeOverride,
    nudgeX,
    nudgeY
  ) {
    if (!obj.st || obj.st === 'u') {
      return;
    }

    var color;
    var statusSize = statusSizeOverride ?? STATUS_DIAMETER;

    if (obj.st === 'wr') {
      color = 'magenta';
      if (!statusSizeOverride) statusSize *= 0.8;
    } else if (obj.st === 'c') {
      color = 'green';
    }

    if (isTesting() && obj.st == 'ready') {
      color = 'cyan';
      if (!statusSizeOverride) statusSize *= 0.6;
    }

    // tables will draw their own thumbs up to control location
    if (obj.gmmName === 't' || obj.gmmName === 'valueTable') return color;

    if (color) {
      // fill the area of the attempt getter with light green when correct
      if (color === 'green') {
        ctx.save();
        ctx.globalAlpha = 0.1;
        roundRect2(
          ctx,
          x ?? 0,
          y ?? -obj.viewportSlideY,
          w ?? obj.viewportW,
          h ?? obj.viewportH,
          radius ?? 8,
          color,
          null,
          1
        );
        ctx.globalAlpha = 1;
        roundRect2(
          ctx,
          x ?? 0,
          y ?? -obj.viewportSlideY,
          w ?? obj.viewportW,
          h ?? obj.viewportH,
          radius ?? 8,
          null,
          color,
          3
        );
        ctx.restore();
      } else if (color === 'magenta') {
        // outline the area of the attempt getter with magenta when wrong
        ctx.save();
        ctx.globalAlpha = 1;
        roundRect2(
          ctx,
          (x ?? 0) + 1,
          (y ?? -obj.viewportSlideY) + 1,
          (w ?? obj.viewportW) - 2,
          (h ?? obj.viewportH) - 2,
          radius ?? 8,
          null,
          color,
          3
        );
        ctx.restore();
      }

      var cx =
        -obj.viewportSlideX +
        obj.viewportW -
        (2 * statusSize) / 3 +
        (nudgeX ?? 0);
      var cy =
        (y ?? -obj.viewportSlideY) +
        obj.viewportH -
        (2 * statusSize) / 3 +
        (nudgeY ?? 0);

      ctx.save();
      ctx.globalAlpha = 0.5;

      if (color === 'cyan' || color === 'magenta') {
        if (color === 'cyan') {
          ctx.translate(-0.25 * statusSize, -0.25 * statusSize);
        }

        // cyan dot for "completed" in exam or magenta dot for "wrong" in practice
        circle(ctx, cx, cy, statusSize / 2, color);

        // draw a white "X" in the middle of the magenta dot
        if (color === 'magenta') {
          ctx.lineWidth = 2;
          ctx.strokeStyle = 'white';
          ctx.moveTo(cx - statusSize / 4, cy - statusSize / 4);
          ctx.lineTo(cx + statusSize / 4, cy + statusSize / 4);
          ctx.moveTo(cx + statusSize / 4, cy - statusSize / 4);
          ctx.lineTo(cx - statusSize / 4, cy + statusSize / 4);
          ctx.stroke();
        }
      } else if (color === 'green') {
        ctx.translate(-0.5 * statusSize, -0.5 * statusSize);

        // green thumbs up for "correct"
        drawImage(
          ctx,
          getImage('thumbsUp', paintCanvas),
          cx,
          cy,
          statusSize,
          statusSize
        );
      }

      ctx.restore();
    }
  };

  /**
   * @memberOf gmm
   *
   * args will have two forks:
   *  cols, which has xml from server
   *    (array of arrays of cells:
   *        cell.xml
   *        cell.e
   *    )
   *  uiCols, which (duh) has ui elements
   */
  var getTable = function (x, y, args, readAloudWrapper) {
    var table = getBasicObject(x, y);

    table.gmmName = 't';
    table.args = args;
    var minTableCellWidth = 40;

    // array of arrays
    var uiCols = [];

    for (var a = 0; a < args.cols.length; a++) {
      var col = args.cols[a];
      var uiCol = [];

      for (var r = 0; r < col.length; r++) {
        (function (b) {
          var cell = col[b];
          var uiCell = getBasicObject();
          var line = toLine({
            problemJS: getProblemJSDependencies(),
            xml: cell.xml,
          });

          line.e = cell.e ? true : false;

          if (line.e) {
            line.lockViewportDim = false;
            table.editableLines = table.editableLines || [];
            table.editableLines.push(line);
            line.tableCol = a;
            line.tableRow = r;
            var lineHolder = getLineHolder(0, 0, line, minTableCellWidth, 30);

            lineHolder.setCenterText(true);
            lineHolder.viewportMargin = 0;
            uiCell.add(lineHolder);
            uiCell.lineHolder = lineHolder;
          } else {
            uiCell.fill = 'lightgray';

            // see later loop: re-orienting for readAloud
            if (table.args.alignment === 'vertical') {
              line.ttsTransfer = cell.tts;
            } else if (readAloudWrapper) {
              injectReadAloudButton(
                readAloudWrapper,
                cell.tts,
                { paintCanvas, errorToServer, $canvas },
                line
              );
            }
          }

          uiCell.viewportMargin = 1;

          if (line.e) {
            uiCell.mouseDownResponse = function () {
              uiCell.line.grabFocus('left');

              return true;
            };
          } else {
            uiCell.add(line);
          }

          uiCell.line = line;
          line.uiCell = uiCell;
          uiCol.push(uiCell);
          table.add(uiCell);
        })(r);
      }

      uiCols.push(uiCol);
      table.uiCols = uiCols;
    }

    // prior loops work vertically through each column,
    // but users expect readAloud to go left to right
    // for vertically aligned tables,
    // so creating a new loop in loop after all the above
    // building just for readAloud
    if (readAloudWrapper && table.args.alignment === 'vertical') {
      for (let row = 0; row < uiCols[0].length; row++) {
        for (let col = 0; col < uiCols.length; col++) {
          const uiCell = uiCols[col][row];
          const line = uiCell.line;

          if (!line.e) {
            injectReadAloudButton(
              readAloudWrapper,
              line.ttsTransfer,
              { paintCanvas, undefined, $canvas },
              line
            );
          }
        }
      }
    }

    table.sizeMe = function () {
      var m = 0;
      var tableH = 0;
      var tableW = 0;

      for (var a = 0; a < table.uiCols.length; a++) {
        var uiCol = table.uiCols[a];
        var maxColW = 0;

        for (var r = 0; r < uiCol.length; r++) {
          var uiCell = uiCol[r];
          var w = uiCell.line.viewportW + 4;

          if (uiCell.lineHolder) {
            w = uiCell.lineHolder.viewportW;
          }

          maxColW = Math.max(w, maxColW);
        }

        for (var r = 0; r < uiCol.length; r++) {
          var uiCell = uiCol[r];

          if (uiCell.lineHolder) {
            uiCell.lineHolder.viewportW = maxColW;
          }
        }

        uiCol.cellW = Math.max(minTableCellWidth, maxColW);
        tableW += uiCol.cellW;
      }

      for (var a = 0; a < table.uiCols[0].length; a++) {
        var h = 30;

        for (var i = 0; i < uiCols.length; i++) {
          var hCheck = table.uiCols[i][a].line.viewportH + 4;

          if (table.uiCols[i][a].lineHolder) {
            hCheck = table.uiCols[i][a].lineHolder.viewportH;
          }

          h = Math.max(h, hCheck);
        }

        tableH += h;

        for (var i = 0; i < uiCols.length; i++) {
          table.uiCols[i][a].viewportH = h;
        }
      }

      for (var a = 0; a < table.uiCols.length; a++) {
        var uiCol = table.uiCols[a];
        var cellX = m;

        if (a > 0) {
          cellX =
            m +
            table.uiCols[a - 1][0].viewportX +
            table.uiCols[a - 1][0].viewportW;
        }

        for (var r = 0; r < uiCol.length; r++) {
          var uiCell = uiCol[r];
          var line = uiCell.line;

          line.viewportX = uiCol.cellW / 2 - line.viewportW / 2;
          line.viewportY = uiCell.viewportH / 2 - line.viewportH / 2;
          uiCell.setAllDim(uiCol.cellW, uiCell.viewportH);
          uiCell.viewportX = cellX;

          if (r > 0) {
            uiCell.viewportY =
              m + uiCol[r - 1].viewportY + uiCol[r - 1].viewportH;
          } else {
            uiCell.viewportY = m;
          }
        }
      }

      // +1 here is like "ummmm" below - it prevents right side of table from being slightly cut off
      table.viewportW = tableW + m * 3 + 1;

      var ummmm = 1;

      table.viewportH = tableH + m * (table.uiCols[0].length + 1) + ummmm;
    };

    table.paintPostClip = function (ctx) {
      paintStatus(table, ctx);
    };

    table.sizeMe();

    return table;
  };

  var getValueTable = function (x, y, args, readAloudWrapper) {
    var valueTable = getBasicObject(x, y);

    valueTable.gmmName = 'valueTable';
    valueTable.args = args;
    var minTableCellWidth = 30;
    var minTableCellHeight = 20;
    var widthPadding = 16;
    var heightPadding = 12;

    // Iterate through table and build cells for view
    var uiRows = [];

    for (var r = 0; r < args.rows.length; r++) {
      var row = args.rows[r];
      var uiRow = [];

      for (var c = 0; c < row.length; c++) {
        (function (b) {
          var cell = row[b];
          var uiCell = getBasicObject();
          var line = toLine({
            problemJS: getProblemJSDependencies(),
            xml: cell.xml,
          });

          line.e = cell.e ? true : false;

          // Check if editable
          if (line.e) {
            line.lockViewportDim = false;
            line.isDynamicSize = true;
            valueTable.editableLines = valueTable.editableLines || [];
            valueTable.editableLines.push(line);
            line.tableCol = c;
            line.tableRow = r;
            var lineHolder = getLineHolder(
              0,
              0,
              line,
              minTableCellWidth,
              minTableCellHeight
            );

            lineHolder.setCenterText(true);
            lineHolder.viewportMargin = 0;
            uiCell.add(lineHolder);
            uiCell.lineHolder = lineHolder;

            // Create mouse down event
            uiCell.mouseDownResponse = function () {
              uiCell.line.grabFocus('left');

              return true;
            };
          } else {
            uiCell.fill = 'lightgray';

            if (readAloudWrapper) {
              injectReadAloudButton(
                readAloudWrapper,
                cell.tts,
                { paintCanvas, undefined, $canvas },
                line
              );
            }

            uiCell.add(line);
          }

          uiCell.viewportMargin = 1;
          line.parent = uiCell;
          uiCell.line = line;
          uiCell.parent = valueTable;
          line.uiCell = uiCell;
          uiRow.push(uiCell);
          valueTable.add(uiCell);
        })(c);
      }

      uiRows.push(uiRow);
      valueTable.uiRows = uiRows;
    }

    valueTable.buildSizeRecursive = function () {
      for (var r = 0; r < valueTable.uiRows.length; r++) {
        var uiRow = valueTable.uiRows[r];

        for (var c = 0; c < uiRow.length; c++) {
          var uiCell = uiRow[c];

          if (uiCell.lineHolder != null) {
            uiCell.lineHolder.line.buildSizeRecursive();
          }
        }
      }

      valueTable.childSizeChanged();
    };

    valueTable.childSizeChanged = function () {
      valueTable.sizeMe();
      valueTable.setAllDim(valueTable.viewportW, valueTable.viewportH);
    };

    valueTable.sizeMe = function () {
      var tableH = 0;
      var tableW = 0;
      var maxColW = [];
      var maxRowH = [];

      // Calculate the widest cell per column and tallest cell per row
      for (var r = 0; r < valueTable.uiRows.length; r++) {
        var uiRow = valueTable.uiRows[r];

        for (var c = 0; c < uiRow.length; c++) {
          var uiCell = uiRow[c];
          var w = uiCell.line.viewportW + widthPadding;
          var h = uiCell.line.viewportH + heightPadding;

          maxColW[c] = Math.max(
            minTableCellWidth,
            Math.max(w, maxColW.length > c ? maxColW[c] : 0)
          );
          maxRowH[r] = Math.max(
            minTableCellHeight,
            Math.max(h, maxRowH.length > r ? maxRowH[r] : 0)
          );
        }

        uiRow.cellH = maxRowH[r];
      }

      // Mark all rows for max cell width
      for (var r = 0; r < valueTable.uiRows.length; r++) {
        var uiRow = valueTable.uiRows[r];

        // Iterate and set standardized width / heights
        var rowWidth = 0;

        for (var c = 0; c < uiRow.length; c++) {
          var uiCell = uiRow[c];

          rowWidth += maxColW[c];
          uiCell.viewportW = maxColW[c];
          uiCell.viewportH = maxRowH[r];

          if (uiCell.lineHolder) {
            uiCell.lineHolder.viewportW = maxColW[c];
            uiCell.lineHolder.viewportH = maxRowH[r];
          }
        }

        tableW = Math.max(tableW, rowWidth);
        tableH += uiRow.cellH;
      }

      // Position each table cell
      for (var r = 0; r < valueTable.uiRows.length; r++) {
        var uiRow = valueTable.uiRows[r];
        var cellY = 0;

        if (r > 0)
          cellY =
            valueTable.uiRows[r - 1][0].viewportY +
            valueTable.uiRows[r - 1][0].viewportH;

        for (var c = 0; c < uiRow.length; c++) {
          var cellX = 0;
          var uiCell = uiRow[c];
          var line = uiCell.line;

          line.viewportX = maxColW[c] / 2 - line.viewportW / 2;
          line.viewportY = maxRowH[r] / 2 - line.viewportH / 2;
          uiCell.setAllDim(uiCell.viewportW, uiCell.viewportH);

          if (c > 0) cellX = uiRow[c - 1].viewportX + uiRow[c - 1].viewportW;

          uiCell.viewportX = cellX;
          uiCell.viewportY = cellY;
        }
      }

      valueTable.viewportW = tableW;
      valueTable.viewportH = tableH;
    };

    valueTable.paintPostClip = function (ctx) {
      var color = paintStatus(valueTable, ctx);

      if (!color) return;

      ctx.save();

      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(valueTable.viewportW + 1, 0);
      ctx.lineTo(valueTable.viewportW + 1, valueTable.viewportH + 1);
      ctx.lineTo(0, valueTable.viewportH + 1);
      ctx.lineTo(0, 0);
      ctx.closePath();

      ctx.strokeStyle = color;
      ctx.lineWidth = color === 'green' ? 3 : 6;
      ctx.globalAlpha = 0.5;
      ctx.stroke();

      // green thumbs up for "correct" and light green shading in table
      if (color === 'green') {
        ctx.globalAlpha = 0.1;
        ctx.fillStyle = 'green';
        ctx.fillRect(0, 0, valueTable.viewportW, valueTable.viewportH);

        ctx.globalAlpha = 1;
        drawImage(
          ctx,
          getImage('thumbsUp', paintCanvas),
          valueTable.viewportW - (2 * STATUS_DIAMETER) / 3,
          valueTable.viewportH - (2 * STATUS_DIAMETER) / 3,
          STATUS_DIAMETER,
          STATUS_DIAMETER
        );
      }

      ctx.restore();
    };

    valueTable.sizeMe();

    return valueTable;
  };

  var getValueTableGetter = function (x, y, args, row) {
    var tg = getBasicObject(x, y);

    tg.t = 'valueTable';
    tg.gmmName = 'valueTableGetter';
    tg.row = row;
    tg.args = args;

    var valueTable = getValueTable(
      0,
      0,
      args.valueTable,
      isReadAloud() ? tg : undefined
    );

    valueTable.parent = tg;
    tg.valueTable = valueTable;
    tg.add(valueTable);
    tg.layoutChildrenLR();
    tg.sizeMeToLowestAndRightmostChildren();

    // We found cases where tables barely fit in narrow view prior to read
    // aloud, so audio icon added to top left caused table's row to exceed
    // narrow max width. Here, we detect this issue and resolve it by
    // bumping the audio icon to above the table
    if (isReadAloud() && isNarrow() && tg.viewportW > NARROW_PROBLEM_WIDTH) {
      tg.layoutChildrenUD();
      tg.children.forEach(child => {
        child.viewportX = 0;
      });
      tg.sizeMeToLowestAndRightmostChildren();
    }

    for (var a = 0; a < valueTable.editableLines.length; a++) {
      valueTable.editableLines[a].tableGetter = tg;
    }

    tg.e = true;
    tg.agId = args.agId;

    tg.getJsonFromServer = function () {
      return tg.args;
    };

    tg.buildSizeRecursive = function () {
      valueTable.buildSizeRecursive();
      tg.childSizeChanged();
    };

    tg.childSizeChanged = function () {
      tg.layoutChildrenLR();
      tg.sizeMeToLowestAndRightmostChildren();
    };

    tg.grabFocus = function (dir) {
      tg.valueTable.editableLines[0].grabFocus(dir || 'left');
    };

    tg.childGainedFocus = function () {
      currentAttemptGetter = tg;
      insertKeyboard(tg.row, tg, tg.agId);

      if (lettersButton != null) {
        lettersButton.staysAnimatedColor = true;
      }

      if (tg.e === false) {
        console.error('error in focus! 28');
      }
    };

    tg.loseFocus = function () {
      focused = undefined;
    };

    tg.setStatus = function (st) {
      args.s = st;
      args.st = st;
      tg.valueTable.st = st;
      tg.st = st;
      tg.e = st !== 'c';

      if (!tg.e) {
        for (var r = 0; r < valueTable.uiRows.length; r++) {
          var uiRow = valueTable.uiRows[r];

          for (var c = 0; c < uiRow.length; c++) {
            var cell = uiRow[c];

            if (cell.lineHolder) {
              cell.lineHolder.setEditable(false);
              cell.mouseDownResponse = undefined;
            }
          }
        }
      }
    };

    tg.isLastFocused = function () {
      var lines = tg.valueTable.editableLines;
      var c = -1,
        r = -1;
      var found = false;

      for (var a = 0; a < lines.length; a++) {
        var line = lines[a];

        if (focused.line === line) {
          c = line.tableCol;
          r = line.tableRow;
          found = true;
          break;
        }
      }

      if (!found) {
        return false;
      }

      var last = lines[lines.length - 1];
      var rowMax = last.tableRow;
      var colMax = last.tableCol;

      return c == colMax && r == rowMax;
    };

    tg.tab = function (dir) {
      if (!dir) {
        dir = 'right';
      }

      var lines = tg.valueTable.editableLines;
      var c = -1,
        r = -1;
      var found = false;

      var rowMax = lines[lines.length - 1].tableRow;
      var colMax = 0;

      for (var a = 0; a < lines.length; a++) {
        var line = lines[a];

        colMax = Math.max(colMax, line.tableCol);

        if (!found && focused.line === line) {
          c = line.tableCol;
          r = line.tableRow;
          found = true;
        }
      }

      if (!found) {
        return;
      }

      var nc = c;
      var nr = r;

      if (dir === 'up') {
        nc++;
        nr--;
      } else if (dir === 'down') {
        nr++;
        nc--;
      }

      // Logically move to the next best cell based on direction
      do {
        var isForward = dir === 'right' || dir === 'down';

        if (isForward) nc++;
        else nc--;

        if (nc > colMax) {
          nc = 0;
          nr++;
        } else if (nc < 0) {
          nc = colMax;
          nr--;
        }

        if (nr > rowMax) {
          nr = 0;
          nc = 0;
        } else if (nr < 0) {
          nr = rowMax;
          nc = colMax;
        }
      } while (lines.length > 0 && !tg.valueTable.uiRows[nr][nc].line.e);

      tg.valueTable.uiRows[nr][nc].line.grabFocus(
        dir === 'left' ? 'right' : 'left'
      );
    };

    // Required getter function
    tg.serializeAttempt = function () {
      var uilines = tg.valueTable.editableLines;
      var lines = [];

      for (var a = 0; a < uilines.length; a++) {
        var line = { c: uilines[a].tableCol, r: uilines[a].tableRow };
        var xm = uilines[a].toXML();

        line.xml = encodeText(xm);
        lines.push(line);
      }

      return { lines };
    };

    return tg;
  };

  var getMultiSelectGetter = function (x, y, jsonFromServer) {
    var multiSelectGetter = getBasicObject(x, y);

    multiSelectGetter.args = jsonFromServer;
    multiSelectGetter.agId = jsonFromServer.agId;
    multiSelectGetter.t = jsonFromServer.t;
    multiSelectGetter.numberSelectable = jsonFromServer.numberSelectable;
    // gmmName is used for convenience when debugging
    multiSelectGetter.gmmName = 'multiSelectGetter';

    // Server may send a prompt, such as 'Choose TWO of the following:'
    if (jsonFromServer.prompt) {
      var prompt = getStaticPanel({
        xml: '<t>' + jsonFromServer.prompt + '</t>',
        tts: buildTTSDTO(jsonFromServer.prompt),
      });

      // margin below prompt
      prompt.viewportH += 8;
      // prompt gets added to the widgets wrapped by the multiSelectGetter
      multiSelectGetter.add(prompt);
    }

    multiSelectGetter.optionPanels = [];

    multiSelectGetter.getJsonFromServer = function () {
      return multiSelectGetter.args;
    };

    multiSelectGetter.addOptionPanel = function (optionPanel) {
      // optionPanel gets added to the widgets wrapped by the multiSelectGetter
      multiSelectGetter.add(optionPanel);
      // optionPanels can be accessed via this collection
      // (not mixed with other widgets, such as the prompt)
      multiSelectGetter.optionPanels.push(optionPanel);
    };

    // ************ OPTION PANELS *************

    // margin between checkbox and left side of panel showing option/choice
    const MARGIN_CHECKBOX_TO_DISPLAY = 10;
    // margin on right side of option/choice
    const MARGIN_RIGHT_OF_DISPLAY = 5;

    // For each each option,
    // create an 'optionPanel' -- a panel that holds both a checkbox
    // and a display of the option,
    // as well as perhaps a horizontal divider
    jsonFromServer.options.forEach((jsonOption, optionIndex) => {
      // build the panel that will hold both checkbox and display of option
      var optionPanel = getBasicObject();

      // gmmName is used for convenience when debugging
      optionPanel.gmmName = 'optionPanel';
      optionPanel.letter = jsonOption.letter;

      optionPanel.getLetter = function () {
        return optionPanel.letter;
      };

      // ************ CHECKBOX *************

      // build the checkbox object
      var checkbox = buildMultiSelectCheckbox({
        letter: jsonOption.letter,
        isTesting,
        onSelectedChange: function (b) {
          multiSelectGetter.args.options.forEach(storedOption => {
            if (storedOption.letter === jsonOption.letter) {
              storedOption.state = b ? 'checked' : 'unchecked';
            }
          });
        },
        paintCanvas,
      });

      optionPanel.add(checkbox);
      optionPanel.checkbox = checkbox;

      optionPanel.isSelected = function () {
        return checkbox.isSelected();
      };

      // ************ DISPLAY OF OPTION *************

      // build a view of the option's display

      // Use a ReadAloudWrapper for audio icon so that when shrinking is needed,
      // the ReadAloudWrapper will protect the icon from shrinking and only shrink
      // the display of the option/choice.
      var readAloudWrapper =
        isReadAloud() && hasReadAloudContent(jsonOption.display.tts)
          ? getReadAloudWrapper()
          : undefined;

      var display = getStaticPanel(
        jsonOption.display,
        undefined,
        undefined,
        true,
        readAloudWrapper
      );

      // When getStaticPanel is called with a targetForReadAloud,
      // it does not add the created static panel to the target panel --
      // this is left to the caller if needed. Normally, it is not needed,
      // such as in setProblem when a second horizontally aligned panel in a
      // single row is supplied the first panel as targetForReadAloud.
      // However, here, we want the created static panel to be the second
      // child of the ReadAloudWrapper for control of shrinking.
      if (readAloudWrapper) {
        readAloudWrapper.add(display);
        readAloudWrapper.layoutChildrenLR();
        display = readAloudWrapper;
      }

      display.viewportX = checkbox.viewportW + MARGIN_CHECKBOX_TO_DISPLAY;
      optionPanel.add(display);

      // horizontal pixels available for display:
      // width of canvas
      // take away width of checkbox
      var availableWidth =
        canvasW -
        checkbox.viewportW -
        MARGIN_CHECKBOX_TO_DISPLAY -
        MARGIN_RIGHT_OF_DISPLAY;

      display.setMaxWidth(availableWidth);

      // Base optionPanel's height on heighest child: checkbox or display
      optionPanel.viewportH = Math.max(checkbox.viewportH, display.viewportH);
      // optionPanel's width is the x coordinate of the right side of the display
      optionPanel.viewportW = display.viewportX + display.viewportW;
      // Align children (checkbox and display) so vertical centers are at same horizontal level
      optionPanel.centerVertically();

      var mouseDownResponse = function () {
        // if the getter is not enabled, ignore mouse down
        if (!multiSelectGetter.isEnabled()) return true;
        if (!checkbox.isEnabled()) return true;

        changedMaybe(multiSelectGetter.agId);

        // if this is a 'choose one,' only allow one selection at a time
        if (jsonFromServer.numberSelectable == 1) {
          multiSelectGetter.deselectAll();
          checkbox.setSelected(true);
        }
        // Allow deselection if hasn't been marked as correct choice on prior submit
        else if (checkbox.isSelected()) checkbox.toggleSelected();
        // Not deselecting, and not in 'choose one' mode?
        // Only permit selections up to 'numberSelectable'
        else if (multiSelectGetter.allowAnotherSelection()) {
          checkbox.toggleSelected();
        }

        multiSelectGetter.grabFocus();

        return true;
      };

      // In narrow mode, only the checkbox responds to mouseDown.
      // This is to avoid a student accidentally 'checking' when trying
      // to scroll on phone.
      if (!narrow) optionPanel.mouseDownResponse = mouseDownResponse;
      else {
        checkbox.mouseDownResponse = mouseDownResponse;
      }

      multiSelectGetter.addOptionPanel(optionPanel);

      // ************ HORIZONTAL SEPARATOR *************

      // Add horizontal gray separator segment after each option except the last one
      if (optionIndex < jsonFromServer.options.length - 1) {
        var separator = getBasicObject();

        separator.t = 'separator';

        // margin between bottom of display and horizontal separator
        optionPanel.viewportH += 8;
        // Separator is at bottom of optionPanel
        separator.viewportY = optionPanel.viewportH - 1;
        // '0' width here is irrelevant, as we reset width for all separators later, matching width of widest optionPanel
        separator.setAllDim(0, SEPARATOR_THICKNESS);
        separator.fill = SEPARATOR_COLOR;
        optionPanel.separator = separator;
        optionPanel.add(separator);
      }
    });

    // Loop to find ideal separator width.
    // Separators (light gray segments between optionPanels)
    // should be as wide as widest optionPanel.
    var separatorWidth = 0;
    var optionPanels = multiSelectGetter.optionPanels;

    optionPanels.forEach(optionPanel => {
      separatorWidth = Math.max(separatorWidth, optionPanel.viewportW);
    });

    // Assign ideal separator width to all separators (and optionPanels)
    optionPanels.forEach(optionPanel => {
      optionPanel.viewportW = separatorWidth;

      if (optionPanel.separator) {
        optionPanel.separator.setAllDim(separatorWidth, SEPARATOR_THICKNESS);
      }
    });

    // effectively, adds 7 pixel margin at the top of each optionPanel
    optionPanels.forEach(optionPanel => {
      optionPanel.viewportH += 7;

      // Slide all children of optionPanel down 7 pixels so that the margin
      // created above is at the top of the optionPanel
      optionPanel.children.forEach(child => {
        child.viewportY += 7;
      });
    });

    // Prompt (optional) and optionPanels will be distributed vertically.
    multiSelectGetter.layoutChildrenUD(0);
    // Getter determines its width and height based on space occupied by children
    multiSelectGetter.sizeMeToLowestAndRightmostChildren();

    // ************ SUBMIT BUTTON *************

    var submitButton = getSubmitButton(
      multiSelectGetter,
      paintCanvas,
      submitAttempt,
      getThemeColor,
      'multiSelect',
      $canvas,
      getButtonW()
    );

    multiSelectGetter.add(submitButton);
    submitButton.viewportX =
      multiSelectGetter.viewportW / 2 - submitButton.viewportW / 2;
    const MARGIN_UNDER_LAST_OPTION_PANEL = 15;

    submitButton.viewportY =
      multiSelectGetter.viewportH + MARGIN_UNDER_LAST_OPTION_PANEL;

    // Recompute getter dimensions now that submit button has been added
    multiSelectGetter.sizeMeToLowestAndRightmostChildren();

    // ************ UNIQUE MultiSelectGetter METHODS *************

    // Apply checkbox state sent from server. For example, mark previously
    // submitted options incorrect or correct and disable them (if not testing).
    multiSelectGetter.update = function (updatedJson) {
      var inboundOptions = updatedJson.options;
      var storedJsonOptions = multiSelectGetter.args.options;

      inboundOptions.forEach((inboundOption, x) => {
        var storedOption = storedJsonOptions[x];

        storedOption.state = inboundOption.state;
        storedOption.correct = inboundOption.correct;

        var checkbox = multiSelectGetter.optionPanels[x].checkbox;

        checkbox.setSelected(inboundOption.state === 'checked');
        checkbox.setCorrectlySubmitted(inboundOption.correct);
      });
    };

    // Apply checkbox state originally received from server
    multiSelectGetter.update(jsonFromServer);

    // How many checkboxes are selected?
    // If not testing, includes checkboxes that were correctly submitted and are now disabled
    multiSelectGetter.getSelectedCount = function () {
      var selectedCount = 0;

      multiSelectGetter.optionPanels.forEach(optionPanel => {
        if (optionPanel.isSelected()) {
          selectedCount++;
        }
      });

      return selectedCount;
    };

    multiSelectGetter.hasRequiredSelectionCount = function () {
      return (
        multiSelectGetter.getSelectedCount() ==
        multiSelectGetter.getNumberSelectable()
      );
    };

    multiSelectGetter.allowAnotherSelection = function () {
      return !multiSelectGetter.hasRequiredSelectionCount();
    };

    multiSelectGetter.getNumberSelectable = function () {
      return multiSelectGetter.numberSelectable;
    };

    multiSelectGetter.deselectAll = function () {
      multiSelectGetter.optionPanels.forEach(optionPanel => {
        optionPanel.checkbox.setSelected(false);
      });
    };

    multiSelectGetter.hasSelected = function () {
      return multiSelectGetter.getSelectedCount() > 0;
    };

    // ************ REQUIRED GETTER METHODS *************

    // All getters are expected to support this function
    multiSelectGetter.setStatus = function (status) {
      multiSelectGetter.st = status;
      multiSelectGetter.e = status !== 'c';
    };

    // All getters are expected to support this function
    multiSelectGetter.loseFocus = function () {};

    // All getters are expected to support this function
    multiSelectGetter.grabFocus = function () {
      if (
        currentAttemptGetter &&
        currentAttemptGetter.gmmName == 'gridGetter'
      ) {
        currentAttemptGetter.g.setSelected(false);
      }

      setFocused(undefined);

      removeKeyboard();
      currentAttemptGetter = multiSelectGetter;
    };

    // All getters will be expected to support this function
    multiSelectGetter.isEnabled = function () {
      return status !== 'c';
    };

    // On submit, package ui state into json to send back to server.
    // All getters are expected to support this function.
    multiSelectGetter.serializeAttempt = function () {
      // if not testing, zero choices is invalid
      if (!isTesting()) {
        var errorMessage;

        if (multiSelectGetter.getSelectedCount() === 0) {
          errorMessage = 'You must make a choice.';
        }
        // if not testing, require number of choices to be number of correct options
        else if (!multiSelectGetter.hasRequiredSelectionCount()) {
          errorMessage =
            'You must select ' + multiSelectGetter.getNumberSelectable() + '.';
        }

        if (errorMessage) {
          getProblemContext().addStackableDialog({
            msg: errorMessage,
            top: 'Invalid',
          });

          return;
        }
      }

      var options = [];

      multiSelectGetter.optionPanels.forEach(option => {
        var jsonOption = {};

        jsonOption.state = option.isSelected() ? 'checked' : 'unchecked';
        jsonOption.letter = option.getLetter();
        options.push(jsonOption);
      });

      return { options };
    };

    return multiSelectGetter;
  };

  /**
   * @memberOf gmm
   *
   * choices: array of panelSuppliers
   *       panelSupplier has whatever it has for normal setProblem
   *         such as t string and lineXml ('normal' line)
   *         or t string and json (grid)
   * choice: letter
   * agId
   * mode: "4", "2by2"
   */
  var getMultiChoiceGetter = function (x, y, args) {
    var multi = getBasicObject(x, y);

    multi.uiChoices = [];
    var letters = 'ABCD';

    multi.checkboxes = [];
    multi.args = args;
    multi.agId = args.agId;
    multi.gmmName = 'multiChoice';
    multi.t = 'm';

    multi.getJsonFromServer = function () {
      return multi.args;
    };

    for (var a = 0; a < args.choices.length; a++) {
      // need closure or byRef uses last value of a for all mouseDowns
      (function (aa) {
        var letter = letters.substring(aa, aa + 1);
        var cb = buildMultiChoiceCheckbox({
          initialSelected: args.choice === letter,
          isTesting,
          paintCanvas,
        });
        var uiChoice = getBasicObject();

        uiChoice.gmmName = 'choicePanel';
        uiChoice.letter = letter;
        uiChoice.add(cb);
        uiChoice.cb = cb;

        var uiH = cb.viewportH;

        // HORIZONTAL PIXELS AVAILABLE:
        // width of canvas (or half width of canvas if mode is 2by2)
        // take away width of checkbox
        // take away 10 for margin between checkbox and left side of panel showing option/choice
        // take away 5 for margin on right side of option/choice
        // take away 1 because a 'precise' measurement ends up shaving the furthest right pixel sometimes
        var maxWidth =
          args.mode === '2by2' && !isNarrow() ? canvasW / 2 : canvasW;
        var maxDisplayWidth = maxWidth - cb.viewportW - 10 - 5 - 1;

        // Use a ReadAloudWrapper for audio icon so that when shrinking is needed,
        // the ReadAloudWrapper will protect the icon from shrinking and only shrink
        // the display of the option/choice.
        var readAloudWrapper =
          isReadAloud() && hasReadAloudContent(args.choices[aa].tts)
            ? getReadAloudWrapper()
            : undefined;

        var pss = getStaticPanel(
          args.choices[aa],
          undefined,
          undefined,
          true,
          readAloudWrapper
        );

        // When getStaticPanel is called with a targetForReadAloud,
        // it does not add the created static panel to the target panel --
        // this is left to the caller if needed. Normally, it is not needed,
        // such as in setProblem when a second horizontally aligned panel in a
        // single row is supplied the first panel as targetForReadAloud.
        // However, here, we want the created static panel to be the second
        // child of the ReadAloudWrapper for control of shrinking.
        if (readAloudWrapper) {
          readAloudWrapper.add(pss);
          readAloudWrapper.layoutChildrenLR();
          pss = readAloudWrapper;
        }

        pss.setMaxWidth(maxDisplayWidth);

        pss.viewportX = cb.viewportW + 10;
        pss.choicePan = true;

        uiChoice.add(pss);

        uiH = Math.max(uiH, pss.viewportH);
        uiChoice.viewportW = pss.viewportX + pss.viewportW;
        uiChoice.viewportH = uiH;

        uiChoice.centerVertically();

        var mdr = function () {
          if (!multi.e) return true;
          // shouldn't need to check for isTesting, as server shouldn't ship this data when testing!
          if (cb.hasBeenIncorrectlySubmitted()) return true;
          changedMaybe(multi.agId);

          if (!cb.isSelected()) {
            for (var z = 0; z < multi.uiChoices.length; z++) {
              multi.uiChoices[z].cb.setSelected(false);
            }

            cb.toggleSelected();
            multi.args.choice = uiChoice.letter;
          }

          multi.grabFocus();

          return true;
        };

        if (!narrow) uiChoice.mouseDownResponse = mdr;
        else {
          cb.mouseDownResponse = mdr;
        }

        multi.uiChoices.push(uiChoice);
        multi.add(uiChoice);

        if (aa < args.choices.length - 1 && (narrow || args.mode === '4')) {
          var sep = getBasicObject();

          uiChoice.viewportH += 8;
          sep.viewportY = uiChoice.viewportY + uiChoice.viewportH - 1;
          sep.setAllDim(uiChoice.viewportW, SEPARATOR_THICKNESS);
          sep.fill = SEPARATOR_COLOR;
          uiChoice.sep = sep;
          uiChoice.add(sep);
        }
      })(a);
    }

    if (args.mode === '4' || narrow) {
      var uic = multi.uiChoices;

      for (var ba = 1; ba < uic.length; ba++) {
        var c = uic[ba];

        c.viewportH += 7;

        for (var bb = 0; bb < c.children.length; bb++) {
          c.children[bb].viewportY += 7;
        }
      }

      var vW = 0;

      for (var a = 0; a < uic.length; a++) {
        vW = Math.max(vW, uic[a].viewportW);
      }

      multi.layoutChildrenUD(0);
      multi.sizeMeToLowestAndRightmostChildren();

      for (var a = 0; a < multi.children.length; a++) {
        var n = multi.children[a];

        if (n.sep) {
          n.viewportW = vW;
          n.sep.setAllDim(vW, SEPARATOR_THICKNESS);
        }
      }
    } else if (args.mode === '2by2') {
      const MARGIN_BETWEEN_COLUMNS = 15;

      var uic = multi.uiChoices;
      var rx =
        uic.length < 3
          ? uic[0].viewportW
          : Math.max(uic[0].viewportW, uic[2].viewportW);

      rx += MARGIN_BETWEEN_COLUMNS;
      uic[1].viewportX = rx;

      if (uic.length > 3) {
        uic[3].viewportX = rx;
      }

      var r1h = Math.max(uic[0].viewportH, uic[1].viewportH);

      if (r1h > uic[0].viewportH) {
        uic[0].viewportY += r1h / 2 - uic[0].viewportH / 2;
      }

      if (r1h > uic[1].viewportH) {
        uic[1].viewportY += r1h / 2 - uic[1].viewportH / 2;
      }

      multi.viewportW = uic[1].gR();
      multi.viewportH = r1h;

      if (uic.length > 2) {
        const MARGIN_BETWEEN_ROWS = 10;

        r1h += MARGIN_BETWEEN_ROWS;
        uic[2].viewportY = r1h;

        if (uic.length > 3) {
          uic[3].viewportY = r1h;
          var r2h = Math.max(uic[2].viewportH, uic[3].viewportH);

          if (uic[2].viewportH < r2h) {
            uic[2].viewportY += r2h / 2 - uic[2].viewportH / 2;
          }

          if (uic[3].viewportH < r2h) {
            uic[3].viewportY += r2h / 2 - uic[3].viewportH / 2;
          }
        }

        // if == 3, use vW of first row (already set)
        if (uic.length > 3) {
          multi.viewportW = Math.max(multi.viewportW, uic[3].gR());
        }

        multi.sizeMeToLowestAndRightmostChildren();

        // add horizontal separator to separate top choices from bottom choices
        var sepHor = getBasicObject();

        sepHor.viewportY =
          r1h - MARGIN_BETWEEN_ROWS / 2 - SEPARATOR_THICKNESS / 2;
        sepHor.setAllDim(multi.viewportW, SEPARATOR_THICKNESS);
        sepHor.fill = SEPARATOR_COLOR;
        multi.add(sepHor);

        // add vertical separator to separate left choices from right choices
        var sepVer = getBasicObject();

        sepVer.viewportX =
          rx - MARGIN_BETWEEN_COLUMNS / 2 - SEPARATOR_THICKNESS / 2;
        sepVer.setAllDim(SEPARATOR_THICKNESS, multi.viewportH);
        sepVer.fill = SEPARATOR_COLOR;
        multi.add(sepVer);
      }
    } else {
      console.error('args.mode not supported in getMultiChoiceGetter');
    }

    multi.updateIncorrectChoices = function (incorrectChoiceLetters) {
      multi.args.incorrectChoices = incorrectChoiceLetters;

      if (!incorrectChoiceLetters) return;

      for (var x = 0; x < incorrectChoiceLetters.length; x++) {
        var letter = incorrectChoiceLetters[x];

        if (letter === multi.args.choice) {
          multi.args.choice = null;
        }

        for (var y = 0; y < multi.uiChoices.length; y++) {
          var uiC = multi.uiChoices[y];

          if (uiC.letter == letter) {
            uiC.cb.setCorrectlySubmitted(false);
            uiC.cb.setSelected(false);
          }
        }
      }
    };

    multi.updateCorrectChoice = function (correctChoiceLetter) {
      multi.args.correctChoice = correctChoiceLetter;

      if (!correctChoiceLetter) return;

      for (var y = 0; y < multi.uiChoices.length; y++) {
        var ui = multi.uiChoices[y];

        if (ui.letter == correctChoiceLetter) {
          ui.cb.setCorrectlySubmitted(true);
          multi.args.choice = correctChoiceLetter;
        }
      }
    };

    multi.updateChoiceStatus = function ({ incorrectChoices, correctChoice }) {
      if (incorrectChoices) multi.updateIncorrectChoices(incorrectChoices);
      if (correctChoice) multi.updateCorrectChoice(correctChoice);
    };

    multi.updateChoiceStatus({
      incorrectChoices: multi.args.incorrectChoices,
      correctChoice: multi.args.correctChoice,
    });

    multi.setStatus = function (st) {
      multi.st = st;
      multi.e = st !== 'c';
    };

    multi.loseFocus = function () {};

    multi.grabFocus = function () {
      if (
        currentAttemptGetter &&
        currentAttemptGetter.gmmName == 'gridGetter'
      ) {
        currentAttemptGetter.g.setSelected(false);
      }

      setFocused(undefined);

      removeKeyboard();
      currentAttemptGetter = multi;
    };

    var submitButton = getSubmitButton(
      multi,
      paintCanvas,
      submitAttempt,
      getThemeColor,
      'multiChoiceGetter',
      $canvas,
      getButtonW()
    );

    multi.add(submitButton);
    submitButton.viewportX = multi.viewportW / 2 - submitButton.viewportW / 2;
    submitButton.viewportY = multi.viewportH + 15;
    multi.sizeMeToLowestAndRightmostChildren();

    // Required getter function
    multi.serializeAttempt = function () {
      if (!multi.args.choice) {
        getProblemContext().addStackableDialog({
          msg: 'You must make a choice.',
          top: 'Invalid',
        });

        return;
      }

      return { choice: multi.args.choice };
    };

    return multi;
  };
  // Cycle through all getters in order (from index 0 to size - 1)
  // and submit any getter that is marked as "uncertain"
  // (i.e. not already submitted or changed after submission)
  // **CAREFUL: if button already selected, don't want to submit
  // blank material in getter
  var submitAllAgs = function (after) {
    var funcs = [];

    for (var a = 0; a < attemptGetters.size; a++) {
      (function (b) {
        var hasAnotherGetter = b < attemptGetters.size - 1;

        funcs[b] = function () {
          currentAttemptGetter = attemptGetters.get(b);

          if (
            !currentAttemptGetter.selectedButton &&
            currentAttemptGetter.uncertain
          ) {
            // make sure we aren't locked from previous submission
            setSubmitLock(false);

            submitAttempt({
              after: hasAnotherGetter ? funcs[b + 1] : after,
              autoSubmittedDuringTest: true,
              eventSource: 'submitAllAgs for autoSubmitDuringTest',
            });
          } else if (hasAnotherGetter) {
            funcs[b + 1]();
          } else if (after) {
            after();
          }
        };
      })(a);
    }

    if (funcs.length > 0) {
      funcs[0]();
    }
  };

  var getAttemptJSON = function (getter, button) {
    var ret = { id: currentProblemId, agId: getter.agId };

    // if currentAttemptGetter is a 'normal getter' used by a number line getter,
    // then this storage logic should acquire the parent number line getter
    var ag = attemptGetters.get(getter.agId);

    if (button) {
      ret.attempt = JSON.stringify({ button: button });

      return ret;
    }

    ret.attempt = ag.serializeAttempt();

    if (ret.attempt) {
      ret.attempt = JSON.stringify(ret.attempt);
    }

    return ret;
  };

  var setSubmitLock = function (b) {
    if (b === submitLock) return;
    submitLock = b;
    getProblemContext().logHistory?.('submitLock changed to: ' + submitLock);
  };

  var submitAttempt = function (submitParams) {
    var button = submitParams?.button || undefined;
    var after = submitParams?.after || undefined;
    var blank = submitParams?.blank || false;
    var autoSubmittedDuringTest =
      submitParams?.autoSubmittedDuringTest || false;
    var eventSource = submitParams?.eventSource || 'unknown';

    cancelReadAloud();

    getProblemContext().logHistory?.(
      'Submit requested, event source: ' + eventSource
    );

    if (!selfCheckListener && submitLock && !blank) {
      getProblemContext().logHistory?.(
        `Abandon submit submitLock: ${submitLock}, blank: ${blank}, selfCheckListener: ${selfCheckListener}`
      );

      return;
    }

    var submitStart = new Date().getTime();

    if (
      !selfCheckListener &&
      !autoSubmittedDuringTest &&
      lastSubmitTime > 0 &&
      submitStart - lastSubmitTime < 300
    ) {
      getProblemContext().logHistory?.(
        `Abandon submit due to submission ${
          submitStart - lastSubmitTime
        } ms after last submit`
      );

      return;
    }

    setLoading(true);
    setSubmitLock(true);

    // reset cursor to default on submit
    $canvas.css('cursor', 'default');

    // internal tool CanvasCapture selfCheck simulates 'clicking' correct button
    if (selfCheckListener && currentAttemptGetter.selectedButton) {
      button = currentAttemptGetter.selectedButton;
    }

    var submitBundle = {};

    if (!blank) {
      submitBundle = getAttemptJSON(currentAttemptGetter, button);

      if (!submitBundle.attempt) {
        getProblemContext().logHistory?.(
          'Abandon submit due to invalidation during getAttemptJSON'
        );

        setLoading(false);
        setSubmitLock(false);

        return;
      }

      currentAttemptGetter.selectedButton = button ? button : undefined;

      if (currentAttemptGetter.gmmName === 'gridGetter') {
        if (button) currentAttemptGetter.clearF(button, false);
        else currentAttemptGetter.deselectAllButtons();
      } else {
        if (button) {
          clearAll(true, true, true, null, false);
          currentAttemptGetter.getJsonFromServer().selectedButton = button;
        } else applySelectedButton();
      }
    } else {
      submitBundle = {
        id: currentProblemId,
        agId: currentAttemptGetter.agId,
      };

      submitBundle.attempt = JSON.stringify({ t: currentAttemptGetter.t });
      submitBundle.blank = true;

      if (currentAttemptGetter.gmmName === 'gridGetter') {
        currentAttemptGetter.deselectAllButtons();
      } else {
        currentAttemptGetter.selectedButton = undefined;
        applySelectedButton();
      }
    }

    if (getProblemContext().getMd5) {
      submitBundle.md5 = getProblemContext().getMd5(currentProblemId);
    }

    if (isTesting()) submitBundle.isTest = true;

    // prevent cache
    submitBundle.a = new Date().getTime();

    if (isStudent) {
      submitBundle.ss = getProblemContext().getSS();
      submitBundle.user = getProblemContext().getUsername();
    }

    var guid = getProblemContext().getGuid();

    if (guid) submitBundle.guid = guid;

    var url = getApiUrl() + '/Submit';

    var submitID = submitCount++;

    getProblemContext().logHistory?.(
      `submit #${submitID}
        for restore ${submitBundle.id}
        (md5 ${submitBundle.md5})
        eventSource ${eventSource}`
    );

    var timeSeen = getProblemContext().harvestTimeSeen(false);

    submitBundle.timeSeen = timeSeen ? timeSeen : 0;

    const apiCall = function () {
      let dataForError;

      return callSubmit(url, submitBundle)
        .then(data => {
          dataForError = data;
          lastSubmitTime = new Date().getTime();
          var duration = lastSubmitTime - submitStart;

          if (selfCheckListener) {
            selfCheckListener.addResult(currentAttemptGetter.agId, data);
          }

          if (getProblemContext().logHistory) {
            var forceLoginInternal = data.forceLoginInternal
              ? ' (forceLoginInternal)'
              : '';
            var msg = `
            Successful round trip to WF Submit Servlet ${forceLoginInternal} 
            on submit #${submitID} 
            for Restore ${submitBundle.id} 
            (md5 ${submitBundle.md5}) 
            in ${duration} millis
          `;

            getProblemContext().logHistory(msg);
          }

          if (data.forceLoginInternal) {
            throw new Error('forceLoginInternal');
          }

          var probs = getProblemContext().getProblems();
          var ag = currentAttemptGetter;
          var abandoned =
            probs && !isTesting() && probs.get(`${data.id}`) === undefined;

          if (!abandoned && data.changedNag) {
            attemptGetters
              .get(data.changedNag.agId)
              .changedNag(data.changedNag);
          }

          if (!abandoned && data.changedGrid) {
            data.changedGrid.color = 'rgba(255, 200, 0, .55)';
            data.changedGrid.lw = 9;
            attemptGetters
              .get(data.changedGrid.agId)
              .g.addLiney(data.changedGrid, true);
          }

          if ((!abandoned && data.correctChoice) || data.incorrectChoices) {
            attemptGetters.get(ag.agId).updateChoiceStatus({
              incorrectChoices: data.incorrectChoices,
              correctChoice: data.correctChoice,
            });
          }

          if (!abandoned && data.updateGetter) {
            attemptGetters.get(ag.agId).update(data.updateGetter);
          }

          if (!abandoned && data.swapLabel) {
            var lins = attemptGetters.get(data.swapLabel.agId).args.lines;

            for (var xa = 0; xa < lins.length; xa++) {
              var lin = lins[xa];
              var xm = lin.xml;
              var bad = data.swapLabel.badLabel;
              var i = xm.indexOf(bad);

              if (i > -1) {
                var good = data.swapLabel.goodLabel;

                lin.xml =
                  xm.substring(0, i) + good + xm.substring(i + bad.length);
                attemptGetters.get(data.swapLabel.agId).swapString(bad, good);
              }
            }
          }

          if (!isTesting() && !abandoned) {
            if (!data.inv && !data.uF) {
              if (data.aC) {
                attemptGetters.get(ag.agId).setStatus('c');
                if (!data.tC) changeFocus(ag.agId);
              } else {
                attemptGetters.get(ag.agId).setStatus('wr');
              }
            }
          }

          // Send along the response to student app via this function.
          // Student app may send back its 'nextExamProblem' function.
          var nextExamProblem = getProblemContext().attemptSubmitted(
            data,
            ag.agId,
            autoSubmittedDuringTest,
            blank
          );

          if (isTesting()) {
            attemptGetters.get(ag.agId).uncertain = blank ? true : false;

            if (data.valids) {
              for (var x = 0; x < data.valids.length; x++) {
                attemptGetters.get(data.valids[x]).setStatus('ready');
              }
            }

            if (data.invalids) {
              for (var x = 0; x < data.invalids.length; x++) {
                attemptGetters.get(data.invalids[x]).setStatus('invalid');
              }
            }

            if (!data.testResult || data.testResult.status !== 'ready') {
              if (!autoSubmittedDuringTest && ag.st === 'ready') {
                changeFocus(ag.agId);
              }
            }

            // if test attempt results in an invalid message, don't auto-advance to next problem
            if (data.thisTestAttempt && data.thisTestAttempt === 'invalid') {
              after = undefined;
              nextExamProblem = undefined;
              // also, set the currentAttemptGetter and the problem to uncertain so that later
              // clicking to a new exam square will trigger auto-submit
              currentAttemptGetter.uncertain = true;
              getProblemContext()
                .getProblems()
                .get(`${data.id}`).uncertain = true;
            }
          }

          // If number line getter was changed, advance focus to that getter,
          // otherwise move to next exam problem.
          if (data.changedNag) {
            // if we're not in an exam and the previous getter was wrong, return focus back to the inequality getter
            if (!isTesting() && data.changedNag.agId > 0) {
              var previousGetter = attemptGetters.get(data.changedNag.agId - 1);

              if (previousGetter && previousGetter.st === 'wr')
                previousGetter.grabFocus('left');
            } else {
              // identify number line getter as the currentAttemptGetter for automatic submission
              currentAttemptGetter = attemptGetters.get(data.changedNag.agId);

              // if replacing a previous attempt, unlock and submit the number line with the new value
              if (currentAttemptGetter.args.s !== 'invalid') {
                // have number line gain focus after update to inequality
                currentAttemptGetter.grabFocus();

                // set status to empty so getter shows as 'untried' from student perspective
                currentAttemptGetter.setStatus('');

                // set changedMaybe so that revisions to previously completed skills won't jump to next
                // available exam problem when updating the inequality and auto-submitting the number line
                changedMaybe(currentAttemptGetter.agId);
              }
            }
          } else if (nextExamProblem) nextExamProblem();

          paintCanvas();
          if (after) after();
        })
        .catch(error => {
          lastSubmitTime = new Date().getTime();
          var duration = lastSubmitTime - submitStart;
          let msg = `
            Error caught when attempting to submit by ${
              isStudent ? 'student' : 'anonymous'
            }. 
            Submit #${submitID} 
            for Restore ${submitBundle.id} 
            (md5 ${submitBundle.md5}) 
            ${duration} millis after submit start.
          `;

          if (error.name === 'AxiosError') {
            const code = error.code || 'No error code';
            const response = error.response
              ? JSON.stringify(error.response, null, 2)
              : 'No response';

            msg += `
              AXIOS ERROR
              Code: ${code}
              Response: ${response}
            `;
          } else {
            msg += `
              GMM LOGIC ERROR
              error.name: ${error.name || 'No error name available'}
              error.message: ${error.message || 'No error message available'}
              error.stack: ${error.stack || 'No stack trace available'}
              forceLoginInternal: ${
                dataForError ? dataForError.forceLoginInternal : 'false'
              }
              outbound submitBundle: ${JSON.stringify(submitBundle, null, 2)}
              inbound data: ${
                dataForError
                  ? JSON.stringify(dataForError, null, 2)
                  : 'No data available'
              }
            `;
          }

          getProblemContext().errorToServer(msg);

          const dialogMsg = isStudent
            ? 'GMM hit an unexpected technical error and needed to reset your screen.'
            : 'GMM hit a connection issue. Please try again in a moment.';

          getProblemContext().addStackableDialog({
            top: 'Connection Trouble',
            msg: dialogMsg,
            reload: true,
            reloadIsInternal: true,
          });
        })
        .finally(() => {
          setLoading(false);
          setSubmitLock(false);
        });
    };

    if (getProblemContext().addQueuedSubmit) {
      getProblemContext().addQueuedSubmit({
        id: submitBundle.id,
        md5: submitBundle.md5,
        submit: apiCall,
      });
    } else {
      apiCall();
    }
  };

  var changeFocus = function (startAgId, fromTab, reverseDirection) {
    if (!attemptGetters || attemptGetters.size === 0) {
      return;
    }

    if (gridGuide && gridGuide.on) {
      gridGuide.close();
    }

    var tabbedGetter = currentAttemptGetter?.nlg ?? currentAttemptGetter;

    if (
      fromTab &&
      currentAttemptGetter &&
      ((currentAttemptGetter.tab && currentAttemptGetter.isLastFocused) ||
        (currentAttemptGetter.nlg?.tab &&
          currentAttemptGetter.nlg?.isLastFocused))
    ) {
      var wasAtLast = tabbedGetter.isLastFocused();

      if (!wasAtLast) {
        tabbedGetter.tab();

        return true;
      }
    }

    /**
     * List getters in order in which they appear, top left to bottom right
     *
     * In older client versions, we used getter.agId as both a unique identifier
     * AND visual order indicator. As of late 2022, server no longer guarantees
     * this concurrent usage, but does still deliver getter json in the order
     * in which the getters appear vertically.
     */
    let gettersOrderedTopToBottom = Array.from(attemptGetters.values());
    let start = 0;

    if (startAgId == undefined && currentAttemptGetter) {
      startAgId = currentAttemptGetter.agId;
    }

    var oldGetter = currentAttemptGetter;
    var newGetter;

    if (reverseDirection) {
      // reverse direction means we want to go 'up' the list of getters
      if (startAgId !== undefined) {
        for (let x = gettersOrderedTopToBottom.length - 1; x >= 0; x--) {
          if (gettersOrderedTopToBottom[x].agId === startAgId) {
            start = x - 1;
            if (start < 0) start = gettersOrderedTopToBottom.length - 1;
            break;
          }
        }
      }

      for (let x = start; x >= 0; x--) {
        if (gettersOrderedTopToBottom[x].st !== 'c') {
          newGetter = gettersOrderedTopToBottom[x];
          break;
        }
      }

      if (!newGetter) {
        for (let x = gettersOrderedTopToBottom.length - 1; x > start; x--) {
          if (gettersOrderedTopToBottom[x].st !== 'c') {
            newGetter = gettersOrderedTopToBottom[x];
            break;
          }
        }
      }
    } else {
      if (startAgId !== undefined) {
        for (let x = 0; x < gettersOrderedTopToBottom.length; x++) {
          if (gettersOrderedTopToBottom[x].agId === startAgId) {
            start = x + 1;
            if (start > gettersOrderedTopToBottom.length) start = 0;
            break;
          }
        }
      }

      for (let x = start; x < gettersOrderedTopToBottom.length; x++) {
        if (gettersOrderedTopToBottom[x].st !== 'c') {
          newGetter = gettersOrderedTopToBottom[x];
          break;
        }
      }

      if (!newGetter) {
        for (let x = 0; x < start; x++) {
          if (gettersOrderedTopToBottom[x].st !== 'c') {
            newGetter = gettersOrderedTopToBottom[x];
            break;
          }
        }
      }
    }

    if (newGetter) {
      currentAttemptGetter = newGetter;
      // Notify old getter of focus loss
      oldGetter?.loseFocus?.();
      // Notify new getter of focus gain
      newGetter.grabFocus('left');

      return true;
    }
  };

  var useTouchKeyboard = function () {
    return isTouchDeviceOnly() || getProblemContext()?.isForceTouchKeyboard();
  };

  var buildKeyboard = function () {
    var ret = getBasicObject();

    ret.gmmName = 'keyboard';

    ret.setShowBarred = function (b) {
      if (ret.fracButton) ret.fracButton.setShowBarred(b);
      ret.showBarred = b;
    };

    ret.setShowDivision = function (b) {
      ret.showDivision = b ? true : false;
    };

    ret.setShowDollarSymbol = function (b) {
      ret.showDollarSymbol = b ? true : false;
    };

    var touch = useTouchKeyboard();

    var submit = getSubmitButton(
      undefined,
      paintCanvas,
      submitAttempt,
      getThemeColor,
      'mouseDown on keyboard submit button, getter: ' +
        getCurrentAttemptGetterName(),
      $canvas,
      LARGER_BUTTON_SCALAR * getButtonW()
    );

    ret.submitter = submit;

    /*
     * Keyboard central has one or two rows of buttons (depending on touch vs. non-touch)
     * and Clear All to the left and Submit to the right
     */
    var keyboardCentral = getBasicObject();

    /*
     * Row 1 is:
     * Non-Touch- Fraction, Quantity, Power, Root, Inequalities (Menu)
     * Touch - Space, Backspace
     */
    var centralRow1 = getBasicObject();

    if (touch) {
      centralRow1.add(getSpaceButton());
      centralRow1.add(getBackspaceButton());
    } else {
      var frac = getFracButton();

      if (ret.showBarred) frac.setShowBarred(true);
      ret.fracButton = frac;

      centralRow1.add(frac);
      centralRow1.add(getQuantityButton());
      centralRow1.add(getPowButton());
      centralRow1.add(getRootButton());
      centralRow1.add(getInequalitiesButton());
    }

    centralRow1.layoutChildrenLR(getButtonGap());
    centralRow1.sizeMeToFitChildren();

    keyboardCentral.add(centralRow1);

    if (!touch) {
      /*
       * Row 2 is only for non-touch devices
       * Absolute Value, Subscript, Pi, Log/Trig (Menu)
       */
      var centralRow2 = getBasicObject();

      centralRow2.add(getAbsoluteValueButton());
      centralRow2.add(getSubscriptButton());
      centralRow2.add(getSymbolButton('\u03C0'));
      centralRow2.add(getLogTrigButton());

      centralRow2.viewportX =
        centralRow1.viewportX + (getButtonW() + getButtonGap()) / 2;
      centralRow2.viewportY =
        centralRow1.viewportY + centralRow1.viewportH + getButtonGap();
      centralRow2.layoutChildrenLR(getButtonGap());
      centralRow2.sizeMeToFitChildren();

      keyboardCentral.add(centralRow2);
      keyboardCentral.layoutChildrenUD(getButtonGap());
    }

    keyboardCentral.sizeMeToFitChildren();

    var mainKeyboard = getBasicObject();

    mainKeyboard.add(getClearAll(LARGER_BUTTON_SCALAR * getButtonW(), true));
    mainKeyboard.add(keyboardCentral);
    mainKeyboard.add(submit);

    mainKeyboard.layoutChildrenLR(getButtonGap());
    mainKeyboard.sizeMeToFitChildren();
    mainKeyboard.centerVertically();

    /*
     * Set up general structure of keyboard:
     * 1. Extra Submit Buttons (if any)
     * 2. Clear All, Keyboard Central (two rows of buttons), Submit
     * 3. Bottom (if touch - based on numbers/alpha mode)
     */
    var extraSubmitButtons = getBasicObject();

    ret.add(extraSubmitButtons);

    ret.add(mainKeyboard);

    var bottom = getBasicObject();

    ret.add(bottom);

    ret.extraSubmitButtons = extraSubmitButtons;
    ret.mainKeyboard = mainKeyboard;
    ret.bottom = bottom;

    keyboard = ret;
    setKeyboardBottom(KEYBOARD_BOTTOM_MODE.NUMBERS);
  };

  var getButtonW = function () {
    return DEFAULT_BUTTON_SIZE * getKeyboardScalar();
  };

  var getButtonGap = function () {
    return DEFAULT_BUTTON_GAP * getKeyboardScalar();
  };

  // convenience function to pass the canvas along to button.ts
  var getButton = function () {
    return getBasicButton($canvas);
  };

  var getHolder = function (key, btwnH, btwnV, first, last) {
    var holder = getBasicObject();

    key.viewportX = !first ? btwnH / 2 : 0;
    key.viewportY = btwnV / 2;
    holder.setAllDim(
      key.viewportW + (first || last ? btwnH / 2 : btwnH),
      key.viewportH + btwnV
    );
    holder.add(key);

    holder.mouseDownResponse = function () {
      return key.mouseDown(
        holder.viewportW / 2,
        holder.viewportH / 2,
        paintCanvas
      );
    };

    return holder;
  };

  var setKeyboardBottom = function (t) {
    var side = getButtonW();
    var gap = 5 * getKeyboardScalar();

    var x = 0;

    /*
     * Set up the extra submit buttons (if any)
     */
    keyboard.extraSubmitButtons.children = [];

    if (currentAttemptGetter) {
      var bs = buttons[keyboard.agId + ''];

      if (bs) {
        buttonKeys = [];

        var buttonRowW = 0;

        for (var a = 0; a < bs.length; a++) {
          (function (z) {
            var key = getAnswerButton(bs[z]);

            keyboard.extraSubmitButtons.add(key);
            buttonKeys.push(key);

            buttonRowW += key.viewportW;
            if (a !== bs.length - 1) buttonRowW += 2 * gap;
          })(a);
        }

        // Currently, no problems use more than two buttons.
        // If a skill requires more, we'll likely want to make the following layout
        // more dynamic to group button pairs (that aren't too wide) together on a row
        // instead of making three (or more) rows of buttons.
        if (buttonRowW > getKeyboardW()) {
          keyboard.extraSubmitButtons.layoutChildrenUD(2 * gap);
          keyboard.extraSubmitButtons.sizeMeToFitChildren();
          keyboard.extraSubmitButtons.centerHorizontally();
        } else {
          keyboard.extraSubmitButtons.layoutChildrenLR(2 * gap);
          keyboard.extraSubmitButtons.sizeMeToFitChildren();
        }

        keyboard.extraSubmitButtons.viewportH += 2 * gap;
        keyboard.extraSubmitButtons.viewportX =
          currentAttemptGetter.viewportX +
          currentAttemptGetter.viewportW / 2 -
          keyboard.extraSubmitButtons.viewportW / 2;
      } else {
        keyboard.extraSubmitButtons.viewportH = 0;
      }
    }

    keyboard.mainKeyboard.viewportY =
      keyboard.extraSubmitButtons.viewportY +
      keyboard.extraSubmitButtons.viewportH;

    /*
     * Set up a skinny row for numbers/alpha selection buttons
     */
    var numbersAlphaRow = getBasicObject();
    var numbersButton = getNumbersButton();
    var alphaButton = getAlphaButton();
    var functionsButton = getFunctionsButton();

    numbersAlphaRow.add(numbersButton);
    numbersAlphaRow.add(alphaButton);
    numbersAlphaRow.add(functionsButton);

    numbersAlphaRow.layoutChildrenLR(gap);
    numbersAlphaRow.sizeMeToFitChildren();
    numbersAlphaRow.viewportY =
      keyboard.mainKeyboard.viewportY +
      keyboard.mainKeyboard.viewportH +
      2 * gap;

    keyboard.bottom.children = [];

    if (useTouchKeyboard()) {
      var cl = 0;

      if (t === KEYBOARD_BOTTOM_MODE.NUMBERS) {
        cl = 1;
      } else if (t == KEYBOARD_BOTTOM_MODE.ALPHABET) {
        cl = 2;
      } else if (t == KEYBOARD_BOTTOM_MODE.FUNCTIONS) {
        cl = 3;
      }

      if (cl) {
        // reset all buttons to unselected
        for (var az = 0; az < numbersAlphaRow.children.length; az++) {
          numbersAlphaRow.children[az].selected = false;
        }

        // select the current numbers/alpa mode button
        numbersAlphaRow.children[cl - 1].selected = true;
      }

      var rs = getBasicObject();

      if (t === KEYBOARD_BOTTOM_MODE.NUMBERS) {
        var digits = [];
        var symbols = [];

        for (var a = 0; a < 10; a++) {
          (function (b) {
            var mouseDownResponse = function () {
              pressedKey(b);

              return true;
            };
            var key = getButton();

            key.mouseDownResponse = mouseDownResponse;
            key.setAllDim(side, side);

            key.paintMe = function (ctx) {
              roundRect2(
                ctx,
                0,
                0,
                side,
                side,
                8,
                key.animated ? getThemeColor() : 'lightgray',
                undefined,
                undefined,
                true
              );
              ctx.save();
              ctx.beginPath();

              ctx.fillStyle = key.animated ? 'white' : 'gray';
              ctx.font = 22 * getKeyboardScalar() + 'px ' + lineFont;
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillText(b + '', side / 2, side / 2);

              ctx.restore();
            };

            var holder = getHolder(key, gap, gap, b === 0 || b == 5, false);

            digits.push(holder);
          })(a);
        }

        var chars = '+\u2212*\u00F7' + '=.,$' + '<\u2264>\u2265%:';

        for (var a = 0; a < chars.length; a++) {
          (function (b) {
            var but = getSymbolButton(b);

            var l = b === '\u00F7' || b === '$' || b === ':';

            symbols.push(getHolder(but, gap, gap, false, l));
          })(chars.substring(a, a + 1));
        }

        var r1Numbers = getBasicObject();
        var x = 0;

        for (var a = 0; a < 5; a++) {
          var n = digits[a];

          n.viewportX = x;
          x += n.viewportW;
          r1Numbers.add(n);
        }

        for (var a = 0; a < 4; a++) {
          var n = symbols[a];

          n.viewportX = x;
          x += n.viewportW;
          r1Numbers.add(n);
        }

        r1Numbers.sizeMeToFitChildren();

        var r2Numbers = getBasicObject();

        x = 0;

        for (var a = 5; a < 10; a++) {
          var n = digits[a];

          n.viewportX = x;
          x += n.viewportW;
          r2Numbers.add(n);
        }

        for (var a = 4; a < 8; a++) {
          var n = symbols[a];

          n.viewportX = x;
          x += n.viewportW;
          r2Numbers.add(n);
        }

        r2Numbers.sizeMeToFitChildren();

        var r3Numbers = getBasicObject();

        var letBox = getBasicObject();
        var wi = side * 3 + gap * 2.5;

        letBox.setAllDim(wi, side + 2 * gap);
        // default to showing x, y, pi
        var letters =
          currentAttemptGetter && currentAttemptGetter.letters
            ? currentAttemptGetter.letters
            : 'xy\u03C0';

        var star =
          letters.length == 1
            ? side + gap / 2
            : letters.length == 2
            ? 0.5 * side
            : 0;

        for (var a = 0; a < letters.length; a++) {
          (function (b) {
            var mouseDownResponse = function () {
              pressedKey(b);

              return true;
            };
            var key = getButton();

            key.mouseDownResponse = mouseDownResponse;
            key.setAllDim(side, side);

            key.paintMe = function (ctx) {
              roundRect2(
                ctx,
                0,
                0,
                side,
                side,
                8,
                key.animated ? getThemeColor() : getContrastingThemeColor(),
                undefined,
                undefined,
                true
              );
              ctx.save();
              ctx.beginPath();

              ctx.fillStyle = 'white';
              ctx.textBaseline = 'middle';
              ctx.textAlign = 'center';
              ctx.font = 22 * getKeyboardScalar() + 'px ' + lineFont;

              ctx.fillText(b + '', side / 2, side / 2);

              ctx.restore();
            };

            var first =
              letters.length == 3 &&
              (!letBox.children || !letBox.children.length);
            var ho = getHolder(key, gap, gap, first);

            ho.viewportX = star;
            star += gap + side - (first ? gap / 2 : 0);
            letBox.add(ho);
          })(letters.substring(a, a + 1));
        }

        r3Numbers.add(letBox);

        x = letBox.viewportW;

        for (var a = 8; a < 14; a++) {
          var n = symbols[a];

          n.viewportX = x;
          x += n.viewportW;
          r3Numbers.add(n);
        }

        r3Numbers.sizeMeToFitChildren();

        rs.add(r1Numbers);
        rs.add(r2Numbers);
        rs.add(r3Numbers);
      } else if (t === KEYBOARD_BOTTOM_MODE.ALPHABET) {
        var letters = [];

        var chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ' + '\u03C0';

        for (var a = 0; a < chars.length; a++) {
          (function (b) {
            var mouseDownResponse = function () {
              pressedKey(b.toLowerCase());

              return true;
            };
            var key = getButton();

            key.mouseDownResponse = mouseDownResponse;
            key.setAllDim(side, side);

            // special case for pi to center it in the button
            if (b === '\u03C0') {
              key.paintMe = function (ctx) {
                roundRect2(
                  ctx,
                  0,
                  0,
                  side,
                  side,
                  8,
                  key.animated ? getThemeColor() : getContrastingThemeColor(),
                  undefined,
                  undefined,
                  true
                );
                ctx.save();
                ctx.beginPath();

                ctx.fillStyle = 'white';
                ctx.textBaseline = 'middle';
                ctx.textAlign = 'center';
                ctx.font = 26 * getKeyboardScalar() + 'px ' + lineFont;

                ctx.fillText(b + '', side / 2, side / 2);

                ctx.restore();
              };
            } else {
              key.paintMe = function (ctx) {
                roundRect2(
                  ctx,
                  0,
                  0,
                  side,
                  side,
                  8,
                  key.animated ? getThemeColor() : 'lightgray',
                  undefined,
                  undefined,
                  true
                );
                ctx.save();
                ctx.beginPath();

                ctx.fillStyle = key.animated ? 'white' : 'gray';
                ctx.textBaseline = 'middle';
                ctx.textAlign = 'center';
                ctx.font = 22 * getKeyboardScalar() + 'px ' + lineFont;

                ctx.fillText(b + '', side / 2, side / 2);

                ctx.restore();
              };
            }

            var first = b === 'A' || b === 'J' || b === 'S';
            var last = b === 'I' || b === 'R' || b === '\u03C0';

            letters.push(getHolder(key, gap, gap, first, last));
          })(chars.substring(a, a + 1));
        }

        var r1Alphabet = getBasicObject();
        var x = 0;

        for (var a = 0; a < 9; a++) {
          var n = letters[a];

          n.viewportX = x;
          x += n.viewportW;
          r1Alphabet.add(n);
        }

        r1Alphabet.sizeMeToFitChildren();

        var r2Alphabet = getBasicObject();

        x = 0;

        for (var a = 9; a < 18; a++) {
          var n = letters[a];

          n.viewportX = x;
          x += n.viewportW;
          r2Alphabet.add(n);
        }

        r2Alphabet.sizeMeToFitChildren();

        var r3Alphabet = getBasicObject();

        x = 0;

        for (var a = 18; a < chars.length; a++) {
          var n = letters[a];

          n.viewportX = x;
          x += n.viewportW;
          r3Alphabet.add(n);
        }

        r3Alphabet.sizeMeToFitChildren();

        rs.add(r1Alphabet);
        rs.add(r2Alphabet);
        rs.add(r3Alphabet);
      } else if (t === KEYBOARD_BOTTOM_MODE.FUNCTIONS) {
        var r1Functions = getBasicObject();

        var frac = getFracButton();

        if (keyboard.showBarred) frac.setShowBarred(true);
        keyboard.fracButton = frac;

        r1Functions.add(frac);
        r1Functions.add(getPowButton());
        r1Functions.add(getRootButton());
        r1Functions.add(getQuantityButton());
        r1Functions.add(getAbsoluteValueButton());
        r1Functions.add(getSubscriptButton());
        r1Functions.add(getLogTrigButton());

        r1Functions.layoutChildrenLR(getButtonGap());
        r1Functions.sizeMeToFitChildren();

        rs.add(r1Functions);

        var r2 = getBasicObject();

        var Q = 6 * getKeyboardScalar();
        var H = 12 * getKeyboardScalar();
        var M = 5 * getKeyboardScalar();
        var X = 2 * getKeyboardScalar();
        var F = 10 * getKeyboardScalar();

        var mouseDownResponse = function () {
          moveCursor('up');

          return true;
        };
        var up = getBasicObject();

        up.setAllDim(
          24 * getKeyboardScalar() + 2 * X,
          22 * getKeyboardScalar() + H / 2 + M
        );

        up.paintMe = function (ctx) {
          ctx.save();
          ctx.translate(X, M);
          arrowUp(
            ctx,
            24 * getKeyboardScalar(),
            22 * getKeyboardScalar(),
            2,
            getContrastingThemeColor(),
            up.animated ? getContrastingThemeColor() : 'white'
          );
          ctx.restore();
        };

        up.animates = true;
        up.mouseDownResponse = mouseDownResponse;

        mouseDownResponse = function () {
          moveCursor('down');

          return true;
        };

        var down = getBasicObject();

        down.setAllDim(
          24 * getKeyboardScalar() + 2 * X,
          22 * getKeyboardScalar() + H / 2 + M
        );

        down.paintMe = function (ctx) {
          ctx.save();
          ctx.translate(X, H / 2);
          arrowDown(
            ctx,
            24 * getKeyboardScalar(),
            22 * getKeyboardScalar(),
            2,
            getContrastingThemeColor(),
            down.animated ? getContrastingThemeColor() : 'white'
          );
          ctx.restore();
        };

        down.animates = true;
        down.mouseDownResponse = mouseDownResponse;

        mouseDownResponse = function () {
          moveCursor('left');

          return true;
        };

        var left = getBasicObject();

        left.setAllDim(
          F + 22 * getKeyboardScalar() + Q,
          22 * 2 * getKeyboardScalar() + H + 2 * M
        );

        left.paintMe = function (ctx) {
          ctx.save();
          ctx.translate(F, left.viewportH / 2 - (24 / 2) * getKeyboardScalar());
          arrowLeft(
            ctx,
            22 * getKeyboardScalar(),
            24 * getKeyboardScalar(),
            2,
            getContrastingThemeColor(),
            left.animated ? getContrastingThemeColor() : 'white'
          );
          ctx.restore();
        };

        left.animates = true;
        left.mouseDownResponse = mouseDownResponse;

        mouseDownResponse = function () {
          moveCursor('right');

          return true;
        };

        var right = getBasicObject();

        right.setAllDim(
          Q + 22 * getKeyboardScalar() + F,
          22 * 2 * getKeyboardScalar() + H + 2 * M
        );

        right.paintMe = function (ctx) {
          ctx.save();
          ctx.translate(
            Q,
            right.viewportH / 2 - (24 / 2) * getKeyboardScalar()
          );
          arrowRight(
            ctx,
            22 * getKeyboardScalar(),
            24 * getKeyboardScalar(),
            2,
            getContrastingThemeColor(),
            right.animated ? getContrastingThemeColor() : 'white'
          );
          ctx.restore();
        };

        right.animates = true;
        right.mouseDownResponse = mouseDownResponse;

        var arrowMouseMoveResponse = function () {
          if (this.mouseIsOver) {
            $canvas.css('cursor', 'pointer');
          } else {
            $canvas.css('cursor', 'default');
          }
        };

        up.mouseMoveResponse = arrowMouseMoveResponse;
        down.mouseMoveResponse = arrowMouseMoveResponse;
        left.mouseMoveResponse = arrowMouseMoveResponse;
        right.mouseMoveResponse = arrowMouseMoveResponse;

        var arrowRepaintOnMouseOver = function () {
          return false;
        };

        up.repaintOnMouseOver = arrowRepaintOnMouseOver;
        down.repaintOnMouseOver = arrowRepaintOnMouseOver;
        left.repaintOnMouseOver = arrowRepaintOnMouseOver;
        right.repaintOnMouseOver = arrowRepaintOnMouseOver;

        up.viewportX = left.viewportW;
        down.viewportX = left.viewportW;
        down.viewportY = up.viewportH;
        right.viewportX = up.viewportX + up.viewportW;

        r2.add(left);
        r2.add(right);
        r2.add(up);
        r2.add(down);
        r2.sizeMeToFitChildren();

        rs.add(r2);
      }

      if (
        t === KEYBOARD_BOTTOM_MODE.NUMBERS ||
        t === KEYBOARD_BOTTOM_MODE.ALPHABET
      ) {
        rs.viewportY =
          numbersAlphaRow.viewportY + numbersAlphaRow.viewportH + gap / 2;
        rs.setAllDim(getKeyboardW(), 3 * getButtonW() + 5 * gap);
      } else if (t === KEYBOARD_BOTTOM_MODE.FUNCTIONS) {
        rs.viewportY =
          numbersAlphaRow.viewportY + numbersAlphaRow.viewportH + 2 * gap;
        rs.setAllDim(getKeyboardW(), 3 * getButtonW() + 2 * gap);
      }

      rs.layoutChildrenUD(gap / 2);
      rs.centerHorizontally();

      var bottomBacking = getBasicObject();

      bottomBacking.setAllDim(getKeyboardW(), 3 * getButtonW() + 5 * gap);

      bottomBacking.paintMe = function (ctx) {
        roundRect2(
          ctx,
          numbersAlphaRow.viewportX +
            numbersAlphaRow.viewportW / 2 -
            bottomBacking.viewportW / 2,
          numbersAlphaRow.viewportY + numbersAlphaRow.viewportH,
          bottomBacking.viewportW,
          bottomBacking.viewportH,
          gap,
          getThemeColor(),
          getThemeColor()
        );
      };

      keyboard.bottom.add(numbersAlphaRow);
      keyboard.bottom.add(bottomBacking);
      keyboard.bottom.add(rs);
    }

    keyboard.bottom.sizeMeToFitChildren();
    keyboard.bottom.centerHorizontally();

    keyboard.sizeMeToFitChildren();
    keyboard.centerHorizontally();
  };

  var getAnswerButton = function (text) {
    var key = getButton();

    key.text = text;

    var mouseDownResponse = function () {
      updateAnswerButtonHighlighting(key);

      submitAttempt({ button: text, eventSource: 'text button for ' + text });

      return true;
    };
    var a = getText(5, 4, text);

    a.setFont('bold 14px ' + lineFont);
    var w = a.viewportW;
    var h = a.viewportH;

    key.setAllDim(w + 16, h + 13);
    key.mouseDownResponse = mouseDownResponse;

    key.paintMe = function (ctx) {
      // draw a rectangle in the background to serve as a border to
      // help distinguish the button from the rest of the keyboard
      roundRect2(
        ctx,
        -2,
        -2,
        w + 20,
        h + 14,
        10,
        getThemeColor(),
        undefined,
        undefined,
        true
      );
      // draw a rectangle (without shadow) on top of the background to serve as the button
      roundRect2(
        ctx,
        0,
        0,
        w + 16,
        h + 10,
        8,
        key.sel ? getThemeColor() : 'lightgray'
      );
      ctx.save();

      ctx.fillStyle = key.sel ? 'white' : 'gray';
      ctx.baseline = 'top';
      ctx.font = a.font;
      ctx.fillText(text, 8, 17);

      ctx.restore();
    };

    return key;
  };

  // clear highlighting of all answer buttons
  // then highlight the one that was pressed (if any)
  var updateAnswerButtonHighlighting = function (key) {
    if (currentAttemptGetter.gmmName === 'gridGetter') {
      currentAttemptGetter.deselectAllButtons();
    } else {
      currentAttemptGetter.getJsonFromServer().selectedButton = undefined;
    }

    for (var a = 0; a < buttonKeys.length; a++) {
      buttonKeys[a].sel = false;
    }

    if (key) {
      key.sel = true;

      if (currentAttemptGetter.gmmName === 'gridGetter') {
        currentAttemptGetter.selectedButton = key;
      } else {
        currentAttemptGetter.getJsonFromServer().selectedButton = key;
      }
    }
  };

  var getSymbolButton = function (b) {
    var key = getButton();
    var mouseDownResponse = function () {
      pressedKey(b);

      return true;
    };

    var s = getButtonW();

    key.mouseDownResponse = mouseDownResponse;
    key.setAllDim(s, s);
    key.gmmName = 'symbol button ' + b;

    key.paintMe = function (ctx) {
      roundRect2(
        ctx,
        0,
        0,
        s,
        s,
        8,
        key.animated ? getThemeColor() : 'lightgray',
        undefined,
        undefined,
        true
      );
      ctx.save();
      ctx.beginPath();

      ctx.fillStyle = key.animated ? 'white' : 'gray';
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'center';
      ctx.font = s - 7 + 'px ' + lineFont;

      var yAdjust = 0;

      if (b == '.' || b == ',')
        yAdjust = -5 * (getButtonW() / DEFAULT_BUTTON_SIZE);
      if (b == '$' || b == '%')
        yAdjust = 2 * (getButtonW() / DEFAULT_BUTTON_SIZE);
      if (b == '*') yAdjust = 4 * (getButtonW() / DEFAULT_BUTTON_SIZE);

      ctx.fillText(b, s / 2, s / 2 + yAdjust);

      ctx.restore();
    };

    return key;
  };

  var clearAll = function (
    preserveBottom,
    skipChangedMaybe,
    skipAnim,
    clearButton,
    updateAnswerButtons
  ) {
    if (focused) {
      if (!skipChangedMaybe) changedMaybe();

      var line = focused.line;

      if (line.normalGetter) {
        line.normalGetter.clear(clearButton);
      } else {
        focused.clearMyLine();
        line.grabFocus();
      }

      storeCurrentLineXML();

      if (updateAnswerButtons) updateAnswerButtonHighlighting(null);

      if (!preserveBottom) setKeyboardBottom(KEYBOARD_BOTTOM_MODE.NUMBERS);
      else if (!skipAnim && clearAllB) {
        clearAllB.animate(paintCanvas);
        paintCanvas();
      }
    }
  };

  var getClearAll = function (size, preserveBottom) {
    var ret = getButton();
    var mouseDownResponse = function () {
      clearAll(preserveBottom, null, null, true, true);

      return true;
    };

    ret.gmmName = 'clearAll';

    ret.setAllDim(size, size);

    ret.paintMe = function (ctx) {
      ctx.save();

      roundRect2(
        ctx,
        0,
        0,
        size,
        size,
        8,
        ret.animated ? getThemeColor() : 'lightgray',
        undefined,
        undefined,
        true
      );

      var i = getImage(ret.animated ? 'trashwhite' : 'trash', paintCanvas);

      drawImage(ctx, i, 0.2 * size, 0.15 * size, 0.6 * size, 0.7 * size);

      ctx.restore();
    };

    ret.mouseDownResponse = mouseDownResponse;

    return ret;
  };

  var getFracButton = function () {
    var frac = getButton();

    frac.setShowBarred = function (b) {
      frac.children = [];
      var mouseDownResponse;

      if (!b) {
        mouseDownResponse = function () {
          if (!focused) {
            return false;
          }

          var created = focused.parent.insertRequested('fraction', focused);

          scaleFraction(created);

          if (created) {
            created.grabFocus();
            setKeyboardBottom(KEYBOARD_BOTTOM_MODE.NUMBERS);
          }

          return true;
        };

        frac.paintMe = function (ctx) {
          functionButton(
            'fraction',
            ctx,
            0,
            0,
            frac.animated,
            getThemeColor(),
            'white',
            getButtonW(),
            false
          );
        };
      } else {
        frac.paintParentFirst = true;

        mouseDownResponse = function () {
          if (!focused) {
            return false;
          }

          var created = focused.parent.insertRequested('barred', focused);

          if (created) {
            created.grabFocus();
            setKeyboardBottom(KEYBOARD_BOTTOM_MODE.NUMBERS);
          }

          return true;
        };

        var bLine = toLine({
          problemJS: getProblemJSDependencies(),
          xml: '<b><t>\u25A2</t></b>',
          font: (14 * getButtonW()) / DEFAULT_BUTTON_SIZE + 'px ' + lineFont,
        });

        bLine.setEditable(false);
        bLine.paintParentFirst = true;
        bLine.setFillStyleRecursive('gray');
        frac.add(bLine);

        frac.paintMe = function (ctx) {
          roundRect2(
            ctx,
            0,
            0,
            frac.viewportW,
            frac.viewportH,
            8,
            frac.animated ? getThemeColor() : 'lightgray',
            undefined,
            undefined,
            true
          );
        };
      }

      frac.mouseDownResponse = mouseDownResponse;
      frac.setAllDim(getButtonW(), getButtonW());
      frac.centerVertically();
      frac.centerHorizontally();
    };

    frac.setShowBarred(false);

    return frac;
  };

  var getQuantityButton = function () {
    var mouseDownResponse = function () {
      if (!focused) {
        return false;
      }

      var created = focused.parent.insertRequested('quantity', focused);

      if (created) {
        created.grabFocus();
        setKeyboardBottom(KEYBOARD_BOTTOM_MODE.NUMBERS);
      }

      return true;
    };

    var quantity = getButton();

    quantity.mouseDownResponse = mouseDownResponse;
    quantity.setAllDim(getButtonW(), getButtonW());
    quantity.centerVertically();
    quantity.centerHorizontally();

    quantity.paintMe = function (ctx) {
      functionButton(
        'quantity',
        ctx,
        0,
        0,
        quantity.animated,
        getThemeColor(),
        'white',
        getButtonW(),
        false
      );
    };

    return quantity;
  };

  var getAbsoluteValueButton = function () {
    var mouseDownResponse = function () {
      if (!focused) {
        return false;
      }

      var created = focused.parent.insertRequested('absoluteValue', focused);

      if (created) {
        created.grabFocus();
        setKeyboardBottom(KEYBOARD_BOTTOM_MODE.NUMBERS);
      }

      return true;
    };

    var absoluteValue = getButton();

    absoluteValue.mouseDownResponse = mouseDownResponse;
    absoluteValue.setAllDim(getButtonW(), getButtonW());
    absoluteValue.centerVertically();
    absoluteValue.centerHorizontally();

    absoluteValue.paintMe = function (ctx) {
      functionButton(
        'absVal',
        ctx,
        0,
        0,
        absoluteValue.animated,
        getThemeColor(),
        'white',
        getButtonW(),
        false
      );
    };

    return absoluteValue;
  };

  var getPowButton = function () {
    var pow = getButton();

    pow.mouseDownResponse = function () {
      if (!focused) {
        return false;
      }

      var created = focused.parent.insertRequested('power', focused);

      if (created) {
        created.unsureWhichToFocus();
        setKeyboardBottom(KEYBOARD_BOTTOM_MODE.NUMBERS);
      }

      return true;
    };

    pow.setAllDim(getButtonW(), getButtonW());
    pow.centerVertically();
    pow.centerHorizontally();

    pow.paintMe = function (ctx) {
      functionButton(
        'power',
        ctx,
        0,
        0,
        pow.animated,
        getThemeColor(),
        'white',
        getButtonW(),
        false
      );
    };

    return pow;
  };

  var getRootButton = function () {
    var root = getButton();

    root.mouseDownResponse = function () {
      if (!focused) {
        return false;
      }

      var created = focused.parent.insertRequested('root', focused);

      if (created) {
        created.grabFocus();
        setKeyboardBottom(KEYBOARD_BOTTOM_MODE.NUMBERS);
      }

      return true;
    };

    root.setAllDim(getButtonW(), getButtonW());
    root.centerVertically();
    root.centerHorizontally();

    root.paintMe = function (ctx) {
      functionButton(
        'root',
        ctx,
        0,
        0,
        root.animated,
        getThemeColor(),
        'white',
        getButtonW(),
        false
      );
    };

    return root;
  };

  var getLogTrigButton = function () {
    var mouseDownResponse = function () {
      if (!focused) {
        return false;
      }

      var options = [
        getLogarithmBaseTenMenuItem(),
        getFunctionMenuItem('sin', theta),
        getFunctionMenuItem('sin' + inverse, theta),
        getFunctionMenuItem('csc', theta),
        getFunctionMenuItem('csc' + inverse, theta),
        getLogarithmMenuItem(),
        getFunctionMenuItem('cos', theta),
        getFunctionMenuItem('cos' + inverse, theta),
        getFunctionMenuItem('sec', theta),
        getFunctionMenuItem('sec' + inverse, theta),
        getFunctionMenuItem('ln', 'X'),
        getFunctionMenuItem('tan', theta),
        getFunctionMenuItem('tan' + inverse, theta),
        getFunctionMenuItem('cot', theta),
        getFunctionMenuItem('cot' + inverse, theta),
      ];

      var tl = getFullTopLeftLocation(keyboard.getter);
      var tlw = keyboard.getter.viewportW;
      var tlh = keyboard.getter.viewportH;

      var menu = getMultiMenu(options, 3, false, $canvas);

      menu.viewportX = tl.x + tlw / 2 - menu.width / 2 + 1;
      menu.viewportY = tl.y + tlh + 2;

      // if student's chosen themeColor is not gray, set it as the floater background color
      // otherwise, the menu's default dark gray color will be used
      if (getThemeName() !== 'gray') menu.setBackgroundColor(getThemeColor());
      menu.includeShadow = true;

      floater.setPanel(menu);

      return true;
    };

    var logTrigButton = getButton();

    logTrigButton.mouseDownResponse = mouseDownResponse;
    logTrigButton.setAllDim(getButtonW(), getButtonW());
    logTrigButton.centerVertically();
    logTrigButton.centerHorizontally();

    logTrigButton.paintMe = function (ctx) {
      functionButton(
        'logTrig',
        ctx,
        0,
        0,
        logTrigButton.animated,
        getThemeColor(),
        'white',
        getButtonW(),
        false,
        true
      );
    };

    return logTrigButton;
  };

  var getSubscriptButton = function () {
    var sub = getButton();

    sub.mouseDownResponse = function () {
      if (!focused) {
        return false;
      }

      var created = focused.parent.insertRequested('subscript', focused);

      if (created) {
        created.unsureWhichToFocus();
        setKeyboardBottom(KEYBOARD_BOTTOM_MODE.NUMBERS);
      }

      return true;
    };

    sub.setAllDim(getButtonW(), getButtonW());
    sub.centerVertically();
    sub.centerHorizontally();

    sub.paintMe = function (ctx) {
      functionButton(
        'sub',
        ctx,
        0,
        0,
        sub.animated,
        getThemeColor(),
        'white',
        getButtonW(),
        false,
        false
      );
    };

    return sub;
  };

  var getInequalitiesButton = function () {
    var inequalitiesButton = getButton();

    inequalitiesButton.animateTimeoutOverride = ANIMATE_TIMEOUT_200MS;

    var mouseDownResponse = function () {
      if (!focused) {
        return false;
      }

      var options = [
        getInequalitiesMenuItem('<'),
        getInequalitiesMenuItem('>'),
        getInequalitiesMenuItem('\u2264'),
        getInequalitiesMenuItem('\u2265'),
      ];

      var tl = getFullTopLeftLocation(keyboard.getter);
      var tlw = keyboard.getter.viewportW;
      var tlh = keyboard.getter.viewportH;

      var menu = getMultiMenu(options, 2, false, $canvas);

      menu.viewportX = tl.x + tlw / 2 - menu.width / 2 + 1;
      menu.viewportY = tl.y + tlh + 2;

      // if student's chosen themeColor is not gray, set it as the floater background color
      // otherwise, the menu's default dark gray color will be used
      if (getThemeName() !== 'gray') menu.setBackgroundColor(getThemeColor());
      menu.includeShadow = true;

      floater.setPanel(menu);

      inequalitiesButton.animate(paintCanvas);

      return true;
    };

    inequalitiesButton.mouseDownResponse = mouseDownResponse;
    inequalitiesButton.setAllDim(getButtonW(), getButtonW());
    inequalitiesButton.centerVertically();
    inequalitiesButton.centerHorizontally();

    inequalitiesButton.paintMe = function (ctx) {
      functionButton(
        'inequalities',
        ctx,
        0,
        0,
        inequalitiesButton.animated,
        getThemeColor(),
        'white',
        getButtonW(),
        false,
        true
      );
    };

    return inequalitiesButton;
  };

  var getNumbersButton = function () {
    var numbers = getKeyboardModeTab(KEYBOARD_BOTTOM_MODE.NUMBERS);

    numbers.mouseDownResponse = function () {
      setKeyboardBottom(KEYBOARD_BOTTOM_MODE.NUMBERS);

      return true;
    };

    return numbers;
  };

  var reportErrorIncludingAttemptSoFar = function (message) {
    let attempt;

    if (currentAttemptGetter && currentAttemptGetter.getXML) {
      try {
        attempt = currentAttemptGetter.getXML();
      } catch (error) {
        console.error(
          'Error getting xml version of current attempt JSON',
          error
        );
      }
    }

    errorToServer(`${message} Getter contents: ${JSON.stringify(attempt)}`);
  };

  var getAlphaButton = function () {
    var alpha = getKeyboardModeTab(KEYBOARD_BOTTOM_MODE.ALPHABET);

    alpha.mouseDownResponse = function () {
      setKeyboardBottom(KEYBOARD_BOTTOM_MODE.ALPHABET);

      if (isTouchDeviceOnly() && focused) {
        const location = getFullTopLeftLocation2(focused);

        problemModalState().setNativeInputText(focused.text);
        problemModalState().setNativeInputCursorOnMount(
          focused.cursorIndentChars
        );
        problemModalState().setNativeInputLocation(location);
        setFocusedNativeInput(focused);
      }

      return true;
    };

    return alpha;
  };

  var getFunctionsButton = function () {
    var functions = getKeyboardModeTab(KEYBOARD_BOTTOM_MODE.FUNCTIONS);

    functions.mouseDownResponse = function () {
      setKeyboardBottom(KEYBOARD_BOTTOM_MODE.FUNCTIONS);

      return true;
    };

    return functions;
  };

  var getKeyboardModeTab = function (type) {
    var numbersAlpha = getButton();

    numbersAlpha.mouseMoveResponse = function () {
      if (!this.selected && this.mouseIsOver) {
        // set cursor as pointer to show button is clickable
        $canvas.css('cursor', 'pointer');
      } else {
        // reset cursor to default when not over a button
        $canvas.css('cursor', 'default');
      }
    };

    numbersAlpha.selected = false;
    numbersAlpha.setAllDim(3 * getButtonW() + 2 * 5, getButtonW());

    numbersAlpha.paintMe = function (ctx) {
      keyboardModeTab(
        type,
        ctx,
        0,
        0,
        numbersAlpha.selected,
        getThemeColor(),
        3 * getButtonW() + 2 * 5,
        getButtonW(),
        getKeyboardScalar()
      );
    };

    return numbersAlpha;
  };

  var getSpaceButton = function () {
    var space = getButton();

    space.mouseDownResponse = function () {
      pressedKey(' ');

      return true;
    };

    var side = getButtonW();

    space.setAllDim(side, side);

    space.paintMe = function (ctx) {
      ctx.save();
      roundRect2(
        ctx,
        0,
        0,
        side,
        side,
        8,
        space.animated ? getThemeColor() : 'lightgray',
        undefined,
        undefined,
        true
      );

      ctx.translate(0, 5);
      ctx.lineWidth = 2;
      ctx.strokeStyle = space.animated ? 'white' : 'gray';
      ctx.beginPath();
      var w = side;
      var h = side;
      var wi = 0.15;
      var hi = 0.4;

      ctx.moveTo(wi * w, hi * h);
      ctx.lineTo(wi * w, (1 - hi) * h);
      ctx.lineTo((1 - wi) * w, (1 - hi) * h);
      ctx.lineTo((1 - wi) * w, hi * h);
      ctx.stroke();
      ctx.restore();
    };

    return space;
  };

  var getBackspaceButton = function () {
    var backspace = getButton();

    backspace.mouseDownResponse = function () {
      if (focused) {
        focused.backspace();
        paintCanvas();

        return true;
      }

      return false;
    };

    var side = getButtonW();

    backspace.setAllDim(side, side);

    backspace.paintMe = function (ctx) {
      ctx.save();

      roundRect2(
        ctx,
        0,
        0,
        side,
        side,
        8,
        backspace.animated ? getThemeColor() : 'lightgray',
        undefined,
        undefined,
        true
      );

      ctx.translate(2, 4);
      ctx.scale(0.8, 0.8);

      ctx.lineWidth = 2;
      ctx.strokeStyle = backspace.animated ? 'white' : 'gray';
      ctx.beginPath();
      ctx.moveTo(getKeyboardScalar(), 0.5 * side);
      ctx.lineTo(0.5 * side, side - getKeyboardScalar());
      ctx.lineTo(0.5 * side, 0.8 * side);
      ctx.lineTo(side - getKeyboardScalar(), 0.8 * side);
      ctx.lineTo(side - getKeyboardScalar(), 0.2 * side);
      ctx.lineTo(0.5 * side, 0.2 * side);
      ctx.lineTo(0.5 * side, getKeyboardScalar());
      ctx.lineTo(getKeyboardScalar(), 0.5 * side);
      ctx.closePath();
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(0.5 * side, 0.3 * side);
      ctx.lineTo(0.85 * side, 0.7 * side);
      ctx.moveTo(0.85 * side, 0.3 * side);
      ctx.lineTo(0.5 * side, 0.7 * side);
      ctx.closePath();

      ctx.stroke();

      ctx.restore();
    };

    return backspace;
  };

  var getLogarithmMenuItem = function () {
    var mouseDownResponse = function () {
      if (!focused) {
        return false;
      }

      var created = focused.parent.insertMenuFunction(focused);

      if (created) {
        created.setTypeAndBase('log', '', true);
        created.unsureWhichToFocus();
        setKeyboardBottom(KEYBOARD_BOTTOM_MODE.NUMBERS);
      }

      return true;
    };

    var pLine = toLine({
      problemJS: getProblemJSDependencies(),
      xml: '<tf><ft><t>log</t></ft><fb><t>n</t></fb><v><t> X</t></v></tf>',
      font: 15 * getKeyboardScalar() + 'px ' + lineFont,
    });

    pLine.setEditable(false);
    pLine.setFillStyleRecursive('white');
    pLine.mouseDownResponse = mouseDownResponse;

    return pLine;
  };

  var getLogarithmBaseTenMenuItem = function () {
    var mouseDownResponse = function () {
      if (!focused) {
        return false;
      }

      var created = focused.parent.insertMenuFunction(focused);

      if (created) {
        created.setTypeAndBase('log', '10', false);
        created.unsureWhichToFocus();
        setKeyboardBottom(KEYBOARD_BOTTOM_MODE.NUMBERS);
      }

      return true;
    };

    var pLine = toLine({
      problemJS: getProblemJSDependencies(),
      xml: '<tf><ft><t>log </t></ft><fb><t>10</t></fb><v><t>X</t></v></tf>',
      font: 15 * getKeyboardScalar() + 'px ' + lineFont,
    });

    pLine.setEditable(false);
    pLine.setFillStyleRecursive('white');
    pLine.mouseDownResponse = mouseDownResponse;

    return pLine;
  };

  var getFunctionMenuItem = function (type, variable) {
    var mouseDownResponse = function () {
      if (!focused) {
        return false;
      }

      var created = focused.parent.insertMenuFunction(focused);

      if (created) {
        created.setTypeAndBase(type, '*', false);
        created.unsureWhichToFocus();
        setKeyboardBottom(KEYBOARD_BOTTOM_MODE.NUMBERS);
      }

      return true;
    };

    var pLine = toLine({
      problemJS: getProblemJSDependencies(),
      xml:
        '<tf><ft><t>' +
        type +
        ' </t></ft><fb><t>*</t></fb><v><t>' +
        variable +
        '</t></v></tf>',
      font: 15 * getKeyboardScalar() + 'px ' + lineFont,
    });

    pLine.setEditable(false);
    pLine.setFillStyleRecursive('white');
    pLine.mouseDownResponse = mouseDownResponse;

    return pLine;
  };

  var getInequalitiesMenuItem = function (type) {
    var mouseDownResponse = function () {
      if (!focused) {
        return false;
      }

      focused.insertString(type);

      storeCurrentLineXML();

      return true;
    };

    var pLine = toLine({
      problemJS: getProblemJSDependencies(),
      xml: '<t>       ' + type + '      </t>',
      font: 28 * getKeyboardScalar() + 'px ' + lineFont,
    });

    pLine.setEditable(false);
    pLine.setFillStyleRecursive('white');
    pLine.mouseDownResponse = mouseDownResponse;

    return pLine;
  };

  var scaleFraction = function (fraction) {
    if (!fraction) return;

    const fractionScale = 0.7;
    const parentScale = 0.9;

    // Check if fraction is an exponent or subscript
    if (
      fraction.parent &&
      (fraction.parent.gmmName === 'exp multi' ||
        fraction.parent.gmmName === 'sub multi')
    ) {
      // adjust the font scaling
      var fractionParent = fraction.parent;

      if (fractionParent.children && fractionParent.children.length > 0) {
        scaleFont(fraction, fractionParent.children[0].font, fractionScale);
        scaleFont(fractionParent, fractionParent.children[0].font, parentScale);
      }
    }
  };

  var resizeCanvas = function (can, w, h) {
    // we send in computed heights that are non-integral
    // we speculate that this causes attempts at 'sub-pixel'
    // precision that can result in slight textual blurring
    if (Math.floor(w) !== w) {
      w = 1 + Math.floor(w);
    }

    if (Math.floor(h) !== h) {
      h = 1 + Math.floor(h);
    }

    applyPixelRatioToCanvas(can, w, h);
  };

  var isTesting = function () {
    return getProblemContext().isTesting();
  };

  var getCurrentAttemptGetterName = function () {
    return currentAttemptGetter ? currentAttemptGetter.gmmName : 'unknown';
  };

  // This method is a hack to ignore all input after mobius totallyCorrect.
  // On totallyCorrect, mobius sample problem stays on screen
  // and mouse clicks and key presses are still captured.
  // This triggers an 'extra' submit, which is then probably marked wrong
  // because WF saved and sent a new problem -- it checks the second
  // submission against that new (unseen) problem. It can also
  // cause a lot of weird additional input to appear on the problem.
  var ignoreTeacherAfterTotallyCorrect = function () {
    return !isStudent && currentAttemptGetter && !currentAttemptGetter.e;
  };

  var keyDown = function (evt, skipRepaint) {
    if (!currentAttemptGetter || submitLock) return;

    try {
      if (ignoreTeacherAfterTotallyCorrect()) return;

      // Ignore key presses if the student app has gone dormant
      // due to inactivity
      if (!getProblemContext().activity()) {
        evt.preventDefault();

        return false;
      } else if (evt.key === 'Backspace') {
        if (focused) {
          focused.backspace();
        }
      } else if (evt.key === 'Delete') {
        if (focused) {
          focused.deletePressed();
        }
      } else if (evt.key === 'Tab') {
        var shouldChangeFocus = true;

        // e.g. if the user's cursor is in the numerator of a fraction,
        // we want to move the focus to the denominator
        if (focused && focused.parent.parent instanceof Fraction) {
          shouldChangeFocus = !moveCursor(evt.shiftKey ? 'up' : 'down');
        }

        // move the cursor through the input before moving to another getter
        if (focused && shouldChangeFocus) {
          shouldChangeFocus = !moveCursor(evt.shiftKey ? 'left' : 'right');
        }

        if (shouldChangeFocus) {
          changeFocus(undefined, true, evt.shiftKey);
        }
      } else if (evt.key === 'Enter' && !evt.ctrlKey) {
        submitAttempt({
          eventSource:
            'pressed Enter to submit, getter: ' + getCurrentAttemptGetterName(),
        });
      } else if (
        ['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown'].includes(evt.key)
      ) {
        if (evt.key === 'ArrowUp') {
          moveCursor('up');
        } else if (evt.key === 'ArrowDown') {
          moveCursor('down');
        } else if (evt.key === 'ArrowRight') {
          moveCursor('right');
        } else if (evt.key === 'ArrowLeft') {
          moveCursor('left');
        }

        // don't want page to scroll in addition to moving cursor in getter
        evt.stopPropagation();
        evt.preventDefault();
      } else if (evt.key === ')') {
        moveCursor('right');
      } else if (evt.metaKey) {
        return;
      }
      // insert or 'normal' key
      else {
        if (!focused) {
          return;
        }
        // insert
        else if (
          evt.ctrlKey ||
          evt.key === '(' ||
          evt.key === '|' ||
          evt.key === '^'
        ) {
          let created;

          switch (evt.key) {
            case 'f':
            case 'F':
              created = focused.parent.insertRequested('fraction', focused);
              scaleFraction(created);
              break;
            case '(':
              created = focused.parent.insertRequested('quantity', focused);
              break;
            case '|':
              created = focused.parent.insertRequested(
                'absoluteValue',
                focused
              );
              break;
            case 'r':
            case 'R':
              created = focused.parent.insertRequested('root', focused);
              break;
            case '^':
            case 'p':
            case 'P':
              created = focused.parent.insertRequested('power', focused);
              break;
            case 'b':
            case 'B':
              created = focused.parent.insertRequested('barred', focused);
              break;
            case ',':
            case '<':
              created = focused.insertString('\u2264'); // less or equal
              break;
            case '.':
            case '>':
              created = focused.insertString('\u2265'); // greater or equal
              break;
            case 'Enter':
              focused.nextLine();
              break;
            default:
              break;
          }

          if (created) {
            if (created.t !== 'power') {
              created.grabFocus();
            } else {
              created.unsureWhichToFocus();
            }

            evt.preventDefault();
            evt.stopPropagation();
          }

          storeCurrentLineXML();
        }
        // 'normal' key
        else {
          // ignore non character keys, such as just pressing 'shift'
          if (evt.key.length !== 1) {
            return;
          }

          pressedKey(evt.key, true);
        }
      }

      if (!skipRepaint) paintCanvas();
    } catch (error) {
      console.error(error);
      errorToServer('Error during keyDown: ' + (error.stack || error.message));
    }
  };

  // width is optional
  var resetSizeAndLayout = function (width) {
    // close nativeInputBox on resize, as otherwise its location
    // is no longer valid
    problemModalState().setNativeInputLocation(undefined);

    canvasW = width || NARROW_PROBLEM_WIDTH;

    if (canvasW < PROBLEM_WIDTH) {
      screenMaxTextW = Math.min(canvasW - 30, NARROW_PROBLEM_WIDTH);
    } else {
      screenMaxTextW = MAX_MULTI_LINE_WIDTH;
    }

    world.viewportW = canvasW;

    if (!$canvas) return;

    // includes building keyboard
    rebuildCurrentProblem();
  };

  function exponentialY(a, b, x, c) {
    return a * Math.pow(b, x) + (c != null ? c : 0);
  }

  var setImageLoadedListener = function (iLL) {
    imageLoadedListener = iLL;
  };

  var setSelfCheckListener = function (asl) {
    selfCheckListener = asl;
  };

  var isReadAloud = function () {
    return getProblemContext().isPermitReadAloud();
  };

  var setPermitReadAloud = function (b) {
    problemModalState().setPermitReadAloud(b);
    if (!$canvas) return;
    rebuildCurrentProblem();
  };

  var rebuildCurrentProblem = function () {
    buildKeyboard();
    if (builtCurrentFrom) setProblem(builtCurrentFrom, currentProblemId);
  };

  var touchStart = function (evt) {
    /*
     * Ideally, we could directly access coordinates of touch on the canvas.
     * This is possible with MouseEvent.offsetX, for example. However,
     * TouchEvent does not have any similar normalized field, so we have to work
     * with two values. First, `pageX`, which gives the x coordinate on the viewport,
     * including horizontal scroll offset. Second, the target's (target = canvas)
     * offset from the left.
     */
    try {
      let x = evt.changedTouches[0].pageX - $(evt.target).offset().left;
      let y = evt.changedTouches[0].pageY - $(evt.target).offset().top;

      if (gridGuide && gridGuide.on && gridGuide.handlePointerDown(x, y, evt)) {
        return true;
      }

      if (dragController) {
        dragController.mostRecentMouseDown = { x: x, y: y };
        dragController.sourceCanvas = evt.target;
      }

      const touchStartedGridDrag =
        currentAttemptGetter &&
        currentAttemptGetter.startGridDragAtCanvasPoint &&
        currentAttemptGetter.startGridDragAtCanvasPoint(x, y, evt);

      if (touchStartedGridDrag) {
        paintCanvas();

        return true;
      }

      const touchStarted = world.touchStart(x, y);

      if (touchStarted) {
        paintCanvas();
      }

      return touchStarted;
    } catch (error) {
      console.error(error);
      errorToServer(
        'Error during touchStart: ' + (error.stack || error.message)
      );

      return false;
    }
  };

  var touchMove = function (evt) {
    try {
      let x = evt.changedTouches[0].pageX - $(evt.target).offset().left;
      let y = evt.changedTouches[0].pageY - $(evt.target).offset().top;

      canvasMouseMoveHelper(x, y, evt);
    } catch (error) {
      console.error(error);
      errorToServer(
        'Error during touchMove: ' + (error.stack || error.message)
      );
    }
  };

  var touchEnd = function (evt) {
    try {
      let x = evt.changedTouches[0].pageX - $(evt.target).offset().left;
      let y = evt.changedTouches[0].pageY - $(evt.target).offset().top;

      canvasMouseUpHelper(x, y);
    } catch (error) {
      console.error(error);
      errorToServer('Error during touchEnd: ' + (error.stack || error.message));
    }
  };

  var stopDragging = function () {
    var stoppedGridGuideArrowRepeat =
      gridGuide && gridGuide.stopGridGuideArrowRepeat();

    if (activeGridDrag && activeGridDrag.gag) {
      activeGridDrag.gag.finishGridDrag(activeGridDrag);
    }

    if (dragController) dragController.setDragging(false);

    if (stoppedGridGuideArrowRepeat) {
      paintCanvas();
    }
  };

  // width is optional, defaults to NARROW_PROBLEM_WIDTH
  var setCanvas = function (canvas, width) {
    if (!canvas) throw 'undefined canvas sent to problemCanvasOwner.setCanvas';

    $canvas = $(canvas);

    if (!world) {
      world = getBasicObject();
      world.gmmName = 'world';

      world.fill = isStudent ? STUDENT_PROBLEM_BACKCOLOR : 'white';

      problemPanel = getBasicObject();
      problemPanel.gmmName = 'problemPanel';

      world.add(problemPanel);
    }

    resetSizeAndLayout(width || NARROW_PROBLEM_WIDTH);

    // in addition to other initialization, initSpeech results in audioIcon, audioStopIcon, and loadingWheel being loaded
    initSpeech(paintCanvas);

    // preload some other commonly used images (e.g. trash can, submit checkmark, thumbs up)
    initImages();
  };

  var onlyHasNormalGetters = function () {
    return onlyNormalGetters;
  };

  var setFocused = function (text) {
    if (!text) {
      focused = null;
    } else {
      var lineAlreadyHadFocus = text.line && text.line.isFocused();

      focused = text;

      if (!lineAlreadyHadFocus) {
        text.line.gainedFocus();
      }
    }
  };

  var getFocused = function () {
    return focused;
  };

  return {
    getStaticPanel,
    setCanvas,
    setProblem,
    setToBlank,
    repaint: paintCanvas,
    rebuildCurrentProblem,
    resetSizeAndLayout,
    keyDown,
    onlyHasNormalGetters,
    submitAllAgs,
    setImageLoadedListener,
    setSelfCheckListener,
    setBase64EncodedImgs,
    setPermitReadAloud,
    setSubmitLock,
    mouseDown: canvasMouseDown,
    mouseMove: canvasMouseMove,
    mouseUp: canvasMouseUp,
    touchStart,
    touchMove,
    touchEnd,
    stopDragging,
    isDragging,
    touchDeviceChanged,
  };
}
